import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { latLonToGrid, gridToLatLon, haversineKm, bearingDeg, bearingToCompass, freqToBandJs, isValidGrid, isFreqInHamBand, isNewerVersion } from './utils';
import AntennaCalculatorView from './AntennaCalculator';
import { useIsMobile, useTheme, THEME_GROUPS, visibleThemes, useNavLayout, useStartView, useTableDensity, useNoteMacros } from './hooks';
import { QSOMap, QsoLocatorMap } from './Maps';
import { AnnouncementsBanner } from './Announcements';
import { StationCallsignBar, OfflineQueueModal } from './UserWidgets';
import StationView, { StationProfileBar } from './Station';
import { bandPlanWarnings, LICENSE_BAND_OPTIONS } from './bandplan';
import { ScpSuggestions, KeyerPanel } from './ContestTools';
import CommandPalette from './CommandPalette';
import ErrorBoundary from './ErrorBoundary';
import Icon from './icons';
import { SidebarNav, BottomTabs, usePinnedViews } from './Shell';
import { WsjtxPanel, WsjtxReplyButton, useWsjtxStatus } from './WsjtxControl';
import { useWebSerialRig, rigAsCatStatus, rigQsy, autoReconnectRig, rstFromSmeter, sUnitsLabel, parseSplit, rigSplitOff } from './webSerialCat';
import { useWebSerialRotor, rotorAsStatus, rotorGoto, autoReconnectRotor } from './webSerialRotor';
import WebSerialPanel, { WebSerialRotorPanel } from './WebSerialPanel';
import { stepFreq, neighbourBand, useFreqWheel, useSpotAtRadio, useRadioStatusPush } from './RadioAssist';
import OnThisDay from './OnThisDay';
import Flag from './Flag';
import NoticeCenter from './NoticeCenter';
import NotificationBell from './NotificationBell';
import InvitesPanel from './Invites';
import QslCardModal, { downloadQslCardsPdf } from './QslCardModal';
import ServiceAccounts, { SERVICES as SERVICE_LIST, serviceConfigured } from './ServiceAccounts';
import { useI18n, AVAILABLE_LANGUAGES, tx, getLang, hasExplicitLang, applyAutoLanguage, applyAccountLanguage } from './i18n';
import { APP_VERSION } from './version';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import 'leaflet/dist/leaflet.css';
import 'flag-icons/css/flag-icons.min.css';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, Rectangle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { io } from 'socket.io-client';
import './App.css';
import { BANDS, BAND_CONDS, COND_COLOR, MODES, MODE_COLORS, NAV_GROUPS, SIMPLE_VIEWS, OFFLINE_STORE, calculateSolarPosition, getQueuedQsos, getRst, nowUTC, openOfflineDB, parseFreq, removeQueuedQso, solarElevation, updateQueuedQsoError , asArray} from './shared';

// ── Leniwe ladowanie (code splitting) ────────────────────────────────────────
// 31 widokow ladowalo sie NARAZ w jednej paczce 245 kB -- takze te, ktorych
// wiekszosc userow nigdy nie otworzy (kalkulator anten, satelity, panel admina).
// Teraz kazdy dociaga sie dopiero przy pierwszym wejsciu.
//
// CELOWO 'na sztywno' zostaja: Maps (mapa QSO w loggerze), Announcements
// (baner dla wszystkich), Flag/shared/hooks/i18n -- to rdzen, potrzebny od razu.

const AntennaCalculator = lazy(() => import('./AntennaCalculator'));
const DashboardView = lazy(() => import('./Dashboard'));
const GoalsView = lazy(() => import('./GoalsView'));
const PortableView = lazy(() => import('./PortableView'));
const CwDecoderView = lazy(() => import('./CwDecoder'));
const CwTrainerView = lazy(() => import('./CwTrainer'));
const PathCalculatorView = lazy(() => import('./PathCalculator'));
const NetControlView = lazy(() => import('./NetControl'));
const SkedsView = lazy(() => import('./Skeds'));
const YearSummaryView = lazy(() => import('./YearSummary'));
const SwlView = lazy(() => import('./Swl'));
const DxccMatrixView = lazy(() => import('./DxccMatrix'));
const BandMapView = lazy(() => import('./BandMap'));
const AzimuthalMapView = lazy(() => import('./AzimuthalMap'));
const KeyerView = lazy(() => import('./ContestTools'));
const QsoRateMeterView = lazy(() => import('./QsoRateMeter'));
const LiveRosterView = lazy(() => import('./LiveRoster'));
const PropagationDashboard = lazy(() => import('./PropagationDashboard'));
const ActivationPlannerView = lazy(() => import('./ActivationPlanner'));
const AprsLookupView = lazy(() => import('./AprsLookup'));
const NewsView = lazy(() => import('./News'));
const ContestsView = lazy(() => import('./Contests'));
const StationCardModal = lazy(() => import('./StationCard'));
const StationPanel = lazy(() => import('./StationCard').then(m => ({ default: m.StationPanel })));
const BeaconsView = lazy(() => import('./Beacons'));
const AlertRulesView = lazy(() => import('./AlertRules'));
const ChatView = lazy(() => import('./Chat'));
const MarketView = lazy(() => import('./Market'));
const RankingView = lazy(() => import('./Ranking'));
const ExamView = lazy(() => import('./Exam'));
const DxpeditionsView = lazy(() => import('./Dxpeditions'));
const MostWantedView = lazy(() => import('./MostWanted'));
const DxccMapView = lazy(() => import('./DxccMap'));
const WwffView = lazy(() => import('./Wwff'));
const DxNeededView = lazy(() => import('./DxNeeded'));
const QslManagerView = lazy(() => import('./QslManager'));
const TrashView = lazy(() => import('./Trash'));
const PersonalPropagation = lazy(() => import('./PersonalPropagation'));
const LogHealthPanel = lazy(() => import('./LogHealth'));
const AgentDiagnosticsView = lazy(() => import('./AgentDiagnostics'));
const WsjtxRemoteView = lazy(() => import('./WsjtxRemote'));
const OnboardingTour = lazy(() => import('./Onboarding'));
const TwoFactorPanel = lazy(() => import('./TwoFactor'));
const SessionsPanel = lazy(() => import('./Sessions'));
const ActivityStatsPanel = lazy(() => import('./ActivityStats'));
const AdminPanel = lazy(() => import('./Admin').then(m => ({ default: m.AdminPanel })));
const PotaSotaView = lazy(() => import('./LiveViews').then(m => ({ default: m.PotaSotaView })));
const PSKReporterView = lazy(() => import('./LiveViews').then(m => ({ default: m.PSKReporterView })));
const SatelliteView = lazy(() => import('./LiveViews').then(m => ({ default: m.SatelliteView })));
const WebSDRView = lazy(() => import('./LiveViews').then(m => ({ default: m.WebSDRView })));
const SvxlinkView = lazy(() => import('./LiveViews').then(m => ({ default: m.SvxlinkView })));
const SvxlinkPlayerWidget = lazy(() => import('./LiveViews').then(m => ({ default: m.SvxlinkPlayerWidget })));
const DiplomasView = lazy(() => import('./Community').then(m => ({ default: m.DiplomasView })));
const NeighborsView = lazy(() => import('./Community').then(m => ({ default: m.NeighborsView })));
const SharedLogbookView = lazy(() => import('./Community').then(m => ({ default: m.SharedLogbookView })));
const AwardsPanel = lazy(() => import('./Community').then(m => ({ default: m.AwardsPanel })));
const BadgesPanel = lazy(() => import('./Community').then(m => ({ default: m.BadgesPanel })));
const WeatherView = lazy(() => import('./Tools').then(m => ({ default: m.WeatherView })));
const EqslDesignerView = lazy(() => import('./Tools').then(m => ({ default: m.EqslDesignerView })));


// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const API = '';  // same origin via nginx proxy


const MODE_GROUPS = {
  'Cyfrowe': ['FT8','FT4','JS8','JT65','JT9','WSPR','MSK144','OLIVIA'],
  'Klasyczne': ['SSB','CW','AM','FM'],
  'Dane': ['RTTY','PSK31','SSTV'],
  'Glos cyfrowy': ['DSTAR','C4FM','DMR'],
};
const BAND_ORDER = ['160m','80m','60m','40m','30m','20m','17m','15m','12m','10m','6m','2m','70cm','23cm'];

// Parsuje wpisana czestotliwosc, akceptujac PRZECINEK jako separator
// dziesietny (powszechne w Polsce/Europie, np. "145,6125") OBOK punktu.
// Bez tego parseFloat("145,6125") zwrocilby 145 -- ucina na pierwszym
// nieprawidlowym znaku, nie rzuca bledu, wiec problem jest cichy i
// niezauwazalny dla uzytkownika (dokladnie ten zglaszany bug).




// Plaska lista wszystkich zakladek -- uzywana tam, gdzie potrzebny jest
// prosty lookup (np. tytul aktywnej zakladki), bez grupowania.
function NAV_ITEMS(userProfile) {
  return NAV_GROUPS(userProfile).flatMap(g => g.type==='single' ? [[g.id, g.label]] : g.items);
}

// POPRAWIONE: poprzednia wersja brala czas LOKALNY (polski) i tylko UDAWALA
// UTC (dopisujac znacznik bez prawdziwej konwersji) -- kazde nowe QSO bylo
// logowane z czasem przesunietym o 1-2h (w zaleznosci od czasu letniego/
// zimowego) wzgledem prawdziwego UTC, mimo ze etykieta pola mowi "Data/Czas
// UTC". Date.toISOString() JUZ prawidlowo konwertuje do UTC niezaleznie od
// strefy czasowej przegladarki -- nie trzeba (i nie wolno) recznie odejmowac
// timezone offsetu.


// ── Wprowadzanie glosowe znaku (Web Speech API, eksperymentalne) ─────────────
// Wspiera alfabet fonetyczny NATO + cyfry wypowiadane slowami. Dziala
// najlepiej w Chrome/Edge -- Web Speech API nie jest wspierane wszedzie,
// stad button po prostu sie nie pojawia w nieobslugiwanych przegladarkach.
const PHONETIC_MAP = {
  'alpha':'A','alfa':'A','bravo':'B','charlie':'C','delta':'D','echo':'E','foxtrot':'F',
  'golf':'G','hotel':'H','india':'I','juliet':'J','juliett':'J','kilo':'K','lima':'L',
  'mike':'M','november':'N','oscar':'O','papa':'P','quebec':'Q','romeo':'R','sierra':'S',
  'tango':'T','uniform':'U','victor':'V','whiskey':'W','whisky':'W','xray':'X','x-ray':'X',
  'yankee':'Y','zulu':'Z',
  'zero':'0','oh':'0','one':'1','two':'2','three':'3','four':'4','five':'5',
  'six':'6','seven':'7','eight':'8','nine':'9','niner':'9',
};

function parseSpokenCallsign(text) {
  const tokens = text.toLowerCase().replace(/[.,!?]/g,'').split(/\s+/).filter(Boolean);
  let result = '';
  for (const tok of tokens) {
    if (PHONETIC_MAP[tok]) result += PHONETIC_MAP[tok];
    else if (/^[a-z0-9]+$/.test(tok)) result += tok.toUpperCase();
  }
  return result;
}

function VoiceCallsignButton({ onResult, notify }) {
  const [listening, setListening] = useState(false);
  const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SpeechRecognition) return null;

  const startListening = () => {
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setListening(true);
    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      const parsed = parseSpokenCallsign(text);
      if (parsed) { onResult(parsed); notify(tx('🎤 Rozpoznano: {0}', [parsed])); }
      else notify(tx('Nie rozpoznano znaku -- spróbuj jeszcze raz (alfabet fonetyczny, po angielsku)'),'warning');
    };
    recognition.onerror = () => { setListening(false); notify(tx('Błąd rozpoznawania glosu'),'error'); };
    recognition.onend = () => setListening(false);
    recognition.start();
  };

  return (
    <button type="button" className="btn btn--qrz" onClick={startListening} disabled={listening}
      title={tx('Wypowiedz znak alfabetem fonetycznym, np. \'Sierra Papa One Alpha Bravo Charlie\' (eksperymentalne)')}>
      <Icon name="mic" size={16} />{listening?'…':''}
    </button>
  );
}

// ── CW Decoder (eksperymentalny, Web Audio API) ──────────────────────────────
// Nasluchuje mikrofonu (np. z glosnika radia), wykrywa obwiednie tonu CW
// i dekoduje na podstawie wzgledych dlugosci kreska/przerwa. Jakosc dekodowania
// silnie zalezy od czystosci sygnalu, poziomu szumu i stabilnosci tempa (WPM)
// nadajacego -- traktuj jako pomoc, nie jako pewny zapis.
const MORSE_TABLE = {
  '.-':'A','-...':'B','-.-.':'C','-..':'D','.':'E','..-.':'F','--.':'G','....':'H',
  '..':'I','.---':'J','-.-':'K','.-..':'L','--':'M','-.':'N','---':'O','.--.':'P',
  '--.-':'Q','.-.':'R','...':'S','-':'T','..-':'U','...-':'V','.--':'W','-..-':'X',
  '-.--':'Y','--..':'Z','-----':'0','.----':'1','..---':'2','...--':'3','....-':'4',
  '.....':'5','-....':'6','--...':'7','---..':'8','----.':'9',
};

function CWDecoder({ onClose, onResult, notify }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [estimatedWpm, setEstimatedWpm] = useState(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({
    toneOn: false, lastTransition: 0, unitMs: 80, recentMarks: [],
    currentLetter: '', noiseFloor: 0.01,
  });

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(()=>{});
    setListening(false);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      // Waski filtr pasmowy wokol typowego tonu pobocznego CW (600-800Hz),
      // odrzuca szum poza ta wskazka -- znacznie ulatwia detekcje obwiedni.
      const bandpass = ctx.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.frequency.value = 700;
      bandpass.Q.value = 8;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(bandpass);
      bandpass.connect(analyser);
      analyserRef.current = analyser;
      stateRef.current = { toneOn:false, lastTransition: performance.now(), unitMs:80, recentMarks:[], currentLetter:'', noiseFloor:0.01 };
      setListening(true);
      setTranscript('');
      tick();
    } catch (e) {
      notify((tx('Brak dostepu do mikrofonu:') + ' ') + e.message, 'error');
    }
  };

  const flushLetter = () => {
    const s = stateRef.current;
    if (s.currentLetter) {
      const letter = MORSE_TABLE[s.currentLetter] || '?';
      setTranscript(t => t + letter);
      s.currentLetter = '';
    }
  };

  const tick = () => {
    const analyser = analyserRef.current;
    const s = stateRef.current;
    if (!analyser) return;

    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    // RMS sygnalu (po odjeciu skladowej 128 = cisza w 8-bit time-domain)
    let sumSq = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / data.length);

    // Adaptacyjny prog szumu -- powolne sledzenie poziomu tla, sygnal musi
    // przekroczyc go o margines, by zostal uznany za "ton".
    s.noiseFloor = s.noiseFloor * 0.98 + rms * 0.02;
    const threshold = Math.max(0.02, s.noiseFloor * 2.5);
    const toneOn = rms > threshold;
    const now = performance.now();

    if (toneOn !== s.toneOn) {
      const duration = now - s.lastTransition;
      if (s.toneOn) {
        // Koniec "kreski" (mark) -- sklasyfikuj jako dit/dah wzgledem unitMs
        s.recentMarks.push(duration);
        if (s.recentMarks.length > 10) s.recentMarks.shift();
        // unitMs estymowany jako najkrotsza ostatnia kreska (dit) -- typowe podejscie
        if (s.recentMarks.length >= 3) {
          s.unitMs = Math.min(...s.recentMarks);
          setEstimatedWpm(Math.round(1200 / s.unitMs)); // standard: WPM = 1200/dit_ms (wzor PARIS)
        }
        s.currentLetter += (duration < s.unitMs * 2) ? '.' : '-';
      } else {
        // Koniec przerwy (space) -- sklasyfikuj jako: w obrebie litery / koniec
        // litery / koniec slowa, wzgledem unitMs
        if (duration > s.unitMs * 5) {
          flushLetter();
          setTranscript(t => t + ' ');
        } else if (duration > s.unitMs * 2) {
          flushLetter();
        }
        // duration < 2*unitMs -- przerwa wewnatrz litery, nic nie robimy
      }
      s.toneOn = toneOn;
      s.lastTransition = now;
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
        <div className="modal__header">
          <h3><Icon name="type" size={18} /> {tx('Dekoder CW (beta)')}</h3>
          <button className="modal__close" onClick={()=>{stop();onClose();}}>✕</button>
        </div>
        <p className="modal__info">
          {tx('Eksperymentalne -- jakość zależy od czystości sygnalu z głośnika radia. Najlepiej działa przy stabilnym tempie i niskim szumie tla.')}
        </p>
        <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:14}}>
          <button type="button" className={"btn "+(listening?'btn--secondary':'btn--primary')}
            onClick={listening?stop:start}>
            {listening?tx('⏹ Stop'):tx('🎤 Start nasluchu')}
          </button>
          {listening&&estimatedWpm>0&&(
            <span style={{fontFamily:'var(--mono)',fontSize:12,color:'var(--text-dim)'}}>~{estimatedWpm} WPM</span>
          )}
        </div>
        <textarea className="input textarea" readOnly value={transcript}
          placeholder={tx('Zdekodowany tekst pojawi się tutaj...')} rows={4}
          style={{fontFamily:'var(--mono)',fontSize:16,letterSpacing:'0.05em'}} />
        <div className="form-actions">
          <button className="btn btn--secondary btn--sm" onClick={()=>{
            const cs = transcript.trim().split(/\s+/).pop();
            if (cs) { onResult(cs); notify(tx('Wstawiono "{0}" do pola znaku', [cs])); }
          }}>{tx('Wstaw ostatnie słowo jako znak')}</button>
          <button className="btn btn--ghost btn--sm" onClick={()=>setTranscript('')}>{tx('Wyczysc')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Axios interceptor for JWT ─────────────────────────────────────────────────
// Kazde zapytanie dostaje NAJSWIEZSZY access token z localStorage (takze
// ponowienie po odswiezeniu tokenu -- dlatego to jest bezwarunkowe).
const getToken = () => localStorage.getItem('access_token');
axios.interceptors.request.use(cfg => {
  const t = getToken();
  if (t) cfg.headers['Authorization'] = `Bearer ${t}`;
  // Backend tlumaczy wtedy komunikaty bledow (logowanie, rejestracja...)
  cfg.headers['X-Lang'] = getLang();
  return cfg;
});

// Reczna zmiana jezyka po zalogowaniu -- zapisujemy ja tez na koncie (dziala na
// kazdym urzadzeniu i w mailach), dopiero potem przeladowanie w nowym jezyku.
const LANG_PENDING_KEY = 'hamlogger_lang_pending';
function changeLanguage(setLang, next, loggedIn) {
  if (!loggedIn) {
    // Wybor na ekranie logowania -- trafi na konto po zalogowaniu
    try { localStorage.setItem(LANG_PENDING_KEY, '1'); } catch {}
    setLang(next);
    return;
  }
  axios.put(`${API}/api/auth/update`, { language: next }).catch(() => {}).finally(() => setLang(next));
}

// Po wczytaniu profilu: jezyk z konta > wybor z ekranu logowania > wykrycie po
// znaku wywolawczym (polski prefiks -> pl, inny kraj -> en). Znak SO/nieznany
// identyfikator SWL -> zostaje jezyk wykryty po adresie IP.
function syncLanguageWithAccount(profile) {
  if (!profile || profile.impersonated_by_callsign) return;
  let pending = false;
  try { pending = localStorage.getItem(LANG_PENDING_KEY) === '1'; localStorage.removeItem(LANG_PENDING_KEY); } catch {}
  if (pending && hasExplicitLang()) {
    if (profile.language !== getLang()) axios.put(`${API}/api/auth/update`, { language: getLang() }).catch(() => {});
    return;
  }
  if (profile.language) { applyAccountLanguage(profile.language); return; }
  if (hasExplicitLang()) { axios.put(`${API}/api/auth/update`, { language: getLang() }).catch(() => {}); return; }
  if (profile.suggested_language) applyAutoLanguage(profile.suggested_language);
}

// WAZNE -- ODDZIELNA instancja axios (BEZ interceptorow) do /api/auth/refresh.
// Poprzednia wersja wolala refresh przez globalny `axios`, wiec:
//  1. interceptor request wyzej NADPISYWAL podany naglowek z refresh tokenem
//     WYGASLYM access tokenem -> backend odpowiadal 401 "Token has expired",
//  2. ten 401 wpadal z powrotem do interceptora response ponizej, ktory
//     znowu probowal odswiezyc... i tak w kolko -- nieskonczony lancuch
//     zapytan, ktory NIGDY sie nie konczyl. Oryginalne zapytanie wisialo na
//     zawsze: dziennik sie nie ladowal, /api/auth/me nie wracal (profil
//     pusty -> znikal panel admina), a wylogowanie w catch nigdy nie
//     nastepowalo. Objaw: "po ok. 1h (czas zycia access tokenu) web zmienia
//     stan, znika admin, dziennik nie laduje".
const authClient = axios.create();

// Jedno wspolne odswiezanie na raz -- gdy wygasly token trafi naraz kilka
// zapytan (dziennik, statystyki, profil, status CAT...), wszystkie czekaja
// na TEN SAM refresh zamiast wysylac kilka rownoleglych.
let refreshPromise = null;
const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = authClient.post(`${API}/api/auth/refresh`, {},
      { headers: { Authorization: `Bearer ${localStorage.getItem('refresh_token')}` } })
      .then(r => {
        localStorage.setItem('access_token', r.data.access_token);
        // Backend obraca refresh token przy kazdym odswiezeniu (sesja wygasa
        // po 3h BEZCZYNNOSCI, nie 3h od zalogowania) -- zapisz nowy.
        if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
        return r.data.access_token;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

// Sesja naprawde się skonczyla (refresh token wygasl/odwolany) -- wyczysc
// TYLKO dane logowania (nie motyw, filtry, makra itp.) i pokaż ekran
// logowania, zamiast zostawiac apke w polowicznym, zepsutym stanie.
const AUTH_STORAGE_KEYS = ['access_token', 'refresh_token', 'callsign',
  'admin_access_token', 'admin_refresh_token', 'admin_callsign'];
let sessionExpiredHandled = false;
const handleSessionExpired = () => {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  AUTH_STORAGE_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch {} });
  try { navigator.serviceWorker?.controller?.postMessage({type:'CLEAR_API_CACHE'}); } catch {}
  window.location.reload();
};

axios.interceptors.response.use(r => r, async err => {
  const cfg = err.config;
  // Tylko 401, tylko gdy mamy czym odswiezyc, i MAKSYMALNIE jedno ponowienie
  // na zapytanie (np. /api/auth/change-password zwraca 401 dla zlego hasła --
  // po odswiezeniu i ponowieniu nadal 401, wiec po prostu oddajemy błąd).
  if (err.response?.status !== 401 || !cfg || cfg._authRetried || !localStorage.getItem('refresh_token')) {
    return Promise.reject(err);
  }
  cfg._authRetried = true;
  try {
    await refreshAccessToken();
  } catch (refreshErr) {
    // Serwer odrzucil refresh token -> koniec sesji. Brak odpowiedzi (siec
    // lezy, tryb offline) -> NIE wylogowuj, tylko oddaj oryginalny błąd.
    if (refreshErr.response) handleSessionExpired();
    return Promise.reject(err);
  }
  return axios(cfg);
});

// ── QuickFreqPanel ────────────────────────────────────────────────────────────
function QuickFreqPanel({ currentMode, currentFreq, onSelect, notify }) {
  const { t } = useI18n();
  const [allFreqs, setAllFreqs] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeGroup, setActiveGroup] = useState('Cyfrowe');
  const [filterMode, setFilterMode] = useState('');
  const [filterBand, setFilterBand] = useState('');
  const [newFavFreq, setNewFavFreq] = useState('');
  const [newFavMode, setNewFavMode] = useState('FM');
  const [newFavDesc, setNewFavDesc] = useState('');
  const [repeaters, setRepeaters] = useState(null);
  const [repeatersLoading, setRepeatersLoading] = useState(false);
  const [repeatersError, setRepeatersError] = useState('');
  const [repeaterRadius, setRepeaterRadius] = useState(50);
  const [repeaterMode, setRepeaterMode] = useState('FM');

  useEffect(() => {
    if (Object.keys(allFreqs).length > 0) return;
    setLoading(true);
    axios.get(`${API}/api/frequencies`).then(r => setAllFreqs(r.data.frequencies || {})).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  // Własne/ulubione częstotliwości (np. lokalne przemienniki) -- osobne od
  // wspolnego planu pasmowego IARU, zapisywane per-user na koncie.
  useEffect(() => {
    axios.get(`${API}/api/frequencies/favorites`).then(r=>setFavorites(asArray(r.data.favorites))).catch(()=>{});
  }, []);

  useEffect(() => {
    for (const [grp, modes] of Object.entries(MODE_GROUPS))
      if (modes.includes(currentMode)) { setActiveGroup(grp); break; }
    setFilterMode(currentMode); setFilterBand('');
  }, [currentMode]);

  // Przemienniki z przemienniki.eu -- ladowane "lazy" (tylko gdy user
  // faktycznie otworzy te zakladke), bo to zewnętrzne zapytanie wokol
  // lokalizacji domowej, nie ma sensu robic tego od razu przy kazdym
  // otwarciu loggera.
  const loadRepeaters = useCallback(() => {
    setRepeatersLoading(true); setRepeatersError('');
    axios.get(`${API}/api/repeaters`, {params:{radius: repeaterRadius, mode: repeaterMode}})
      .then(r => {
        if (r.data.error) setRepeatersError(r.data.error);
        else setRepeaters(asArray(r.data.repeaters));
      })
      .catch(e => setRepeatersError(e.response?.data?.error || tx('Błąd pobierania przemienników')))
      .finally(() => setRepeatersLoading(false));
  }, [repeaterRadius, repeaterMode]);

  useEffect(() => {
    if (activeGroup === 'Przemienniki') loadRepeaters();
  }, [activeGroup, loadRepeaters]);

  const saveFavorites = async (next) => {
    setFavorites(next);
    try { await axios.put(`${API}/api/frequencies/favorites`, {favorites: next}); }
    catch { notify?.(tx('Błąd zapisu ulubionych częstotliwości'),'error'); }
  };

  const addFavorite = () => {
    const freq = parseFreq(newFavFreq);
    if (!freq) { notify?.(tx('Podaj poprawną częstotliwość (MHz)'),'error'); return; }
    saveFavorites([...favorites, {freq, mode: newFavMode, desc: newFavDesc.trim() || tx('Ulubiona {0}', [newFavMode])}]);
    setNewFavFreq(''); setNewFavDesc('');
  };
  const removeFavorite = (idx) => saveFavorites(favorites.filter((_,i)=>i!==idx));

  const GROUP_NAMES = [...Object.keys(MODE_GROUPS), 'Przemienniki', 'Moje'];
  const isFavGroup = activeGroup === 'Moje';
  const isRepeaterGroup = activeGroup === 'Przemienniki';
  const groupModes = MODE_GROUPS[activeGroup] || [];
  const groupFreqs = isFavGroup ? favorites : groupModes.flatMap(m => (allFreqs[m]||[]).map(f=>({...f,mode:m})));
  const filtered = isFavGroup ? favorites
    : groupFreqs.filter(f=>!filterMode||f.mode===filterMode).filter(f=>!filterBand||f.band===filterBand);
  const availBands = [...new Set(groupFreqs.filter(f=>!filterMode||f.mode===filterMode).map(f=>f.band))]
    .sort((a,b)=>BAND_ORDER.indexOf(a)-BAND_ORDER.indexOf(b));

  return (
    <div className="qfp">
      <div className="qfp__top">
        <div className="qfp__groups">
          {GROUP_NAMES.map(grp => (
            <button key={grp} type="button"
              className={"qfp__grp"+(activeGroup===grp?" qfp__grp--active":"")}
              onClick={()=>{setActiveGroup(grp);setFilterMode("");setFilterBand("");}}>
              {grp}
            </button>
          ))}
        </div>
        {!isFavGroup&&!isRepeaterGroup&&(
          <div className="qfp__modes">
            <button type="button" className={"qfp__mode-chip"+(filterMode===""?" qfp__mode-chip--active":"")}
              onClick={()=>{setFilterMode("");setFilterBand("");}}>{tx('Wszystkie')}</button>
            {groupModes.filter(m=>allFreqs[m]?.length>0).map(m=>(
              <button key={m} type="button"
                className={"qfp__mode-chip"+(filterMode===m?" qfp__mode-chip--active":"")}
                style={filterMode===m?{background:MODE_COLORS[m]+"22",borderColor:MODE_COLORS[m],color:MODE_COLORS[m]}:{}}
                onClick={()=>{setFilterMode(m);setFilterBand("");}}>
                {m}{currentMode===m&&<span className="qfp__cur-dot"/>}
              </button>
            ))}
          </div>
        )}
      </div>

      {isFavGroup?(
        <div className="qfp__fav-add">
          <input className="input input--sm" style={{width:90,fontFamily:'var(--mono)'}} placeholder={tx('MHz')}
            value={newFavFreq} onChange={e=>setNewFavFreq(e.target.value)} />
          <select className="input input--sm" style={{width:90}} value={newFavMode} onChange={e=>setNewFavMode(e.target.value)}>
            {MODES.map(m=><option key={m}>{m}</option>)}
          </select>
          <input className="input input--sm" style={{flex:1,minWidth:100}} placeholder={tx('Opis (np. Przemiennik SR3PO)')}
            value={newFavDesc} onChange={e=>setNewFavDesc(e.target.value)} />
          <button type="button" className="btn btn--secondary btn--sm" onClick={addFavorite}>{tx('+ Dodaj')}</button>
        </div>
      ):isRepeaterGroup?(
        <div className="qfp__fav-add">
          <span className="qfp__label">{t('common.range')}</span>
          {[25,50,100,200].map(km=>(
            <button key={km} type="button" className={"qfp__band"+(repeaterRadius===km?" qfp__band--active":"")}
              onClick={()=>setRepeaterRadius(km)}>{km} {tx('km')}</button>
          ))}
          <select className="input input--sm" style={{width:110,marginLeft:'auto'}} value={repeaterMode} onChange={e=>setRepeaterMode(e.target.value)}>
            {[['FM','FM'],['FMPOLAND',tx('FM Poland')],['DMR','DMR'],['C4FM','C4FM'],['DSTAR','DSTAR'],['TETRA','TETRA'],['ECHOLINK','EchoLink'],['FMLINK','FM-LINK'],['MINILINK','MiniLink'],['EXTREMELINK','ExtremeLink']].map(([val,lbl])=><option key={val} value={val}>{lbl}</option>)}
          </select>
          <button type="button" className="btn btn--secondary btn--sm" onClick={loadRepeaters} disabled={repeatersLoading}>
            <Icon name={repeatersLoading?'clock':'refresh'} size={16} />
          </button>
        </div>
      ):(
        <div className="qfp__bands">
          <span className="qfp__label">{tx('Pasmo:')}</span>
          <button type="button" className={"qfp__band"+(filterBand===""?" qfp__band--active":"")} onClick={()=>setFilterBand("")}>{tx('Wszystkie')}</button>
          {availBands.map(b=>(
            <button key={b} type="button" className={"qfp__band"+(filterBand===b?" qfp__band--active":"")} onClick={()=>setFilterBand(b)}>{b}</button>
          ))}
        </div>
      )}

      {isRepeaterGroup?(
        <>
          {repeatersError&&<div className="qfp__empty" style={{color:'var(--warning)'}}>{repeatersError}</div>}
          <div className="qfp__tiles">
            {repeatersLoading&&<span className="qfp__loading">{t('common.loadingRepeaters')}</span>}
            {!repeatersLoading&&!repeatersError&&repeaters?.length===0&&(
              <span className="qfp__empty">{tx('Brak przemienników')} {repeaterMode} {tx('w zasięgu')} {repeaterRadius} {tx('km')}</span>
            )}
            {!repeatersLoading&&repeaters?.map((rp,i)=>{
              const active = parseFreq(currentFreq)===rp.tx_freq;
              return (
                <button key={i} type="button"
                  className={"qfp__tile qfp__tile--repeater"+(active?" qfp__tile--active":"")}
                  style={{"--tile-color":MODE_COLORS[rp.modes[0]]||"#888"}}
                  onClick={()=>onSelect(rp.tx_freq, rp.band, rp.modes[0]||'FM',
                    {repeater:{offsetMHz: rp.rx_freq ? +(rp.rx_freq-rp.tx_freq).toFixed(6) : (rp.offset||0), ctcss: parseFloat(rp.rx_ctcss)||null}})}
                  title={`${rp.callsign} -- ${rp.qth||rp.locator}${rp.rx_ctcss?` -- ton ${rp.rx_ctcss}`:''}`}>
                  <span className="qfp__tile-mode">{rp.callsign}</span>
                  <span className="qfp__tile-freq">{rp.tx_freq.toFixed(4)}</span>
                  <span className="qfp__tile-band">{rp.distance_km} {tx('km')}{rp.rx_ctcss?` · ${rp.rx_ctcss}`:''}</span>
                </button>
              );
            })}
          </div>
          <div className="qfp__footer">
            {repeaters?.length>0?tx('{0} przemienników · źródło: przemienniki.eu', [repeaters.length]):tx('Źródło: przemienniki.eu')}
          </div>
        </>
      ):(
      <>
      <div className="qfp__tiles">
        {loading&&<span className="qfp__loading">{tx('Ładowanie...')}</span>}
        {!loading&&filtered.length===0&&(
          <span className="qfp__empty">{isFavGroup?tx('Brak ulubionych -- dodaj swój pierwszy przemiennik powyżej'):tx('Brak czestotliwosci')}</span>
        )}
        {filtered.map((f,i)=>{
          const col=MODE_COLORS[f.mode]||"#888";
          const active=parseFreq(currentFreq)===f.freq;
          return (
            <div key={i} style={{position:'relative'}}>
              <button type="button"
                className={"qfp__tile"+(active?" qfp__tile--active":"")}
                style={{"--tile-color":col}}
                onClick={()=>onSelect(f.freq,f.band,f.mode)}
                title={isFavGroup?f.desc:f.desc+" ("+f.region+")"}>
                <span className="qfp__tile-mode">{f.mode}</span>
                <span className="qfp__tile-freq">{f.freq.toFixed(f.freq<10?4:3)}</span>
                <span className="qfp__tile-band">{f.band||'—'}</span>
                {!isFavGroup&&f.region!=='R1/R2/R3'&&<span className="qfp__tile-region">{f.region}</span>}
              </button>
              {isFavGroup&&(
                <button type="button" className="qfp__fav-remove" title={t('repeater.removeFav')}
                  onClick={(e)=>{e.stopPropagation();removeFavorite(i);}}>✕</button>
              )}
            </div>
          );
        })}
      </div>
      <div className="qfp__footer">
        {isFavGroup
          ? tx('{0} ulubionych czestotliwosci', [filtered.length])
          : tx('{0} czestotliwosci · IARU Region 1/2/3 Band Plan', [filtered.length])}
      </div>
      </>
      )}
    </div>
  );
}

// ── Leaflet Map ───────────────────────────────────────────────────────────────
// ── Maidenhead Grid Locator System ────────────────────────────────────────────
// Standard wedlug ktorego krotkofalowcy okreslaja swoja pozycje (np. KO02, JO82FX).
// Pole (2 litery, A-R) = 20° dlugosci x 10° szerokosci geogr.
// Kwadrat (2 cyfry, 0-9) = 2° dlugosci x 1° szerokosci geogr.
// Podkwadrat (2 litery, A-X) = 5' dlugosci x 2.5' szerokosci geogr.

// Funkcje geo/pasma (latLonToGrid, gridToLatLon, haversineKm, bearingDeg,
// bearingToCompass, freqToBandJs) wydzielone do utils.js -- patrz import na gorze.

// ── Kolejka offline (IndexedDB) ───────────────────────────────────────────────
// Gdy nie ma polaczenia z serwerem (np. w terenie podczas aktywacji POTA/SOTA),
// QSO zapisywane jest lokalnie w IndexedDB i wyslane automatycznie po powrocie
// polaczenia (nasluch zdarzenia 'online'). Dziala niezaleznie od cache'owania
// w service workerze -- to jest "kolejka zapisu", SW obsluguje tylko odczyt.





async function queueOfflineQso(qsoData) {
  const db = await openOfflineDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OFFLINE_STORE, 'readwrite');
    tx.objectStore(OFFLINE_STORE).add({ ...qsoData, queuedAt: new Date().toISOString() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}





// Zapisuje powod ostatniej nieudanej proby wyslania TEGO konkretnego
// elementu z powrotem do IndexedDB -- bez tego, user nie mial ZADNEGO
// sposobu zobaczyc DLACZEGO konkretne QSO nie chce wyjsc z kolejki (poprzednio
// bylo to calkowicie milczone -- patrz komentarz w sendOfflineQueue).


// ── Grayline (linia dnia/nocy) ────────────────────────────────────────────────
// Standardowe wzory astronomiczne: deklinacja Slonca (przyblizenie) + dlugosc
// podslonecznego punktu na podstawie czasu UTC. Cieniowanie nocy realizowane
// jako siatka komorek (podobnie jak MaidenheadGridLayer) -- prostsze i bardziej
// odporne na zawijanie dlugosci geograficznej niz jeden poligon polkuli.




function PropagationBar() {
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/propagation`); if (!r.data.error) setData(r.data); } catch {}
  }, []);
  const loadWeather = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/weather`); if (!r.data.error) setWeather(r.data); } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 300000); return ()=>clearInterval(t); }, [load]);
  useEffect(() => { loadWeather(); const t = setInterval(loadWeather, 600000); return ()=>clearInterval(t); }, [loadWeather]);
  if (!data) return null;
  const kVal = parseInt(data.k_index||'0');
  const kColor = kVal<=2?'#00e5a0':kVal<=4?'#ffbb00':'#ff4444';
  const storm = weather?.storm_warning;
  return (
    <div className="prop-bar">
      <div className="prop-indices">
        {[{l:'SFI',v:data.sfi},{l:'A',v:data.a_index},{l:'K',v:data.k_index,c:kColor},{l:'SSN',v:data.sunspots},{l:'X-Ray',v:data.x_ray}].map(x=>(
          <div key={x.l} className="prop-idx" title={x.l}>
            <span className="prop-idx__label">{x.l}</span>
            <span className="prop-idx__value" style={x.c?{color:x.c}:{}}>{x.v||'—'}</span>
          </div>
        ))}
      </div>
      <div className="prop-bands">
        {BAND_CONDS.map(bc=>{
          const cond=data.conditions?.[bc.key];
          if(!cond) return null;
          return (
            <div key={bc.key} className="prop-band" style={{'--c':COND_COLOR[cond]||'#888'}}>
              <span className="prop-band__name">{bc.label}</span>
              <span className="prop-band__pill">{cond}</span>
            </div>
          );
        })}
        {/* Wskaznik burzy -- na prawo od warunkow 10m, stylem dopasowany do
            pigulek propagacyjnych (Good/Fair/Poor), zawsze widoczny (nie
            tylko gdy jest ostrzezenie) dla konsekwencji z reszta paska */}
        {weather&&(
          <div className="prop-band prop-band--storm" style={{'--c': storm?'#ff4444':'#00e5a0'}} title={storm?tx('{0}{1} -- rozważ odłączenie anteny', [storm.source==='IMGW'?'[Oficjalne ostrzeżenie IMGW] ':'', storm.message]):tx('Brak burzy w prognozie/ostrzezeniach na najblizsze 6h')}>
            <span className="prop-band__name">{tx('⛈️ Burza')}</span>
            <span className={"prop-band__pill"+(storm?" prop-band__pill--alert":"")}>
              {storm?storm.message:tx('Nie')}
            </span>
          </div>
        )}
      </div>
      <div className="prop-meta">
        <button className="prop-refresh" onClick={load}>&#8635;</button>
        <span className="prop-updated">{data.updated}</span>
      </div>
    </div>
  );
}

// ── Alert Aurora (Kp-index) -- DEDYKOWANY wskaznik dla operatorow VHF/UHF,
// osobny od ogolnego paska warunkow wyzej -- inny przypadek uzycia: aurora
// otwiera NIETYPOWE propagacje na 6m/2m (rozproszenie na zorzy polarnej),
// interesujace zwlaszcza dla wyzszych szerokosci geograficznych, przy
// Kp>=5 (poziom G1 burzy geomagnetycznej wg skali NOAA) -- to ustalony,
// uzywany w hobby prog, nie przypadkowa liczba.
const AURORA_KP_THRESHOLD = 5;

function AuroraAlertBadge() {
  const [kIndex, setKIndex] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/propagation`);
      if (!r.data.error) setKIndex(parseInt(r.data.k_index || '0'));
    } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 300000); return () => clearInterval(t); }, [load]);

  if (kIndex == null || kIndex < AURORA_KP_THRESHOLD || dismissed) return null;

  return (
    <button type="button" className="session-badge session-badge--secondary" onClick={()=>setDismissed(true)}
      style={{background:'rgba(168,85,247,0.15)',color:'#a855f7',borderColor:'rgba(168,85,247,0.35)',cursor:'pointer'}}
      title={tx('Kp={0} -- mozliwa nietypowa propagacja na 6m/2m (rozproszenie na zorzy polarnej). Im wyzej na polnoc/poludnie, tym wieksza szansa. Kliknij, zeby zamknac.', [kIndex])}>
      {tx('🌌 Aurora Kp=')}{kIndex}
    </button>
  );
}


// ── Sugestie propagacyjne (geolokalizacja + warunki pasm) ────────────────────
// WAŻNE -- uczciwosc epistemiczna: to NIE jest precyzyjny model propagacji
// (jak VOACAP) -- liczenie prawdziwego "point-to-point" MUF wymaga znacznie
// więcej danych (kąt padania, liczba przeskokow, stan jonosfery na trasie).
// To prosta, ale realna heurystyka oparta na: (1) prawdziwych danych
// słonecznych z hamqsl.com (te same co w PropagationBar wyzej), (2)
// faktycznej pozycji Slonca wzgledem Twojej lokalizacji i kazdego regionu
// (dokladne obliczenie astronomiczne, nie zgadywanie), (3) tym samym
// rozumowaniu "dzień/noc + ocena pasma", którym krotkofalowcy posluguja się
// ręcznie od dekad. Prezentowane jako wskazowki, nie pewne przewidywania.
const PROP_REGIONS = [
  {name: tx('Ameryka Północna'), lat: 40, lon: -100},
  {name: tx('Ameryka Południowa'), lat: -15, lon: -60},
  {name: 'Europa', lat: 50, lon: 15},
  {name: 'Afryka', lat: 0, lon: 20},
  {name: 'Azja', lat: 35, lon: 100},
  {name: 'Oceania', lat: -25, lon: 135},
];
const PROP_BAND_PAIRS = [
  {key: '80m-40m', label: tx('80m/40m')},
  {key: '30m-20m', label: tx('30m/20m')},
  {key: '17m-15m', label: tx('17m/15m')},
  {key: '12m-10m', label: tx('12m/10m')},
];
const PROP_QUALITY_RANK = {Good: 3, Fair: 2, Poor: 1};

function computePropagationSuggestions(userLat, userLon, propData) {
  const now = new Date();
  const { declination, subsolarLon } = calculateSolarPosition(now);
  const userElev = solarElevation(userLat, userLon, declination, subsolarLon);
  const userIsDay = userElev > 0;
  const userNearTerminator = Math.abs(userElev) < 8; // ~grayline -- klasycznie dobre warunki na dolne pasma

  const suggestions = [];
  for (const region of PROP_REGIONS) {
    const elev = solarElevation(region.lat, region.lon, declination, subsolarLon);
    const isDay = elev > 0;
    const nearTerminator = Math.abs(elev) < 8;
    for (const bp of PROP_BAND_PAIRS) {
      const cond = propData.conditions?.[`${bp.key}_${isDay ? 'day' : 'night'}`];
      if (!cond) continue;
      suggestions.push({
        region: region.name, band: bp.label, condition: cond,
        regionIsDay: isDay, regionNearTerminator: nearTerminator,
        rank: PROP_QUALITY_RANK[cond] || 0,
      });
    }
  }
  suggestions.sort((a, b) => b.rank - a.rank);
  return { suggestions: suggestions.slice(0, 6), userIsDay, userNearTerminator, userElev };
}

function PropagationSuggestionWidget({ userProfile }) {
  const { t } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [coords, setCoords] = useState(null);

  const runSuggestion = async () => {
    setError(''); setLoading(true); setResult(null);
    let lat, lon;
    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, {timeout: 10000})
        );
        lat = pos.coords.latitude; lon = pos.coords.longitude;
        setCoords({lat, lon});
      } catch (e) {
        // WAŻNE: GeolocationPositionError ma KONKRETNY kod (1=odmowa
        // (PERMISSION_DENIED), 2=lokalizacja niedostepna (POSITION_UNAVAILABLE,
        // np. wyłączone uslugi lokalizacji na poziomie systemu), 3=timeout) --
        // poprzednia wersja ZAWSZE mowila "brak zgody" niezaleznie od
        // prawdziwej przyczyny, co utrudnialo diagnoze (ten sam błąd, który
        // wczesniej naprawilismy przy tescie SMTP -- generyczny komunikat
        // maskujacy prawdziwy powód).
        const geoReasons = {1:tx('Odmówiono dostępu do lokalizacji'), 2:tx('Lokalizacja niedostępna (sprawdź czy usługi lokalizacji są włączone w systemie/przeglądarce)'), 3:tx('Przekroczono czas oczekiwania na lokalizację')};
        const reason = geoReasons[e.code] || e.message || tx('nieznany błąd');
        // Brak zgody/odmowa -- nie blokujemy calkowicie, spróbuj uzyc
        // lokalizacji domowej z profilu jako fallback (jeśli ustawiona).
        if (userProfile?.home_lat && userProfile?.home_lon) {
          lat = userProfile.home_lat; lon = userProfile.home_lon;
          setCoords({lat, lon});
          setError(tx('{0} -- użyto lokalizacji domowej z profilu.', [reason]));
        } else {
          setLoading(false);
          setError(tx('{0}, i brak lokalizacji domowej w profilu -- nie można policzyć sugestii.', [reason]));
          return;
        }
      }
    } else if (userProfile?.home_lat && userProfile?.home_lon) {
      lat = userProfile.home_lat; lon = userProfile.home_lon;
      setCoords({lat, lon});
    } else {
      setLoading(false);
      setError(tx('Przeglądarka nie wspiera geolokalizacji, i brak lokalizacji domowej w profilu.'));
      return;
    }

    try {
      const r = await axios.get(`${API}/api/propagation`);
      if (r.data.error) throw new Error();
      setResult(computePropagationSuggestions(lat, lon, r.data));
    } catch {
      setError(tx('Błąd pobierania danych propagacyjnych -- spróbuj ponownie.'));
    }
    setLoading(false);
  };

  const openModal = () => { setShowModal(true); runSuggestion(); };

  return (
    <>
      <button className="btn btn--secondary btn--sm" onClick={openModal}><Icon name="globe" size={16} /> {t('prop.whereToTx')}</button>
      {showModal&&(
        <div className="modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
            <div className="modal__header">
              <h3><Icon name="globe" size={18} /> {tx('Sugestie propagacyjne')}</h3>
              <button className="modal__close" onClick={()=>setShowModal(false)}>✕</button>
            </div>
            <p className="modal__info">
              {tx('Wskazówki, NIE precyzyjna predykcja -- oparte na realnych danych słonecznych (hamqsl.com) i faktycznej pozycji Słońca względem Ciebie i każdego regionu. Przeglądarka poprosi o zgodę na lokalizację, żeby to policzyć.')}
            </p>
            {loading&&<div className="qfp__loading" style={{padding:20,textAlign:'center'}}>{tx('Liczenie...')}</div>}
            {error&&<div className="notification notification--warning" style={{position:'relative',top:0,right:0,margin:'0 0 12px'}}>{error}</div>}
            {result&&(
              <>
                <div style={{fontSize:12,color:'var(--text-dim)',marginBottom:12}}>
                  {tx('Twoja lokalizacja:')} {coords?.lat.toFixed(2)}, {coords?.lon.toFixed(2)} {tx('-- u Ciebie teraz')} {result.userIsDay?tx('☀️ dzień'):tx('🌙 noc')}
                  {result.userNearTerminator&&(' ' + tx('· blisko terminatora (greyline) -- dobry moment na dolne pasma!') )}
                </div>
                {result.suggestions.length===0&&<div className="empty-state">{t('prop.noBandData')}</div>}
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {result.suggestions.map((s,i)=>(
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)'}}>
                      <span style={{fontSize:13}}>
                        <strong>{s.band}</strong> → {s.region}
                        <span style={{color:'var(--text-dim)',fontSize:11}}> ({s.regionIsDay?tx('dzień'):'noc'}{s.regionNearTerminator?tx(', greyline'):''})</span>
                      </span>
                      <span className="prop-band__pill" style={{'--c':COND_COLOR[s.condition]||'#888',background:`color-mix(in srgb, ${COND_COLOR[s.condition]||'#888'} 18%, transparent)`,color:COND_COLOR[s.condition]||'#888'}}>
                        {s.condition}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <button className="btn btn--ghost btn--sm" style={{width:'100%',marginTop:14}} onClick={runSuggestion} disabled={loading}>
              {tx('↻ Odśwież')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Weather View (pogoda + ostrzezenie przed burza) ───────────────────────────



// ── Tryb zawodow / Field Day -- uproszczony, szybki interfejs logowania ─────
// Osobny komponent (nie modyfikuje istniejacego, rozbudowanego formularza
// Loggera) -- duze przyciski/pola, mniej elementow, Enter z kazdego pola
// zapisuje QSO (jak w N1MM), auto-numeracja wymiany seryjnej.
// Presety wymiany -- NIE licza punktow (regula scoringu za bardzo sie roznia
// miedzy zawodami), tylko podpowiadaja FORMAT pola "wymiana wyslana", zeby
// operator nie musial pamietac na pamiec co dany kontest oczekuje.
const CONTEST_EXCHANGE_PRESETS = [
  { id: 'generic', label: tx('Ogólny (RST + numer)'), placeholder: '599 001' },
  { id: 'cqww', label: tx('CQ WW (RST + strefa CQ)'), placeholder: '599 14' },
  { id: 'arrl_dx', label: tx('ARRL DX (RST + moc/stan)'), placeholder: tx('599 100W albo 599 CA') },
  { id: 'wpx', label: tx('CQ WPX (RST + numer)'), placeholder: '599 001' },
  { id: 'fd', label: tx('Field Day (klasa + sekcja)'), placeholder: '2A NNJ' },
  { id: 'ss', label: tx('ARRL SS (numer + prec. + sekcja + rok lic.)'), placeholder: '123 A NNJ 99' },
];

function ContestModeView({ form, setForm, submitQso, lookupCallsign, loading, qrzStatus, notify, duplicateWarning, mycall }) {
  const { t } = useI18n();
  const [autoSerial, setAutoSerial] = useState(true);
  const [serialNumber, setSerialNumber] = useState(1);
  const [contestQsoCount, setContestQsoCount] = useState(0);
  const [exchangePreset, setExchangePreset] = useState('generic');
  const [multSummary, setMultSummary] = useState(null);
  const sessionStartRef = useRef(Date.now());
  const callsignRef = useRef(null);
  const prevMultCountsRef = useRef(null);

  useEffect(() => { callsignRef.current?.focus(); }, []);

  // Panel mnoznikow -- na zywo, przeliczany po kazdym zapisanym QSO. Nie
  // liczy punktow (patrz komentarz przy CONTEST_EXCHANGE_PRESETS), tylko
  // pokazuje UNIKALNE kraje/strefy/stany zrobione w TYCH zawodach do tej
  // pory -- to i tak najbardziej przydatna informacja "na zywo".
  const loadMultSummary = useCallback(async () => {
    if (!form.contest_name?.trim()) { setMultSummary(null); return; }
    try {
      const r = await axios.get(`${API}/api/contest/summary`, { params: { contest_name: form.contest_name } });
      const counts = { dxcc: r.data.unique_dxcc.length, zones: r.data.unique_zones.length, states: r.data.unique_states.length };
      const prev = prevMultCountsRef.current;
      if (prev && (counts.dxcc > prev.dxcc || counts.zones > prev.zones || counts.states > prev.states)) {
        notify?.(tx('🎉 Nowy mnożnik!'), 'success', 2500);
      }
      prevMultCountsRef.current = counts;
      setMultSummary(r.data);
    } catch { /* podgląd -- ciche niepowodzenie nie blokuje logowania */ }
  }, [form.contest_name, notify]);

  useEffect(() => {
    const t = setTimeout(loadMultSummary, 500);
    return () => clearTimeout(t);
  }, [loadMultSummary]);

  const insertSerial = () => {
    const padded = String(serialNumber).padStart(3, '0');
    setForm(f => ({...f, contest_exchange_sent: f.contest_exchange_sent
      ? `${f.contest_exchange_sent.replace(/\d{3}$/, '').trim()} ${padded}`.trim()
      : `599 ${padded}`}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitQso(e);
    setContestQsoCount(n => n + 1);
    if (autoSerial) setSerialNumber(n => n + 1);
    callsignRef.current?.focus();
    loadMultSummary();
  };

  // Enter w KAZDYM polu zapisuje QSO (nie tylko w ostatnim) -- kluczowe dla
  // szybkiego tempa zawodow, gdzie nie ma czasu klikac myszka w przycisk.
  const onKeyDownAnywhere = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const elapsedHours = (Date.now() - sessionStartRef.current) / 3_600_000;
  const rate = elapsedHours > 0.005 ? Math.round(contestQsoCount / elapsedHours) : 0;

  return (
    <section className="section contest-mode">
      <div className="section__header">
        <h2><Icon name="flag" size={20} /> {t('contest.title')}</h2>
        <div style={{display:'flex',gap:16,alignItems:'center',fontFamily:'var(--mono)'}}>
          <span style={{color:'var(--accent)',fontWeight:700}}>{contestQsoCount} QSO</span>
          <span style={{color:'var(--text-dim)',fontSize:12}}>{rate}/h</span>
        </div>
      </div>

      {/* Panel mnoznikow -- na zywo, tylko gdy wpisano nazwe zawodow (bez
          niej nie wiadomo po czym grupowac QSO tych zawodow w bazie). */}
      {multSummary&&(
        <div style={{display:'flex',gap:18,flexWrap:'wrap',margin:'0 24px 14px',padding:'10px 14px',
          background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:8,fontFamily:'var(--mono)',fontSize:13}}>
          <span>{tx('Kraje:')} <strong style={{color:'var(--accent)'}}>{multSummary.unique_dxcc.length}</strong></span>
          <span>{tx('Strefy CQ:')} <strong style={{color:'var(--accent)'}}>{multSummary.unique_zones.length}</strong></span>
          <span>{tx('Stany USA:')} <strong style={{color:'var(--accent)'}}>{multSummary.unique_states.length}</strong></span>
          <span>{tx('Prefiksy:')} <strong style={{color:'var(--accent)'}}>{multSummary.unique_prefixes.length}</strong></span>
          <span style={{marginLeft:'auto',color:'var(--text-dim)',fontSize:11}}>
            {Object.entries(multSummary.by_band).map(([b,c])=>`${b}:${c}`).join(' · ')||tx('brak QSO w tych zawodach')}
          </span>
        </div>
      )}

      {duplicateWarning&&(
        <div className="dup-warning" style={{margin:'0 24px'}}>
          {tx('⚠️ Już masz QSO z')} <strong>{form.callsign}</strong> {tx('dzisiaj na tym paśmie/modzie!')}
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={onKeyDownAnywhere} className="contest-form">
        <div className="contest-row">
          <input ref={callsignRef} className="contest-input contest-input--call" value={form.callsign}
            onChange={e=>setForm(f=>({...f,callsign:e.target.value.toUpperCase()}))}
            onBlur={e=>lookupCallsign(e.target.value)} placeholder="ZNAK" autoFocus />
          <button type="button" className="btn btn--qrz" onClick={()=>lookupCallsign(form.callsign)}
            disabled={loading||!(qrzStatus.logged_in||qrzStatus.hamqth)}><Icon name={loading?'clock':'search'} size={16} /></button>
        </div>
        <ScpSuggestions value={form.callsign}
          onPick={(c)=>{ setForm(f=>({...f,callsign:c})); lookupCallsign(c); callsignRef.current?.focus(); }} />
        <KeyerPanel compact mode={form.mode} notify={notify}
          ctx={{ mycall, call: form.callsign, nr: String(serialNumber).padStart(3,'0'), rst: form.rst_sent }} />

        <div className="contest-row contest-row--triple">
          <select className="contest-input contest-input--sm" value={form.mode} onChange={e=>setForm(f=>({...f,mode:e.target.value}))}>
            {['SSB','CW','FT8','FT4','RTTY'].map(m=><option key={m}>{m}</option>)}
          </select>
          <input className="contest-input contest-input--sm" type="text" inputMode="decimal" value={form.frequency}
            onChange={e=>setForm(f=>({...f,frequency:e.target.value}))} placeholder={tx('MHz')} />
          <input className="contest-input contest-input--sm" value={form.power}
            onChange={e=>setForm(f=>({...f,power:e.target.value}))} placeholder="W" />
        </div>

        <div className="contest-row contest-row--triple">
          <input className="contest-input contest-input--sm" value={form.rst_sent}
            onChange={e=>setForm(f=>({...f,rst_sent:e.target.value}))} placeholder={tx('RST wys.')} />
          <input className="contest-input contest-input--sm" value={form.rst_recv}
            onChange={e=>setForm(f=>({...f,rst_recv:e.target.value}))} placeholder={tx('RST odb.')} />
          <input className="contest-input contest-input--sm" value={form.contest_name}
            onChange={e=>setForm(f=>({...f,contest_name:e.target.value}))} placeholder={t('contest.name')} />
        </div>

        <div className="contest-row">
          <select className="contest-input contest-input--sm" value={exchangePreset}
            onChange={e=>setExchangePreset(e.target.value)} title={tx('Typ wymiany -- tylko podpowiada format pola, nie liczy punktow')}>
            {CONTEST_EXCHANGE_PRESETS.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div className="contest-row">
          <input className="contest-input" value={form.contest_exchange_sent}
            onChange={e=>setForm(f=>({...f,contest_exchange_sent:e.target.value}))}
            placeholder={CONTEST_EXCHANGE_PRESETS.find(p=>p.id===exchangePreset)?.placeholder||t('contest.exchangeSent')} />
          <input className="contest-input" value={form.contest_exchange_recv}
            onChange={e=>setForm(f=>({...f,contest_exchange_recv:e.target.value}))} placeholder={tx('Wymiana odebrana')} />
        </div>

        <div className="contest-row" style={{justifyContent:'space-between',alignItems:'center'}}>
          <label className="checkbox-label" style={{fontSize:13}}>
            <input type="checkbox" checked={autoSerial} onChange={e=>setAutoSerial(e.target.checked)} />
            {tx('Auto-numeracja')}
          </label>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <span style={{fontFamily:'var(--mono)',fontSize:18,color:'var(--accent)'}}>#{String(serialNumber).padStart(3,'0')}</span>
            <button type="button" className="btn btn--secondary btn--sm" onClick={insertSerial}>{tx('↳ Wstaw')}</button>
          </div>
        </div>

        <button className="btn btn--primary contest-submit" type="submit" disabled={loading}>
          {loading?'…':tx('✓ Zapisz (Enter)')}
        </button>
      </form>
    </section>
  );
}

// ── Radio Dialer (floating, jak softphone) + Konfiguracja radia ─────────────
const DIAL_MODES = ['SSB','CW','FT8','FT4','RTTY','FM','AM','PSK31','JS8'];
// Emisje z raportem w dB SNR (z programu), a nie z S-metra
const DIGITAL_SNR_MODES = ['FT8','FT4','JS8','JT65','JT9','Q65','MSK144','FST4','WSPR'];

function RadioDialerWidget({ notify, remote }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showWsjtxAgent, setShowWsjtxAgent] = useState(false);
  const [agentStatus, setStatus] = useState({connected:false});
  // Radio podlaczone bezposrednio przez USB (Web Serial) ma pierwszenstwo przed agentem
  const webRig = useWebSerialRig();
  const status = rigAsCatStatus(webRig) || agentStatus;
  const [dialMode, setDialMode] = useState('SSB');
  const [dialFreq, setDialFreq] = useState('14.200');
  const [allFreqs, setAllFreqs] = useState({});
  const [agentRotorStatus, setRotorStatus] = useState({connected:false});
  // Obrotnica podlaczona przez USB w przegladarce ma pierwszenstwo przed agentem
  const webRotor = useWebSerialRotor();
  const rotorStatus = rotorAsStatus(webRotor) || agentRotorStatus;
  const [rotorTarget, setRotorTarget] = useState('0');

  // Ten widget jest zamontowany NA STALE (caly czas dzialania apki, na
  // KAZDYM widoku, patrz miejsce montowania) -- odpytywanie co 5s 24/7
  // (nawet gdy popup jest zamkniety i user go w ogole nie widzi) generowalo
  // ogromna ilosc zbednych zadan API w tle (zgloszone: wysokie I/O
  // kontenerow). Gdy popup jest ZAMKNIETY, wystarcza rzadsze odpytywanie --
  // kropka na przycisku i tak nie musi byc idealnie na biezaco co sekunde.
  // Pelna czestotliwosc (5s) tylko gdy user faktycznie patrzy na panel.
  useEffect(() => {
    const poll = () => axios.get(`${API}/api/cat/status`).then(r=>setStatus(r.data)).catch(()=>setStatus({connected:false}));
    poll();
    const t = setInterval(poll, open ? 5000 : 30000);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    const poll = () => axios.get(`${API}/api/rotor/status`).then(r=>setRotorStatus(r.data)).catch(()=>setRotorStatus({connected:false}));
    poll();
    const t = setInterval(poll, open ? 5000 : 30000);
    return () => clearInterval(t);
  }, [open]);

  const sendRotorGoto = async () => {
    const az = parseFloat(rotorTarget);
    if (isNaN(az) || az < 0 || az > 360) { notify(tx('Azymut musi być w zakresie 0-360°'),'error'); return; }
    try {
      if (rotorStatus.source === 'webserial') {
        await rotorGoto(az);
        notify(tx('🧭 Obrotnica (USB): obrót na {0}°', [az]));
        return;
      }
      await axios.post(`${API}/api/rotor/command`, {azimuth: az});
      notify(tx('🧭 Obrót wysłany: {0}° -- zadziała jeśli masz uruchomiony agent z obrotnicą', [az]));
    } catch { notify(tx('Błąd wysyłki komendy obrotu'),'error'); }
  };

  // Gdy agent jest podlaczony, pokazuj zywa częstotliwość/mode z radia jako
  // punkt startowy do wpisania w polu (nie nadpisuje już wpisanej wartosci
  // jeśli user wlasnie cos wpisuje -- tylko inicjalizuje przy pierwszym wykryciu).
  const initializedFromRig = useRef(false);
  useEffect(() => {
    if (status.connected && status.frequency && !initializedFromRig.current) {
      setDialFreq(String(status.frequency.toFixed ? status.frequency.toFixed(4) : status.frequency));
      if (status.mode) setDialMode(status.mode);
      initializedFromRig.current = true;
    }
  }, [status]);

  useEffect(() => {
    if (open && Object.keys(allFreqs).length === 0) {
      axios.get(`${API}/api/frequencies`).then(r=>setAllFreqs(r.data.frequencies||{})).catch(()=>{});
    }
  }, [open, allFreqs]);

  const selectBand = (band) => {
    const entries = allFreqs[dialMode] || [];
    const match = entries.find(f => f.band === band);
    if (match) setDialFreq(String(match.freq));
  };

  const sendQsy = async () => {
    const freq = parseFreq(dialFreq);
    if (!freq) { notify(t('notify.badFreq'),'error'); return; }
    try {
      if (status.source === 'webserial') {
        await rigQsy(freq, dialMode);
        notify(tx('📻 Radio (USB) nastawione: {0} MHz ({1})', [freq, dialMode]));
        return;
      }
      await axios.post(`${API}/api/cat/command`, {frequency: freq, mode: dialMode});
      notify(tx('📻 QSY wysłane: {0} MHz ({1}) -- zadziała jeśli masz uruchomiony agent CAT', [freq, dialMode]));
    } catch { notify(t('notify.qsyError'),'error'); }
  };

  const availableBands = [...new Set((allFreqs[dialMode]||[]).map(f=>f.band))]
    .sort((a,b)=>BAND_ORDER.indexOf(a)-BAND_ORDER.indexOf(b));

  return (
    <>
      <button className={"radio-fab"+(status.connected?" radio-fab--connected":"")} onClick={()=>setOpen(o=>!o)} title={tx('Sterowanie radiem')}>
        <Icon name="radio" size={16} />{status.connected&&<span className="radio-fab__dot" />}
      </button>

      {open&&(
        <div className="radio-dialer">
          <div className="radio-dialer__header">
            <span>{tx('📻 Radio')}</span>
            <span className={"radio-dialer__status"+(status.connected?" radio-dialer__status--ok":"")}>
              {status.source==='webserial'?tx('🟢 USB (przeglądarka)'):status.connected?tx('🟢 Połączono'):tx('🔴 Brak agenta')}
            </span>
            <button className="modal__close" onClick={()=>setOpen(false)}>✕</button>
          </div>

          <div className="radio-dialer__display">
            <input className="radio-dialer__freq-input" value={dialFreq}
              onChange={e=>setDialFreq(e.target.value)} placeholder="14.200" inputMode="decimal" />
            <span className="radio-dialer__unit">{tx('MHz')}</span>
          </div>

          {status.connected&&(
            <div className="radio-dialer__live">
              {tx('Na żywo z radia:')} {typeof status.frequency==='number'?status.frequency.toFixed(4):status.frequency} {tx('MHz ·')} {status.mode}
            </div>
          )}

          <div className="radio-dialer__modes">
            {DIAL_MODES.map(m=>(
              <button key={m} type="button" className={"radio-dialer__mode"+(dialMode===m?" radio-dialer__mode--active":"")}
                style={dialMode===m?{borderColor:MODE_COLORS[m],color:MODE_COLORS[m]}:{}}
                onClick={()=>setDialMode(m)}>{m}</button>
            ))}
          </div>

          <div className="radio-dialer__bands">
            {(availableBands.length?availableBands:BAND_ORDER).map(b=>(
              <button key={b} type="button" className="radio-dialer__band" onClick={()=>selectBand(b)}>{b}</button>
            ))}
          </div>

          <button type="button" className="btn btn--primary" style={{width:'100%',marginTop:10}} onClick={sendQsy}>
            <Icon name="tower" size={16} /> {tx('Nastaw radio')}
          </button>

          <div style={{marginTop:12,paddingTop:10,borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
            <div style={{position:'relative',width:44,height:44,borderRadius:'50%',border:'2px solid var(--border)',flexShrink:0}}>
              <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',
                transform:`rotate(${rotorStatus.connected&&rotorStatus.azimuth!=null?rotorStatus.azimuth:0}deg)`,
                transition:'transform 0.3s'}}>
                <span style={{fontSize:18,lineHeight:1}}>⬆️</span>
              </div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,color:'var(--text-dim)'}}>
                🧭 {rotorStatus.connected?tx('Azymut: {0}°', [Math.round(rotorStatus.azimuth)]):tx('Brak agenta z obrotnicą')}
              </div>
              <div style={{display:'flex',gap:4,marginTop:4}}>
                <input className="input" style={{width:60,fontSize:12,padding:'2px 6px'}} value={rotorTarget}
                  onChange={e=>setRotorTarget(e.target.value)} placeholder="0-360" />
                <button type="button" className="btn btn--secondary btn--sm" onClick={sendRotorGoto}>{tx('Obróć')}</button>
              </div>
            </div>
          </div>

          <WebSerialRotorPanel notify={notify} />

          <WebSerialPanel notify={notify} />

          <WsjtxPanel notify={notify} remote={remote} />

          <button type="button" className="btn btn--ghost btn--sm" style={{width:'100%',marginTop:8}} onClick={()=>setShowConfig(true)}>
            <Icon name="settings" size={16} /> {tx('Konfiguracja radia')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" style={{width:'100%',marginTop:8}} onClick={()=>setShowWsjtxAgent(true)}>
            <Icon name="download" size={16} /> {tx('Agent HamLogger (WSJT-X + CAT)')}
          </button>
        </div>
      )}

      {showConfig&&<RadioConfigModal onClose={()=>setShowConfig(false)} notify={notify} />}
      {showWsjtxAgent&&<WsjtxAgentModal onClose={()=>setShowWsjtxAgent(false)} notify={notify} />}
    </>
  );
}

function RadioConfigModal({ onClose, notify }) {
  const { t } = useI18n();
  const [rigs, setRigs] = useState([]);
  const [config, setConfig] = useState(null);
  const [rotors, setRotors] = useState([]);
  const [rotorConfig, setRotorConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // allSettled -- blad czesci obrotnicy nie moze blokowac konfiguracji
    // radia (z Promise.all cale okno zostawalo puste).
    Promise.allSettled([
      axios.get(`${API}/api/cat/rig-models`),
      axios.get(`${API}/api/cat/rig-config`),
      axios.get(`${API}/api/rotor/rotor-models`),
      axios.get(`${API}/api/rotor/rig-config`),
    ]).then(([rigsR, configR, rotorsR, rotorConfigR]) => {
      if (rigsR.status === 'fulfilled') setRigs(rigsR.value.data);
      if (configR.status === 'fulfilled') setConfig(configR.value.data);
      if (rotorsR.status === 'fulfilled') setRotors(rotorsR.value.data);
      if (rotorConfigR.status === 'fulfilled') setRotorConfig(rotorConfigR.value.data);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/cat/rig-config`, config);
      if (rotorConfig) await axios.put(`${API}/api/rotor/rig-config`, rotorConfig);
      notify(t('notify.radioSaved'));
      onClose();
    } catch { notify(t('notify.saveError'),'error'); }
    setSaving(false);
  };

  if (!config) return null;
  const updateField = (k,v) => setConfig(c=>({...c,[k]:v}));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
        <div className="modal__header">
          <h3><Icon name="settings" size={18} /> {tx('Konfiguracja radia')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="modal__info">
          {tx('Ustawienia jak w WSJT-X (zakładka Radio) -- model, port, baud rate. Odczytywane przez lokalnego agenta CAT przy starcie. Po zmianie ustawień uruchom agenta ponownie.')}
        </p>

        <div className="form-group">
          <label>{t('radio.model')}</label>
          <select className="input" value={config.rig_model}
            onChange={e=>{
              const rig = rigs.find(r=>r.id===parseInt(e.target.value));
              setConfig(c=>({...c, rig_model: parseInt(e.target.value), rig_model_name: rig?.name||''}));
            }}>
            {rigs.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>{t('radio.connType')}</label>
          <div className="checkbox-group" style={{flexDirection:'column',alignItems:'flex-start',gap:8}}>
            <label className="checkbox-label">
              <input type="radio" checked={config.connection_type==='serial'} onChange={()=>updateField('connection_type','serial')} />
              {tx('Port szeregowy (agent sam uruchomi rigctld)')}
            </label>
            <label className="checkbox-label">
              <input type="radio" checked={config.connection_type==='network'} onChange={()=>updateField('connection_type','network')} />
              {tx('Sieć (rigctld już działa gdzie indziej)')}
            </label>
          </div>
        </div>

        {config.connection_type==='serial'?(
          <>
            <div className="form-row">
              <div className="form-group"><label>{t('radio.serialPort')}</label>
                <input className="input" value={config.serial_port} onChange={e=>updateField('serial_port',e.target.value)} placeholder={tx('COM3 albo /dev/ttyUSB0')} /></div>
              <div className="form-group"><label>{tx('Baud rate')}</label>
                <select className="input" value={config.baud_rate} onChange={e=>updateField('baud_rate',parseInt(e.target.value))}>
                  {[1200,2400,4800,9600,19200,38400,57600,115200].map(b=><option key={b} value={b}>{b}</option>)}
                </select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>{tx('Data bits')}</label>
                <select className="input" value={config.data_bits} onChange={e=>updateField('data_bits',parseInt(e.target.value))}>
                  <option value={7}>7</option><option value={8}>8</option>
                </select></div>
              <div className="form-group"><label>{tx('Stop bits')}</label>
                <select className="input" value={config.stop_bits} onChange={e=>updateField('stop_bits',parseInt(e.target.value))}>
                  <option value={1}>1</option><option value={2}>2</option>
                </select></div>
              <div className="form-group"><label>{tx('Handshake')}</label>
                <select className="input" value={config.handshake} onChange={e=>updateField('handshake',e.target.value)}>
                  <option value="None">{tx('Brak')}</option><option value="Hardware">{tx('Hardware')}</option><option value="Software">{tx('Software')}</option>
                </select></div>
            </div>
            <div className="form-group">
              <label>PTT</label>
              <select className="input" value={config.ptt_method} onChange={e=>updateField('ptt_method',e.target.value)}>
                <option value="CAT">CAT</option><option value="RTS">RTS</option><option value="DTR">DTR</option><option value="None">{tx('Brak')}</option>
              </select>
            </div>
          </>
        ):(
          <>
            <div className="form-row">
              <div className="form-group"><label>{tx('Host')}</label>
                <input className="input" value={config.network_host} onChange={e=>updateField('network_host',e.target.value)} placeholder="localhost" /></div>
              <div className="form-group"><label>{tx('Port')}</label>
                <input className="input" type="number" value={config.network_port} onChange={e=>updateField('network_port',parseInt(e.target.value))} /></div>
            </div>
            {/* Podpowiedz dla flrig -- rozwiazuje najczestszy realny problem:
                port COM moze otworzyc tylko JEDEN program naraz. */}
            {config.rig_model===4&&(
              <div style={{padding:'10px 14px',borderRadius:8,marginBottom:12,
                background:'rgba(0,229,160,0.08)',border:'1px solid rgba(0,229,160,0.3)',
                fontSize:12,color:'var(--text-dim)',lineHeight:1.6}}>
                💡 <strong style={{color:'var(--accent)'}}>{tx('flrig jako pośrednik.')}</strong> {tx('Domyślnie nasłuchuje na')} <code style={{fontFamily:'var(--mono)'}}>{tx('localhost:12345')}</code> {tx('(Host: localhost, Port: 12345).')}
                <br />
                {tx('Największa korzyść: port szeregowy może otworzyć tylko')} <strong>{tx('jeden')}</strong> {tx('program. Gdy flrig jest jedynym właścicielem portu, fldigi, WSJT-X i HamLogger mogą korzystać z radia')} <strong>{tx('jednocześnie')}</strong> {tx('— bez konfliktu o COM.')}
              </div>
            )}
          </>
        )}

        <p style={{fontSize:11,color:'var(--text-dim)',lineHeight:1.6,margin:'10px 0 14px'}}>
          {tx('W trybie "Port szeregowy" agent CAT (')}<code style={{fontFamily:'var(--mono)'}}>{tx('cat_agent/rigctl_agent.py')}</code>{tx(') sam uruchamia i zarządza procesem rigctld (tak jak WSJT-X) -- nie trzeba go odpalać ręcznie w drugim okienku. Wymaga zainstalowanego Hamlib na komputerze, na którym działa agent.')}
        </p>

        {rotorConfig&&(
          <div style={{marginTop:18,paddingTop:14,borderTop:'1px solid var(--border)'}}>
            <label className="checkbox-label" style={{fontSize:13,marginBottom:10}}>
              <input type="checkbox" checked={!!rotorConfig.enabled}
                onChange={e=>setRotorConfig(c=>({...c,enabled:e.target.checked}))} />
              {tx('🧭 Mam obrotnicę anteny (rotor) podłączoną do tego komputera')}
            </label>
            {rotorConfig.enabled&&(
              <>
                <div className="form-group">
                  <label>{tx('Model obrotnicy')}</label>
                  <select className="input" value={rotorConfig.rotor_model}
                    onChange={e=>{
                      const rot = rotors.find(r=>r.id===parseInt(e.target.value));
                      setRotorConfig(c=>({...c, rotor_model: parseInt(e.target.value), rotor_model_name: rot?.name||''}));
                    }}>
                    {rotors.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div className="checkbox-group" style={{flexDirection:'column',alignItems:'flex-start',gap:8,marginBottom:10}}>
                  <label className="checkbox-label">
                    <input type="radio" checked={rotorConfig.connection_type==='serial'} onChange={()=>setRotorConfig(c=>({...c,connection_type:'serial'}))} />
                    {tx('Port szeregowy (agent sam uruchomi rotctld)')}
                  </label>
                  <label className="checkbox-label">
                    <input type="radio" checked={rotorConfig.connection_type==='network'} onChange={()=>setRotorConfig(c=>({...c,connection_type:'network'}))} />
                    {tx('Sieć (rotctld już działa gdzie indziej)')}
                  </label>
                </div>
                {rotorConfig.connection_type==='serial'?(
                  <div className="form-row">
                    <div className="form-group"><label>{tx('Port szeregowy')}</label>
                      <input className="input" value={rotorConfig.serial_port} onChange={e=>setRotorConfig(c=>({...c,serial_port:e.target.value}))} placeholder={tx('COM4 albo /dev/ttyUSB1')} /></div>
                    <div className="form-group"><label>{tx('Baud rate')}</label>
                      <select className="input" value={rotorConfig.baud_rate} onChange={e=>setRotorConfig(c=>({...c,baud_rate:parseInt(e.target.value)}))}>
                        {[1200,2400,4800,9600,19200,38400].map(b=><option key={b} value={b}>{b}</option>)}
                      </select></div>
                  </div>
                ):(
                  <div className="form-row">
                    <div className="form-group"><label>{tx('Host')}</label>
                      <input className="input" value={rotorConfig.network_host} onChange={e=>setRotorConfig(c=>({...c,network_host:e.target.value}))} placeholder="localhost" /></div>
                    <div className="form-group"><label>{tx('Port')}</label>
                      <input className="input" type="number" value={rotorConfig.network_port} onChange={e=>setRotorConfig(c=>({...c,network_port:parseInt(e.target.value)}))} /></div>
                  </div>
                )}
                <label className="checkbox-label" style={{fontSize:13,marginTop:4}}>
                  <input type="checkbox" checked={!!rotorConfig.has_elevation}
                    onChange={e=>setRotorConfig(c=>({...c,has_elevation:e.target.checked}))} />
                  {tx('Obrotnica ma też elewację (typowe dla śledzenia satelitów)')}
                </label>
              </>
            )}
          </div>
        )}

        <button className="btn btn--primary" onClick={save} disabled={saving} style={{width:'100%',marginTop:14}}>
          {saving?'…':tx('✓ Zapisz konfigurację')}
        </button>
      </div>
    </div>
  );
}

// ── Agent HamLogger (WSJT-X live logging + CAT) -- pobranie "plug and play" ──
// WSJT-X wysyla UDP na 127.0.0.1 wlasnego komputera -- ten ruch fizycznie
// nie dotrze do (zwykle zdalnego) serwera HamLoggera, wiec agent lokalny
// jest niezbedny. Ten sam agent obsluguje TEZ CAT (odczyt/QSY radia) --
// dawniej dwa osobne programy, teraz jeden .exe. Nie trzeba niczego recznie
// wpisywac: program pobiera sie jednym klikniem (statyczny .exe), a
// konfiguracja (adres serwera + prywatny token) jest generowana i pobierana
// gotowa z API -- wystarczy wrzucic oba pliki do jednego folderu i odpalic.
function WsjtxAgentModal({ onClose, notify }) {
  const [busy, setBusy] = useState(false);

  const downloadExe = () => {
    window.open('/downloads/HamLogger_Agent.exe', '_blank');
  };

  const downloadConfig = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/wsjtx/agent-config`, {responseType:'blob'});
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'hamlogger_agent.ini';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { notify(tx('Błąd pobierania konfiguracji'),'error'); }
    setBusy(false);
  };

  const regenerate = async () => {
    if (!window.confirm(tx('Nowy token unieważni stary -- już uruchomiony agent przestanie działać, dopóki nie pobierzesz nowej konfiguracji. Kontynuować?'))) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/wsjtx/agent-config/regenerate`);
      notify(tx('Nowy token wygenerowany -- pobierz konfigurację ponownie (przycisk wyżej)'));
    } catch { notify(tx('Blad'),'error'); }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
        <div className="modal__header">
          <h3><Icon name="download" size={18} /> {tx('Agent HamLogger (WSJT-X + CAT)')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="modal__info">
          {tx('Mały program działający na komputerze podłączonym do radia (nie na serwerze) -- automatycznie wysyła do Twojego dziennika każde QSO zalogowane w WSJT-X, oraz (jeśli masz skonfigurowane radio w zakładce "📻 Radio") odczytuje częstotliwość i wykonuje QSY. Jeden program robi teraz obie rzeczy naraz. Nie musisz niczego ręcznie wpisywać -- oba pliki poniżej są już gotowe.')}
        </p>
        <ol style={{fontSize:13,lineHeight:2.2,paddingLeft:20,marginBottom:14}}>
          <li>
            <button className="btn btn--secondary btn--sm" onClick={downloadExe}><Icon name="download" size={16} /> {tx('Pobierz program (.exe)')}</button>
          </li>
          <li>
            <button className="btn btn--secondary btn--sm" disabled={busy} onClick={downloadConfig}><Icon name="download" size={16} /> {tx('Pobierz konfigurację (auto-uzupełnioną)')}</button>
          </li>
          <li>{tx('Wrzuć oba pliki do')} <strong>{tx('tego samego')}</strong> {tx('folderu')}</li>
          <li>{tx('Uruchom')} <code style={{fontFamily:'var(--mono)'}}>{tx('HamLogger_Agent.exe')}</code></li>
          <li>{tx('W WSJT-X: Settings → Reporting → UDP Server, port')} <strong>2237</strong> {tx('(domyślny, zgodny z konfiguracją)')}</li>
          <li>{tx('CAT jest opcjonalny -- jeśli nie skonfigurujesz radia, ta część po prostu się wyłączy bez błędu')}</li>
        </ol>
        <p style={{fontSize:11,color:'var(--text-dim)',lineHeight:1.6}}>
          {tx('Pobrany plik konfiguracji zawiera Twój prywatny token dostępu do agenta -- traktuj go jak hasło, nie udostępniaj nikomu.')}
        </p>
        <button className="btn btn--ghost btn--sm" disabled={busy} onClick={regenerate}>
          <Icon name="send" size={16} /> {tx('Unieważnij token i wygeneruj nowy')}
        </button>
      </div>
    </div>
  );
}

// ── Auth Screen ───────────────────────────────────────────────────────────────
// ── Publiczny profil (/u/ZNAK) -- ladna, graficzna strona zamiast surowego
// JSON pod /api/u/ZNAK. Zero logowania, dziala dla kazdego (jesli user
// wlaczyl "Profil publiczny" w ustawieniach konta). Stylistycznie
// nawiazuje do reszty GUI (te same karty statystyk, pigulki pasm, ciemna
// mapa) -- nie wymysla nowego jezyka wizualnego.
// Prosty, lekki renderer markdown -- bez nowej zaleznosci (nie ma sensu
// dociagac calej biblioteki dla jednej strony regulaminu). Obsluguje
// dokladnie to, czego potrzebuje szablon regulaminu: naglowki #/##,
// **bold**, *italic*, listy "- ", linie "---" jako separator, akapity.
function renderSimpleMarkdown(text) {
  if (!text) return null;
  const renderInline = (s, key) => {
    const parts = s.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return <React.Fragment key={key}>{parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2,-2)}</strong>;
      if (part.startsWith('*') && part.endsWith('*')) return <em key={i}>{part.slice(1,-1)}</em>;
      return part;
    })}</React.Fragment>;
  };
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  const flushList = () => {
    if (listItems.length) {
      elements.push(<ul key={`ul-${elements.length}`} style={{margin:'8px 0',paddingLeft:22}}>
        {listItems.map((li,i)=><li key={i} style={{marginBottom:4}}>{renderInline(li,i)}</li>)}
      </ul>);
      listItems = [];
    }
  };
  lines.forEach((line, idx) => {
    if (line.startsWith('## ')) { flushList(); elements.push(<h3 key={idx} style={{marginTop:20,marginBottom:8,fontFamily:'var(--cond)',color:'var(--accent)'}}>{line.slice(3)}</h3>); }
    else if (line.startsWith('# ')) { flushList(); elements.push(<h2 key={idx} style={{marginTop:24,marginBottom:10}}>{line.slice(2)}</h2>); }
    else if (line.startsWith('- ')) { listItems.push(line.slice(2)); }
    else if (line.trim() === '---') { flushList(); elements.push(<hr key={idx} style={{border:'none',borderTop:'1px solid var(--border)',margin:'20px 0'}} />); }
    else if (line.trim() === '') { flushList(); }
    else { flushList(); elements.push(<p key={idx} style={{margin:'8px 0',lineHeight:1.6}}>{renderInline(line,idx)}</p>); }
  });
  flushList();
  return elements;
}

// ── Przelacznik motywu -- jeden wspoldzielony komponent zamiast 4 kopii
// tego samego przycisku w roznych ekranach (TermsPage, PublicProfilePage,
// AuthScreen, glowna apka). Select zamiast custom dropdow -- dziala od razu
// wszedzie (fixed/absolute/inline), bez wlasnej logiki otwierania/zamykania.
// Zegar UTC w naglowku -- WLASNY stan. Wczesniej stan zegara byl w App, wiec
// co sekunde przerysowywala sie CALA aplikacja (widok pod spodem + otwarte
// okna, np. Ustawienia konta) -- na slabszym sprzecie i przy duzym logu to
// bylo odczuwalne "mulenie". Teraz co sekunde zmienia sie tylko ten element.
function UtcClockChip() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Tykniecie rowno z pelna sekunda, potem co 1 s; w ukrytej karcie przegladarka i tak spowalnia timery
    let t;
    const tick = () => { setNow(new Date()); t = setTimeout(tick, 1000 - (Date.now() % 1000)); };
    t = setTimeout(tick, 1000 - (Date.now() % 1000));
    return () => clearTimeout(t);
  }, []);
  return (
    <span className="status-chip status-chip--clock status-chip--secondary" title={tx('Aktualny czas UTC (do logowania QSO)')}>
      <Icon name="clock" size={14} /> {now.toISOString().slice(11,19)} <small>UTC</small>
    </span>
  );
}

function ThemeSwitcher({ theme, setTheme, style, userProfile }) {
  const available = visibleThemes(userProfile);
  return (
    <select className="input theme-switcher" value={theme} onChange={e=>setTheme(e.target.value)}
      style={{padding:'4px 8px',fontSize:13,cursor:'pointer',...style}} title={tx('Motyw')}>
      {THEME_GROUPS.map(g=>{
        const inGroup = available.filter(th=>th.group===g.id);
        if (inGroup.length===0) return null;  // np. "Ekskluzywne" -- pusta dla kogos bez wczesnego dostepu
        return (
          <optgroup key={g.id} label={g.label}>
            {inGroup.map(th=>(
              <option key={th.id} value={th.id}>{th.icon} {th.label}</option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

// ── Boczne menu nawigacji (alternatywa dla poziomego paska u gory) --------
// Wybierane w Ustawieniach konta -> Wyglad, per przegladarka (localStorage,
// jak motyw). Tylko desktop -- na telefonie zawsze zostaje istniejaca
// szuflada (mobile-nav-drawer), bo boczny pasek staly nie ma sensu na
// waskim ekranie. Ikony pozycji zalezne od motywu (patrz navIconFor) --
// Terminal/Neon maja wlasny zestaw, reszta motywow dzieli domyslny.
// Etykiety z NAV_GROUPS maja WLASNY emoji wpisany na sztywno na poczatku
// ("🎙 Logger", "📖 Dziennik" itd. -- patrz i18n.js/shared.js). SidebarNav
// pokazuje TEZ osobna, zaleznа od motywu ikone (navIconFor) w osobnym
// spanie -- bez tego wyciecia wychodzily dwie ikony obok siebie. Etykieta to
// zawsze "emoji + spacja + tekst", wiec wystarczy uciac pierwszy token.
const stripLabelIcon = (label) => label.replace(/^\S+\s+/, '');
// Etykieta w gornym pasku: ikona + tekst w osobnych <span>, zeby na wezszych
// ekranach (laptop) CSS mogl schowac sam tekst i zostawic ikone (patrz
// .nav-btn__text w App.css). Pelna nazwa zostaje w podpowiedzi (title).
// Kolumny dziennika: kolejnosc, klucz sortowania w API, czy mozna ukryc
const LOG_COLUMNS = [
  {id:'date_time', label:tx('Data/Czas'), sort:'date_time'},
  {id:'callsign', label:tx('Znak'), sort:'callsign'},
  {id:'band', label:tx('Pasmo'), sort:'band', hideable:true},
  {id:'mode', label:tx('Emisja'), sort:'mode', hideable:true},
  {id:'frequency', label:tx('Częst.'), sort:'frequency', hideable:true},
  {id:'rst', label:'RST S/R', hideable:true},
  {id:'operator_name', label:tx('Operator'), sort:'operator_name', hideable:true},
  {id:'country', label:tx('Kraj'), sort:'country', hideable:true},
  {id:'grid_locator', label:tx('Lokator'), sort:'grid_locator', hideable:true},
  {id:'source', label:tx('Źródło'), hideable:true},
  {id:'qsl', label:'QSL', hideable:true},
  {id:'eqsl', label:'eQSL', hideable:true},
  {id:'qrz', label:'QRZ', hideable:true},
];

const NavLabel = ({ label, icon }) => (
  <><span className="nav-btn__icon"><Icon name={icon} size={17} /></span><span className="nav-btn__text">{stripLabelIcon(label)}</span></>
);

// ── Powitanie po pierwszym logowaniu -- jednorazowe podsumowanie kilku
// funkcji, które latwo przeoczyc w apce z kilkudziesiecioma widokami.
// Zapamietywane w localStorage (per przegladarka, jak reszta preferencji UI
// w tej apce -- theme, cookie-notice), nie w koncie -- to czysto lokalna
// podpowiedz, nie stan danych.
function WelcomeModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
        <div className="modal__header">
          <h3>{tx('👋 Witaj w HamLoggerze!')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="modal__info">{tx('Kilka rzeczy, o których łatwo nie wiedzieć na start:')}</p>
        <ul style={{fontSize:13,lineHeight:1.9,paddingLeft:20,marginBottom:16}}>
          <li><strong>{tx('🔍 Ctrl+K')}</strong> {tx('(albo lupka w nagłówku) — szybkie przejście do dowolnego widoku bez klikania po menu.')}</li>
          <li><strong>{tx('🔔 Watchlist + powiadomienia push')}</strong> {tx('— obserwuj znaki/kraje/aktywacje POTA i dostawaj powiadomienie w przeglądarce, nawet gdy karta jest zamknięta.')}</li>
          <li><strong>{tx('📤 Auto-wysyłka QSO')}</strong> {tx('— eQSL.cc, ClubLog i QRZ Logbook mogą wysyłać nowe łączności same, raz dziennie (zakładka eQSL/ClubLog/QRZ, opcja "zapamiętaj i wysyłaj automatycznie").')}</li>
          <li><strong>{tx('🎨 Motywy i układ menu')}</strong> {tx('— poza jasnym/ciemnym są też Spokojny, XP, Vista, Terminal i Neon, a menu można ustawić z boku ekranu (Ustawienia konta → Wygląd).')}</li>
        </ul>
        <div className="form-actions">
          <button className="btn btn--primary" onClick={onClose}>{tx('Rozumiem, zaczynajmy')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Regulamin (/regulamin) -- analogiczna "osobna strona" w SPA jak
// PublicProfilePage nizej -- bez logowania, dostepna rowniez PRZED
// rejestracja. Trescowo edytowalna w calosci przez admina.
function TermsContent() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/api/terms`).then(r=>setData(r.data)).catch(()=>setData({content:tx('Błąd ładowania regulaminu.')})); }, []);
  if (!data) return <p className="auth-sub" style={{textAlign:'center'}}>{t('common.loadingDots')}</p>;
  return (
    <>
      {renderSimpleMarkdown(data.content)}
      {(data.admin_contact_name || data.admin_contact_email)&&(
        <div style={{marginTop:20,padding:14,background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)'}}>
          <strong>{tx('Kontakt do administratora')}{data.org_name?` (${data.org_name})`:''}:</strong><br/>
          {data.admin_contact_name&&<>{data.admin_contact_name}<br/></>}
          {data.admin_contact_email&&<a href={`mailto:${data.admin_contact_email}`} style={{color:'var(--accent)'}}>{data.admin_contact_email}</a>}
        </div>
      )}
    </>
  );
}

// Bezposredni link /regulamin (np. z maila albo z zakladki) nadal dziala jako
// osobna strona; ze stopki regulamin otwiera sie w oknie (InfoModal nizej).
function TermsPage() {
  const { t } = useI18n();
  const [theme, setTheme] = useTheme();
  return (
    <div className="auth-screen">
      <ThemeSwitcher theme={theme} setTheme={setTheme} style={{position:'fixed',top:14,right:14}} />
      <div className="auth-card public-profile-card" style={{textAlign:'left',maxHeight:'88vh',overflowY:'auto'}}>
        <div style={{textAlign:'center',marginBottom:10}}>
          <div className="auth-logo">📡</div>
        </div>
        <TermsContent />
        <p style={{marginTop:24,textAlign:'center'}}>
          <a href="/" style={{color:'var(--accent)',fontWeight:700,textDecoration:'none'}}>{t('common.backToHamlogger')}</a>
        </p>
      </div>
    </div>
  );
}

// ── Changelog (klik w numer wersji w stopce). Plik CHANGELOG.md jest kopiowany
// do frontend/public przez deploy.sh (kontekst builda frontendu nie widzi
// katalogu glownego repo). Wlasny mini-parser, bo format jest inny niz w
// regulaminie: "## [x.y.z] — data", "### Sekcja", punkty "- " z zawijanymi
// liniami wcietymi o 2 spacje, podpunkty "  - ", `kod`.
const CHANGELOG_PAGE = 8;

function renderChangelogInline(text) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) return <code key={i} className="changelog__code">{part.slice(1, -1)}</code>;
    return part;
  });
}

function parseChangelog(md) {
  const versions = [];
  let cur = null, section = null, item = null;
  md.split(/\r?\n/).forEach(line => {
    const vm = line.match(/^## \[([^\]]+)\]\s*[—-]?\s*(.*)$/);
    if (vm) { cur = { version: vm[1], date: vm[2], sections: [] }; versions.push(cur); section = null; item = null; return; }
    if (!cur) return; // naglowek pliku (opis formatu wersji) -- pomijamy
    if (line.startsWith('### ')) { section = { title: line.slice(4), items: [], text: [] }; cur.sections.push(section); item = null; return; }
    if (!line.trim()) return;
    if (!section) { section = { title: '', items: [], text: [] }; cur.sections.push(section); }
    const subItem = line.match(/^\s{2,}- (.*)$/);
    if (/^- /.test(line)) { item = { text: line.slice(2), subs: [] }; section.items.push(item); }
    else if (subItem && item) { item.subs.push(subItem[1]); }
    else if (/^\s+\S/.test(line) && item) {
      const tx = line.trim();
      if (item.subs.length) item.subs[item.subs.length - 1] += ' ' + tx; else item.text += ' ' + tx;
    }
    else { section.text.push(line.trim()); item = null; }
  });
  return versions;
}

function ChangelogContent() {
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(false);
  const [shown, setShown] = useState(CHANGELOG_PAGE);
  useEffect(() => {
    fetch(`/CHANGELOG.md?v=${APP_VERSION}`, { cache: 'no-store' })
      .then(r => { if (!r.ok) throw new Error(); return r.text(); })
      // nginx przy braku pliku oddaje index.html (fallback SPA) -- to nie changelog
      .then(txt => { if (txt.trim().startsWith('<')) throw new Error(); setVersions(parseChangelog(txt)); })
      .catch(() => setError(true));
  }, []);
  if (error) return <p style={{color:'var(--text-dim)'}}>{tx('Nie udało się wczytać listy zmian.')}</p>;
  if (!versions) return <p style={{color:'var(--text-dim)'}}>{tx('⏳ Ładowanie...')}</p>;
  return (
    <div className="changelog">
      {versions.slice(0, shown).map(v => (
        <div key={v.version} className="changelog__version">
          <h3>
            v{v.version}
            {v.version === APP_VERSION && <span className="changelog__current">{tx('obecna')}</span>}
            <span className="changelog__date">{v.date}</span>
          </h3>
          {v.sections.map((sec, si) => (
            <div key={si}>
              {sec.title && <h4>{sec.title}</h4>}
              {sec.text.map((tx, ti) => <p key={ti}>{renderChangelogInline(tx)}</p>)}
              {sec.items.length > 0 && (
                <ul>
                  {sec.items.map((it, ii) => (
                    <li key={ii}>
                      {renderChangelogInline(it.text)}
                      {it.subs.length > 0 && <ul>{it.subs.map((su, k) => <li key={k}>{renderChangelogInline(su)}</li>)}</ul>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ))}
      {shown < versions.length && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShown(n => n + CHANGELOG_PAGE)}>
          {tx('Pokaż starsze wersje (')}{versions.length - shown})
        </button>
      )}
    </div>
  );
}

// Okno "nad strona" dla regulaminu i changelogu -- mocno przyciemnione tlo,
// zamykane przez X, "Wroc", klik w tlo albo Esc.
function InfoModal({ kind, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose]);
  const isTerms = kind === 'terms';
  return (
    <div className="modal-overlay info-modal-overlay" onClick={onClose}>
      <div className="modal info-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <div className="modal__header">
          <h3>{isTerms ? tx('📜 Regulamin') : tx('📝 Lista zmian')}</h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label={tx('Zamknij')}>✕</button>
        </div>
        <div className="info-modal__body">
          {isTerms ? <TermsContent /> : <ChangelogContent />}
        </div>
        <div className="form-actions info-modal__footer">
          <button type="button" className="btn btn--primary btn--sm" onClick={onClose}>{tx('← Wróć')}</button>
          {!isTerms && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={forceRefreshApp}
              title={tx('Czyści cache przeglądarki i pobiera najnowszą wersję aplikacji')}>
              <Icon name="send" size={16} /> {tx('Wymuś pobranie najnowszej wersji')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Publiczne "Czy jestem w logu?" (/log/ZNAK) -- bez logowania. Ktos wpisuje
// SWOJ znak i widzi swoje QSO z ta stacja (data, pasmo, emisja). Dziala tylko
// dla stacji, ktorych wlasciciel wlaczyl to w ustawieniach konta.
function LogSearchPage({ station }) {
  const [theme, setTheme] = useTheme();
  const [available, setAvailable] = useState(null); // null = sprawdzam, true/false
  const [call, setCall] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/logsearch/${station}`)
      .then(() => setAvailable(true))
      .catch(() => setAvailable(false));
  }, [station]);

  const search = async (e) => {
    e.preventDefault();
    const c = call.trim().toUpperCase();
    if (c.length < 3) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await axios.get(`${API}/api/public/logsearch/${station}`, {params: {call: c}});
      setResult(r.data);
    } catch (err) {
      setError(err.response?.status === 429 ? tx('Za dużo zapytań -- spróbuj za minutę.')
        : (err.response?.data?.error || tx('Błąd wyszukiwania')));
    }
    setBusy(false);
  };

  return (
    <div className="auth-screen">
      <ThemeSwitcher theme={theme} setTheme={setTheme} style={{position:'fixed',top:14,right:14}} />
      <div className="auth-card public-profile-card">
        <div className="auth-logo">📡</div>
        <h1 className="auth-title" style={{fontSize:34}}>{station}</h1>
        {available===null&&<p className="auth-sub">{tx('Sprawdzam...')}</p>}
        {available===false&&<p className="auth-sub">{tx('Log tej stacji nie jest publicznie dostępny do przeszukiwania.')}</p>}
        {available&&(
          <>
            <p className="auth-sub">{tx('Czy jestem w logu? Wpisz swój znak.')}</p>
            <form onSubmit={search} style={{display:'flex',gap:8,margin:'14px 0'}}>
              <input className="input" style={{flex:1,fontFamily:'var(--mono)'}}
                value={call} onChange={e=>setCall(e.target.value.toUpperCase())} placeholder={tx('Twój znak')} autoFocus />
              <button className="btn btn--primary" type="submit" disabled={busy||call.trim().length<3}>
                {busy?'…':<><Icon name="search" size={16} /> {tx('Szukaj')}</>}
              </button>
            </form>
            {error&&<p style={{color:'var(--danger)',fontSize:13}}>{error}</p>}
            {result&&(result.found?(
              <>
                <p style={{color:'var(--accent)',fontWeight:700,margin:'6px 0 10px'}}>
                  ✅ {result.call} {tx('-- znaleziono')} {result.qsos.length} {tx('QSO z')} {result.station}
                </p>
                <table className="log-table" style={{width:'100%'}}>
                  <thead><tr><th>{tx('Data/czas (UTC)')}</th><th>{tx('Pasmo')}</th><th>{tx('Emisja')}</th></tr></thead>
                  <tbody>
                    {result.qsos.map((q,i)=>(
                      <tr key={i}><td style={{fontFamily:'var(--mono)'}}>{q.date_time}</td><td>{q.band}</td><td>{q.mode}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            ):(
              <p style={{color:'var(--warning)',margin:'6px 0'}}>
                {tx('❌ Brak QSO')} {result.call} z {result.station} {tx('w logu. Sprawdź pisownię znaku albo spróbuj później -- log mógł jeszcze nie zostać wgrany.')}
              </p>
            ))}
          </>
        )}
        <p style={{marginTop:24,textAlign:'center',fontSize:12}}>
          <a href="/" style={{color:'var(--accent)',textDecoration:'none'}}>{tx('HamLogger')}</a>
        </p>
      </div>
    </div>
  );
}

function PublicProfilePage({ callsign }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [theme, setTheme] = useTheme();

  useEffect(() => {
    axios.get(`${API}/api/u/${callsign}`)
      .then(r => setData(r.data))
      .catch(err => { if (err.response?.status === 404) setNotFound(true); else setLoadError(true); });
  }, [callsign]);

  const homeIcon = L.divIcon({ className:'', html:'<div style="width:14px;height:14px;background:#00e5a0;border:2px solid #fff;border-radius:50%;box-shadow:0 0 8px #00e5a0"></div>', iconAnchor:[7,7] });

  return (
    <div className="auth-screen">
      <ThemeSwitcher theme={theme} setTheme={setTheme} style={{position:'fixed',top:14,right:14}} />

      <div className="auth-card public-profile-card">
        <div className="auth-logo">📡</div>

        {notFound?(
          <>
            <h1 className="auth-title">{callsign}</h1>
            <p className="auth-sub">{tx('Ten profil nie istnieje albo nie jest publiczny.')}</p>
          </>
        ):loadError?(
          <>
            <h1 className="auth-title">{callsign}</h1>
            <p className="auth-sub">{t('profile.loadError')}</p>
          </>
        ):!data?(
          <p className="auth-sub">{t('common.loadingDots')}</p>
        ):(
          <>
            <h1 className="auth-title" style={{fontSize:38}}>{data.callsign}</h1>
            <p className="auth-sub">{t('profile.hamProfile')}</p>

            {(data.total_qsos!==undefined || data.unique_countries!==undefined)&&(
              <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap',margin:'26px 0 10px'}}>
                {data.total_qsos!==undefined&&(
                  <div className="stat-card">
                    <div className="stat-card__value">{data.total_qsos}</div>
                    <div className="stat-card__label">{t('common.qsos')}</div>
                  </div>
                )}
                {data.unique_countries!==undefined&&(
                  <div className="stat-card">
                    <div className="stat-card__value">{data.unique_countries}</div>
                    <div className="stat-card__label">{tx('Kraje (DXCC)')}</div>
                  </div>
                )}
              </div>
            )}

            {data.by_band && Object.keys(data.by_band).length>0&&(
              <div style={{marginTop:18,textAlign:'left'}}>
                <h3 className="public-profile-section-title">{tx('Pasma')}</h3>
                <div className="prop-bands" style={{justifyContent:'center',borderBottom:'none',padding:'4px 0'}}>
                  {Object.entries(data.by_band)
                    .sort((a,b)=>BAND_ORDER.indexOf(a[0])-BAND_ORDER.indexOf(b[0]))
                    .map(([band,count])=>(
                      <div key={band} className="prop-band" style={{'--c':'#00e5a0'}}>
                        <span className="prop-band__name">{band}</span>
                        <span className="prop-band__pill">{count}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {data.worked_countries && data.worked_countries.length>0&&(
              <div style={{marginTop:18,textAlign:'left'}}>
                <h3 className="public-profile-section-title">{tx('Pracowane kraje (')}{data.worked_countries.length})</h3>
                <div style={{display:'flex',flexWrap:'wrap',gap:6,justifyContent:'center'}}>
                  {data.worked_countries.sort().map(c=><span key={c} className="session-badge">{c}</span>)}
                </div>
              </div>
            )}

            {data.home_lat&&data.home_lon&&(
              <div style={{marginTop:18}}>
                <h3 className="public-profile-section-title">{tx('Lokalizacja')}</h3>
                <div style={{height:220,borderRadius:8,overflow:'hidden'}}>
                  <MapContainer center={[data.home_lat,data.home_lon]} zoom={6} style={{height:'100%',width:'100%'}} className="leaflet-dark" zoomControl={false}>
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                      attribution='Tiles &copy; Esri' />
                    <Marker position={[data.home_lat,data.home_lon]} icon={homeIcon} />
                  </MapContainer>
                </div>
              </div>
            )}
          </>
        )}

        <p style={{marginTop:32,fontSize:11,color:'var(--text-dim)'}}>
          {tx('Wygenerowano przez')} <a href="/" style={{color:'var(--accent)',fontWeight:700,textDecoration:'none'}}>{tx('HamLogger')}</a>
        </p>
      </div>
      <Footer />
      <CookieNotice />
    </div>
  );
}

// Stopka -- widoczna na ekranie logowania i w glownej apce. Minimalna,
// nie odpytuje backendu (pelne dane kontaktowe sa na samej stronie
// regulaminu, nie trzeba ich duplikowac tutaj).
// Twarde przeladowanie dostepne globalnie (Footer nie ma dostepu do stanu App).
// Wyrejestrowuje service workery i czysci Cache Storage, potem przeladowuje --
// programowy odpowiednik "wyczysc dane strony".
export async function forceRefreshApp() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } catch { /* i tak sprobuj przeladowac */ }
  window.location.href = window.location.pathname + '?v=' + Date.now();
}

function Footer() {
  const [modal, setModal] = useState(null); // null | 'terms' | 'changelog'
  const close = useCallback(() => setModal(null), []);
  const openTerms = (e) => { e.preventDefault(); setModal('terms'); };
  return (
    <footer className="app-footer">
      © {new Date().getFullYear()} {tx('HamLogger ·')} <a href="/regulamin" onClick={openTerms}>{tx('Regulamin')}</a> · <button type="button" className="app-footer__version" onClick={() => setModal('changelog')}
        title={tx('Wersja aplikacji — kliknij, aby zobaczyć listę zmian')}
        style={{background:'none',border:'none',padding:0,font:'inherit',color:'inherit',cursor:'pointer',textDecoration:'underline dotted'}}>
        v{APP_VERSION}
      </button>
      {modal && <InfoModal kind={modal} onClose={close} />}
    </footer>
  );
}

// Informacja o localStorage/IndexedDB/Cache Storage -- jednorazowy,
// niemozliwy do przeoczenia, ale NIE blokujacy baner (apka nie uzywa
// prawdziwych cookies ani trackerow/reklam/analityki, wiec nie ma tu
// niczego co prawnie wymagaloby zgody -- to czysta informacja, nie prosba
// o zgode). Zapamietywany przez localStorage (tak, tym samym mechanizmem
// o ktorym informuje -- to standardowy, przyjety wzorzec).
//
// WAZNE (nauczka z Footer wyzej): position:fixed, NIE zwykly element w
// normalnym przeplywie -- "auth-screen" i inne kontenery uzywaja
// display:flex bez ustalonego flex-direction, gdzie zwykly sibling
// ladowalby sie OBOK karty logowania, nie pod nia. Fixed pozycjonowanie
// wyjmuje to calkowicie z tej gry, niezaleznie od layoutu rodzica.
function CookieNotice() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('cookie_notice_dismissed') === '1');
  const [showTerms, setShowTerms] = useState(false);
  const closeTerms = useCallback(() => setShowTerms(false), []);
  if (dismissed) return null;
  const dismiss = () => { localStorage.setItem('cookie_notice_dismissed', '1'); setDismissed(true); };
  return (
    <div className="cookie-notice">
      <span>
        {tx('🍪 Ta aplikacja korzysta z localStorage/IndexedDB do logowania i działania offline -- bez śledzenia, reklam czy analityki.')} <a href="/regulamin" onClick={e => { e.preventDefault(); setShowTerms(true); }}>{tx('Szczegóły')}</a>.
      </span>
      <button type="button" className="btn btn--primary btn--sm" onClick={dismiss}>{tx('Rozumiem')}</button>
      {showTerms && <InfoModal kind="terms" onClose={closeTerms} />}
    </div>
  );
}

function AuthScreen({ onLogin }) {
  const { t, lang, setLang } = useI18n();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({callsign:'',email:'',password:''});
  const [accountType, setAccountType] = useState('ham');   // 'ham' | 'swl' (tylko rejestracja)
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [needsAdmin, setNeedsAdmin] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [maintenance, setMaintenance] = useState(null);
  const [theme, setTheme] = useTheme();
  // Zaproszenie z linku (?invite=TOKEN): od razu rejestracja z wpisanym adresem
  const [invite, setInvite] = useState(null);
  const inviteToken = (() => { try { return new URLSearchParams(window.location.search).get('invite') || ''; } catch { return ''; } })();
  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API}/api/invites/check/${encodeURIComponent(inviteToken)}`).then(r => {
      if (!r.data?.valid) return;
      setInvite(r.data);
      setMode('register');
      setAccountType('ham');
      setForm(f => ({ ...f, email: f.email || r.data.email || '' }));
    }).catch(() => {});
  }, [inviteToken]);

  useEffect(() => {
    axios.get(`${API}/api/auth/bootstrap-status`).then(r=>{
      setNeedsAdmin(r.data.needs_admin);
      setRegistrationOpen(r.data.registration_open!==false);
      if (r.data.maintenance_mode) setMaintenance(r.data.maintenance_message || tx('Serwis jest tymczasowo niedostepny (przerwa techniczna). Spróbuj później.'));
    }).catch(()=>{});
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (mode === 'forgot') {
        const r = await axios.post(`${API}/api/auth/request-password-reset`, {email: form.email});
        setResetSent(true);
        setLoading(false);
        return;
      }
      const url = mode === 'login' ? `${API}/api/auth/login` : `${API}/api/auth/register`;
      // Gdy konto ma 2FA, dokladamy kod (pole pojawia sie dopiero po tym, jak
      // backend odpowie 'totp_required' -- nie pytamy o kod na zapas).
      const payload = totpRequired ? {...form, totp_code: totpCode}
        : mode === 'register' ? {...form, account_type: accountType, ui_language: getLang(),
            ...(hasExplicitLang() ? {language: getLang()} : {}),
            ...(invite && inviteToken ? {invite_token: inviteToken} : {})} : form;
      const r = await axios.post(url, payload);
      localStorage.setItem('access_token', r.data.access_token);
      localStorage.setItem('refresh_token', r.data.refresh_token);
      localStorage.setItem('callsign', r.data.callsign);
      if (inviteToken) { try { window.history.replaceState({}, '', window.location.pathname); } catch { /* brak history */ } }
      onLogin(r.data.callsign, r.data.became_admin);
    } catch(err) {
      // 2FA: backend prosi o kod -- pokazujemy pole zamiast bledu
      if (err.response?.data?.totp_required) {
        setTotpRequired(true);
        setTotpCode('');
        setError(err.response?.data?.error || '');  // pusty przy pierwszym pytaniu
      } else {
        setError(err.response?.data?.error || tx('Blad polaczenia'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-corner">
          <select className="input auth-lang" value={lang} onChange={e=>changeLanguage(setLang, e.target.value, false)}
            title={t('lang.label')} aria-label={t('lang.label')}>
            {AVAILABLE_LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
          <ThemeSwitcher theme={theme} setTheme={setTheme} />
        </div>
        <div className="auth-logo">📡</div>
        <h1 className="auth-title">{tx('HamLogger')}</h1>
        <p className="auth-sub">{tx('Dziennik łączności krotkofalarskiej')}</p>
        {maintenance&&(
          <div className="auth-bootstrap-notice" style={{background:'rgba(255,68,68,0.1)',borderColor:'rgba(255,68,68,0.35)',color:'#ff8888'}}>
            🔧 {maintenance}
          </div>
        )}
        {needsAdmin&&mode!=='forgot'&&(
          <div className="auth-bootstrap-notice">
            ℹ️ {mode==='login'
              ? tx('W systemie nie ma jeszcze administratora -- zaloguj się, a automatycznie nim zostaniesz.')
              : tx('W systemie nie ma jeszcze administratora -- po zarejestrowaniu zaloguj się, a automatycznie nim zostaniesz.')}
          </div>
        )}
        {invite&&mode==='register'&&(
          <div className="auth-invite">
            <Icon name="mail" size={16} />
            <span>{tx('{0} zaprasza Cię do HamLoggera — załóż konto poniżej.', [invite.inviter])}</span>
          </div>
        )}
        {mode!=='forgot'&&(
          <div className="auth-tabs">
            <button className={"auth-tab"+(mode==='login'?" auth-tab--active":"")} onClick={()=>{setMode('login');setError('');}}>{tx('Logowanie')}</button>
            {registrationOpen&&(
              <button className={"auth-tab"+(mode==='register'?" auth-tab--active":"")} onClick={()=>{setMode('register');setError('');}}>{tx('Rejestracja')}</button>
            )}
          </div>
        )}
        {mode==='forgot'&&resetSent?(
          <div style={{textAlign:'center',padding:'12px 0'}}>
            <div style={{fontSize:32,marginBottom:12}}>✉️</div>
            <p style={{color:'var(--text)',fontSize:13,lineHeight:1.6}}>
              {tx('Jeśli podany email istnieje w naszej bazie, wyslalismy link resetujacy hasło.')}
            </p>
            <button className="btn btn--ghost btn--sm" style={{marginTop:12}}
              onClick={()=>{setMode('login');setResetSent(false);}}>{tx('← Wroc do logowania')}</button>
          </div>
        ):(
        <form onSubmit={submit} className="auth-form">
          {mode==='register'&&(
            <div className="auth-type" role="radiogroup" aria-label={tx('Typ konta')}>
              <button type="button" role="radio" aria-checked={accountType==='ham'}
                className={'auth-type__opt'+(accountType==='ham'?' auth-type__opt--active':'')} onClick={()=>setAccountType('ham')}>
                <Icon name="mic" size={18} />
                <span><strong>{tx('Krótkofalowiec')}</strong><small>{tx('Mam znak wywoławczy i nadaję')}</small></span>
              </button>
              <button type="button" role="radio" aria-checked={accountType==='swl'}
                className={'auth-type__opt'+(accountType==='swl'?' auth-type__opt--active':'')} onClick={()=>setAccountType('swl')}>
                <Icon name="headphones" size={18} />
                <span><strong>{tx('Nasłuchowiec (SWL)')}</strong><small>{tx('Słucham i prowadzę dziennik nasłuchów')}</small></span>
              </button>
            </div>
          )}
          {mode!=='forgot'&&(
            <div className="form-group">
              <label>{mode==='register'&&accountType==='swl' ? tx('Identyfikator SWL') : t('auth.callsign')}</label>
              <input className="input" value={form.callsign} onChange={e=>setForm(f=>({...f,callsign:e.target.value.toUpperCase()}))}
                placeholder={mode==='register'&&accountType==='swl' ? tx('np. SP9-1234') : 'SP1ABC'} required />
              {mode==='register'&&accountType==='swl'&&(
                <p className="auth-hint">
                  {tx('Numer nasłuchowca (np. SP9-1234) albo inny identyfikator — litery, cyfry, „/” i „-”. Będziesz się nim logować. Konto zaczyna od dziennika nasłuchów; zwykłe QSO możesz dodać później, gdy zdobędziesz licencję.')}
                </p>
              )}
            </div>
          )}
          {(mode==='register'||mode==='forgot')&&(
            <div className="form-group">
              <label>{tx('Email')}</label>
              <input className="input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="operator@example.com" required />
            </div>
          )}
          {mode!=='forgot'&&(
            <div className="form-group">
              <label>{t('auth.password')}</label>
              <input className="input" type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="••••••••" required />
            </div>
          )}
          {/* 2FA -- pole pojawia sie DOPIERO gdy backend zazada kodu. Akceptuje
              tez kod zapasowy (gdy telefon niedostepny), stad maxLength 9. */}
          {totpRequired&&mode==='login'&&(
            <div className="form-group">
              <label>{t('2fa.code')}</label>
              <input className="input" value={totpCode} maxLength={9} autoFocus
                onChange={e=>setTotpCode(e.target.value.toUpperCase())}
                placeholder="000000"
                style={{fontFamily:'var(--mono)',fontSize:18,letterSpacing:3,textAlign:'center'}} />
              <p style={{fontSize:11,color:'var(--text-dim)',marginTop:6,lineHeight:1.5}}>
                {t('auth.totpHint')}
              </p>
            </div>
          )}
          {error&&<div className="auth-error">{error}</div>}
          <button className="btn btn--primary btn--lg" type="submit" disabled={loading} style={{width:'100%'}}>
            {loading?'...':mode==='login'?(totpRequired?t('auth.confirmCode'):t('auth.login')):mode==='register'?t('auth.register'):t('auth.sendResetLink')}
          </button>
          {mode==='login'&&(
            <button type="button" className="btn btn--ghost btn--sm" style={{width:'100%'}}
              onClick={()=>{setMode('forgot');setError('');}}>{tx('Zapomnialem hasła')}</button>
          )}
          {mode==='forgot'&&(
            <button type="button" className="btn btn--ghost btn--sm" style={{width:'100%'}}
              onClick={()=>{setMode('login');setError('');}}>{tx('← Wroc do logowania')}</button>
          )}
        </form>
        )}
      </div>
      <Footer />
      <CookieNotice />
    </div>
  );
}

// ── Reset Password Screen (z linku w mailu /reset-password/<token>) ──────────
function ResetPasswordScreen({ token, onDone }) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (password.length < 6) { setError(tx('Haslo musi miec min. 6 znakow')); return; }
    if (password !== confirm) { setError(tx('Hasla nie sa identyczne')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {token, password});
      setDone(true);
    } catch(err) {
      setError(err.response?.data?.error || tx('Blad resetowania hasla'));
    }
    setLoading(false);
  };

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-logo">🔑</div>
        <h1 className="auth-title">{tx('Reset hasła')}</h1>
        {done?(
          <div style={{textAlign:'center',padding:'12px 0'}}>
            <p style={{color:'var(--accent)',fontSize:14}}>{tx('✓ Hasło zostalo zmienione!')}</p>
            <button className="btn btn--primary" style={{marginTop:12,width:'100%'}} onClick={onDone}>{tx('Przejdz do logowania')}</button>
          </div>
        ):(
          <form onSubmit={submit} className="auth-form">
            <div className="form-group">
              <label>{t('auth.newPassword')}</label>
              <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required autoFocus />
            </div>
            <div className="form-group">
              <label>{t('auth.repeatPassword')}</label>
              <input className="input" type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="••••••••" required />
            </div>
            {error&&<div className="auth-error">{error}</div>}
            <button className="btn btn--primary btn--lg" type="submit" disabled={loading} style={{width:'100%'}}>
              {loading?'...':tx('Ustaw nowe haslo')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Verify Email Screen (z linku w mailu /verify-email/<token>) ──────────────
function VerifyEmailScreen({ token, onDone }) {
  const [status, setStatus] = useState('loading'); // loading / success / error
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    axios.post(`${API}/api/auth/verify-email/${token}`)
      .then(() => setStatus('success'))
      .catch(err => { setStatus('error'); setErrMsg(err.response?.data?.error||tx('Blad weryfikacji')); });
  }, [token]);

  return (
    <div className="auth-screen">
      <div className="auth-card" style={{textAlign:'center'}}>
        <div className="auth-logo">{status==='loading'?'…':status==='success'?'✅':'⚠️'}</div>
        <h1 className="auth-title">{tx('Weryfikacja Email')}</h1>
        {status==='loading'&&<p style={{color:'var(--text-dim)'}}>{tx('Sprawdzanie...')}</p>}
        {status==='success'&&<p style={{color:'var(--accent)'}}>{tx('Adres email zostal potwierdzony!')}</p>}
        {status==='error'&&<p style={{color:'#ff8888'}}>{errMsg}</p>}
        {status!=='loading'&&(
          <button className="btn btn--primary" style={{marginTop:16,width:'100%'}} onClick={onDone}>{tx('Przejdz do aplikacji')}</button>
        )}
      </div>
    </div>
  );
}

// Konwersja klucza VAPID (base64url, tak jak zwraca go backend) na Uint8Array
// -- to format, jakiego oczekuje PushManager.subscribe({applicationServerKey}).
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// ── Watchlist View ─────────────────────────────────────────────────────────────
function WatchlistView({ notify, userProfile }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({callsign:'',country:'',notify_telegram:true,notify_email:false,notify_push:true});
  const [addedMsg, setAddedMsg] = useState(null);
  const [pushStatus, setPushStatus] = useState('unknown'); // 'unknown' | 'unsupported' | 'denied' | 'subscribed' | 'not-subscribed'
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushStatus('unsupported'); return; }
    if (Notification.permission === 'denied') { setPushStatus('denied'); return; }
    navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription()).then(sub => {
      setPushStatus(sub ? 'subscribed' : 'not-subscribed');
    }).catch(() => setPushStatus('not-subscribed'));
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setPushStatus('denied'); setPushBusy(false); return; }
      const { data } = await axios.get(`${API}/api/push/vapid-public-key`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      await axios.post(`${API}/api/push/subscribe`, sub.toJSON());
      setPushStatus('subscribed');
      notify(tx('Powiadomienia push włączone dla tej przeglądarki 🔔'));
    } catch (err) {
      notify(tx('Nie udało się włączyć powiadomień push'),'error');
    }
    setPushBusy(false);
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API}/api/push/subscribe`, {data:{endpoint:sub.endpoint}});
        await sub.unsubscribe();
      }
      setPushStatus('not-subscribed');
      notify(tx('Powiadomienia push wyłączone dla tej przeglądarki'));
    } catch { notify(tx('Błąd wylaczania powiadomien'),'error'); }
    setPushBusy(false);
  };

  const load = async () => {
    try { const r = await axios.get(`${API}/api/watchlist`); setItems(r.data); } catch {}
  };
  useEffect(()=>{ load(); },[]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/watchlist`, form);
      setForm({callsign:'',country:'',notify_telegram:true,notify_email:false,notify_push:true});
      load();
      setAddedMsg('Dodano do watchlisty!');
      setTimeout(()=>setAddedMsg(null),3000);
    } catch (err) { notify(err.response?.data?.error || tx('Nie dodano do watchlisty'), 'error'); }
  };

  const del = async (id) => {
    await axios.delete(`${API}/api/watchlist/${id}`);
    load();
  };

  return (
    <section className="section">
      <div className="section__header">
        <h2><Icon name="star" size={20} /> {t('watchlist.title')}</h2>
        <span style={{fontSize:12,color:'var(--text-dim)'}}>
          {tx('Gdy znak lub kraj pojawi się w klastrze DX (w tym RBN), albo obserwowany znak aktywuje park POTA — dostaniesz powiadomienie. Dla POTA możesz też wpisać prefiks z gwiazdką (np. SP*), żeby złapać dowolny znak zaczynający się tak jak podałeś.')}
        </span>
      </div>
      <div style={{padding:'20px'}}>
        {addedMsg&&<div className="notification notification--success" style={{position:'relative',top:0,right:0,marginBottom:12}}>{addedMsg}</div>}

        <div style={{marginBottom:20,padding:'12px 16px',background:'var(--bg3)',borderRadius:6,border:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--text-bright)',display:'flex',alignItems:'center',gap:6}}><Icon name="bell" size={16} /> {tx('Powiadomienia push (ta przeglądarka)')}</div>
          {pushStatus==='unsupported'&&<span style={{fontSize:12,color:'var(--text-dim)'}}>{tx('Ta przeglądarka nie wspiera powiadomień push.')}</span>}
          {pushStatus==='denied'&&<span style={{fontSize:12,color:'#ff8888'}}>{tx('Zablokowane w ustawieniach przeglądarki -- odblokuj ręcznie, żeby włączyć.')}</span>}
          {pushStatus==='subscribed'&&(
            <>
              <span style={{fontSize:12,color:'var(--accent)'}}>{tx('✅ Włączone')}</span>
              <button type="button" className="btn btn--ghost btn--sm" disabled={pushBusy} onClick={disablePush}>{tx('Wyłącz na tym urządzeniu')}</button>
            </>
          )}
          {pushStatus==='not-subscribed'&&(
            <button type="button" className="btn btn--secondary btn--sm" disabled={pushBusy} onClick={enablePush}>
              {pushBusy?'…':tx('Włącz powiadomienia push')}
            </button>
          )}
        </div>

        <form onSubmit={add} style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:20}}>
          <input className="input" style={{maxWidth:140,fontFamily:'var(--mono)'}}
            value={form.callsign} onChange={e=>setForm(f=>({...f,callsign:e.target.value.toUpperCase()}))}
            placeholder={tx('Znak (np. 3B9FR lub SP*)')}
            title={tx('Dla dopasowania aktywacji POTA możesz użyć wildcarda -- np. SP* złapie SP1ABC, SP3DCD itd. Dopasowanie w klastrze DX pozostaje dokładne (bez wildcarda).')} />
          <input className="input" style={{maxWidth:180}}
            value={form.country} onChange={e=>setForm(f=>({...f,country:e.target.value}))}
            placeholder={tx('Kraj (np. Reunion)')} />
          <label className="checkbox-label">
            <input type="checkbox" checked={form.notify_telegram} onChange={e=>setForm(f=>({...f,notify_telegram:e.target.checked}))}/>
            {tx('Telegram')}
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.notify_email||false} onChange={e=>setForm(f=>({...f,notify_email:e.target.checked}))}/>
            {tx('E-mail')}
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.notify_push!==false} onChange={e=>setForm(f=>({...f,notify_push:e.target.checked}))}/>
            {tx('🔔 Push')}
          </label>
          <button className="btn btn--primary" type="submit">{tx('+ Dodaj')}</button>
        </form>
        <div className="table-wrap">
          <table className="log-table">
            <thead><tr><th>{tx('Znak')}</th><th>{tx('Kraj')}</th><th>{tx('Telegram')}</th><th>{tx('E-mail')}</th><th>{tx('Push')}</th><th></th></tr></thead>
            <tbody>
              {items.length===0&&<tr><td colSpan={6} className="empty-state">{tx('Watchlista jest pusta')}</td></tr>}
              {items.map(w=>(
                <tr key={w.id} className="log-row">
                  <td style={{fontFamily:'var(--mono)',color:'var(--accent)'}}>{w.callsign||'—'}</td>
                  <td>{w.country||'—'}</td>
                  <td>{w.notify_telegram?'✅':'—'}</td>
                  <td>{w.notify_email?'✅':'—'}</td>
                  <td>{w.notify_push?'✅':'—'}</td>
                  <td><button className="btn-icon btn-icon--danger" onClick={()=>del(w.id)}><Icon name="trash" size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{marginTop:20,padding:'12px 16px',background:'var(--bg3)',borderRadius:6,border:'1px solid var(--border)'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--text-bright)',display:'flex',alignItems:'center',gap:6,marginBottom:6}}><Icon name="chat" size={16} /> {tx('Webhook Discord')}</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <input className="input" style={{flex:1,minWidth:240,fontFamily:'var(--mono)',fontSize:12}}
              defaultValue={userProfile?.webhook_url||''}
              placeholder="https://discord.com/api/webhooks/..."
              onBlur={async e=>{
                try {
                  await axios.put(`${API}/api/auth/update`,{webhook_url:e.target.value});
                  notify(tx('Webhook Discord zapisany -- alerty z watchlisty będą teraz wysylane na Discord'));
                } catch { notify(tx('Błąd zapisu webhooka'),'error'); }
              }} />
          </div>
          <div style={{fontSize:11,color:'var(--text-dim)',marginTop:6}}>
            {tx('Wklej tu link webhooka swojego kanalu Discord -- alerty z watchlisty (wszystkie, bez osobnego checkboxa) będą wysylane jako kolorowe "embedy". Działa również ze Slack (URL webhooka z ustawien kanalu) -- wtedy alerty wysylane są jako tekst.')}
          </div>
        </div>

        <div style={{marginTop:12,padding:'12px 16px',background:'var(--bg3)',borderRadius:6,border:'1px solid var(--border)'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--text-bright)',marginBottom:6}}>{tx('Jak skonfigurować Telegram')}</div>
          <div style={{fontSize:12,color:'var(--text)',lineHeight:1.8}}>
            {tx('1. Napisz do')} <strong>{tx('@BotFather')}</strong> {tx('na Telegramie → /newbot → skopiuj token')}<br/>
            {tx('2. Admin wpisuje token w panelu Admin → Ustawienia (albo zmiennej')} <code>TELEGRAM_BOT_TOKEN</code>)<br/>
            {tx('3. Napisz /start do swojego bota i wpisz swoje Chat ID w ustawieniach profilu')}<br/>
            {tx('4. Alerty będą wysylane automatycznie gdy HamLogger znajdzie spot z watchlisty')}
          </div>
        </div>

        <div style={{marginTop:12,padding:'12px 16px',background:'var(--bg3)',borderRadius:6,border:'1px solid var(--border)'}}>
          <div style={{fontSize:13,fontWeight:600,color:'var(--text-bright)',marginBottom:6}}>{tx('Jak skonfigurować Discord')}</div>
          <div style={{fontSize:12,color:'var(--text)',lineHeight:1.8}}>
            {tx('1. Na swoim serwerze Discord: Ustawienia kanalu → Integracje → Webhooki → Nowy webhook')}<br/>
            {tx('2. Skopiuj URL webhooka i wklej w pole powyzej')}<br/>
            {tx('3. Gotowe -- bez dodatkowej konfiguracji, alerty pojawia się automatycznie jako "embed"')}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Admin Panel ────────────────────────────────────────────────────────────────
// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const { t, lang, setLang } = useI18n();
  // Publiczny profil (/u/ZNAK) -- calkowicie osobna "strona" w obrebie tej
  // samej aplikacji SPA, bez logowania, renderowana ZAMIAST normalnego
  // ekranu logowania/loggera. Sprawdzane PRZED jakimkolwiek innym hookiem
  // (useState callsign itd.) -- to bezpieczne, bo ta sciezka jest ustalona
  // raz, na podstawie URL przy wczytaniu strony, i nie zmienia sie w czasie
  // zycia tego konkretnego zamontowania komponentu (uzytkownik nie
  // "przelacza sie" miedzy /u/ZNAK a reszta apki bez pelnego przeladowania
  // strony) -- wiec nie narusza to zasady "hooki w tej samej kolejnosci".
  const publicProfileMatch = window.location.pathname.match(/^\/u\/([A-Za-z0-9]+)\/?$/);
  if (publicProfileMatch) {
    return <PublicProfilePage callsign={publicProfileMatch[1].toUpperCase()} />;
  }
  // "Czy jestem w logu?" -- ta sama zasada (osobna publiczna strona).
  const logSearchMatch = window.location.pathname.match(/^\/log\/([A-Za-z0-9]+)\/?$/);
  if (logSearchMatch) {
    return <LogSearchPage station={logSearchMatch[1].toUpperCase()} />;
  }
  // Regulamin -- ta sama zasada jak wyzej (osobna "strona", bez logowania,
  // dostepna rowniez PRZED rejestracja -- m.in. stad link z ekranu logowania).
  if (window.location.pathname.replace(/\/$/, '') === '/regulamin') {
    return <TermsPage />;
  }

  const [callsign, setCallsign] = useState(localStorage.getItem('callsign'));
  const [justBecameAdmin, setJustBecameAdmin] = useState(false);
  const [startView, setStartView] = useStartView();
  const [view, setView] = useState(() => startView);
  const swlStartDone = useRef(false);
  const [tableDensity, setTableDensity] = useTableDensity();
  const [qsos, setQsos] = useState([]);
  const [stats, setStats] = useState(null);
  const [qrzStatus, setQrzStatus] = useState({logged_in:false});
  const [bearingInfo, setBearingInfo] = useState(null);   // {distanceKm, bearingDeg, compass} -- po znalezieniu wspolrzednych stacji
  const [newAchievement, setNewAchievement] = useState(null); // {is_new_country, is_new_band, is_new_mode}
  const [routeProp, setRouteProp] = useState(null); // wynik predykcji propagacji trasy (modal)
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  // Filtry logu zapamietane miedzy sesjami (localStorage) -- nie resetuja sie
  // przy kazdym wejsciu. Lazy init z zapisanych wartosci.
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBand, setFilterBand] = useState(()=>{ try { return localStorage.getItem('log_filter_band')||''; } catch { return ''; } });
  const [filterMode, setFilterMode] = useState(()=>{ try { return localStorage.getItem('log_filter_mode')||''; } catch { return ''; } });
  const [filterTag, setFilterTag] = useState('');
  useEffect(()=>{ try { localStorage.setItem('log_filter_band', filterBand); } catch {} }, [filterBand]);
  useEffect(()=>{ try { localStorage.setItem('log_filter_mode', filterMode); } catch {} }, [filterMode]);
  const [page, setPage] = useState(1);
  // Dziennik: sortowanie (klik w naglowek) i ukryte kolumny -- zapamietane
  const [logSort, setLogSortState] = useState(() => { try { return JSON.parse(localStorage.getItem('log_sort')) || {key:'date_time',order:'desc'}; } catch { return {key:'date_time',order:'desc'}; } });
  const setLogSort = (v) => { setLogSortState(v); try { localStorage.setItem('log_sort', JSON.stringify(v)); } catch { /* brak storage */ } };
  const [hiddenCols, setHiddenColsState] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem('log_hidden_cols')) || []); } catch { return new Set(); } });
  const toggleCol = (id) => setHiddenColsState(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); try { localStorage.setItem('log_hidden_cols', JSON.stringify([...n])); } catch { /* brak storage */ } return n; });
  const [totalPages, setTotalPages] = useState(1);
  const [editingQso, setEditingQso] = useState(null);
  // Godzina QSO ma "zywo" trzymac obecny czas komputera dopoki user jej recznie
  // nie zmieni (np. cofnie o kilka minut, bo lacznosc byla wczesniej) -- wtedy
  // przestajemy nadpisywac, zeby nie "zjadac" jego korekty. Wraca do trybu
  // zywego po zapisie QSO albo klikniecim "Teraz". Nie dotyczy edycji istniejacego
  // QSO -- tam czas ma zostac taki, jaki byl zapisany.
  const [dateTimeAuto, setDateTimeAuto] = useState(true);
  const [qsoStart, setQsoStart] = useState(null);   // poczatek QSO (chwila wpisania znaku) -- patrz buildQsoPayload
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // B4 (worked before) -- dyskretna historia znaku z calego logu (nie tylko
  // dzis, jak duplicateWarning). Pokazuje, czy i na jakich pasmach/emisjach
  // juz go masz. Informacyjne, nie blokujace.
  const [workedBefore, setWorkedBefore] = useState(null);
  const [selectedQsoIds, setSelectedQsoIds] = useState(new Set());
  const [bulkBand, setBulkBand] = useState('');
  const [bulkMode, setBulkMode] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [qslCardQso, setQslCardQso] = useState(null);
  const [bulkStationCall, setBulkStationCall] = useState('');
  const [showQrzModal, setShowQrzModal] = useState(false);
  // Karta stacji w oknie -- znak z loggera, z palety (Ctrl+K) albo z panelu
  const [stationCardCall, setStationCardCall] = useState(null);
  const [pinnedViews, togglePinnedView] = usePinnedViews();
  const [sideCollapsed, setSideCollapsedState] = useState(() => localStorage.getItem('nav_collapsed') === '1');
  const setSideCollapsed = (v) => { setSideCollapsedState(v); try { localStorage.setItem('nav_collapsed', v ? '1' : '0'); } catch { /* brak storage */ } };
  // Rzadziej uzywane pola loggera (imie, QTH, QSL, notatki...) -- zwiniete,
  // stan zapamietany. Imie/QTH/kraj i tak uzupelnia wyszukiwanie znaku.
  const [showMoreFields, setShowMoreFieldsState] = useState(() => localStorage.getItem('logger_more_fields') === '1');
  const [showFreqPicker, setShowFreqPickerState] = useState(() => localStorage.getItem('logger_freq_picker') === '1');
  const setShowFreqPicker = (v) => { setShowFreqPickerState(v); try { localStorage.setItem('logger_freq_picker', v ? '1' : '0'); } catch { /* brak storage */ } };
  const setShowMoreFields = (v) => { setShowMoreFieldsState(v); try { localStorage.setItem('logger_more_fields', v ? '1' : '0'); } catch { /* brak storage */ } };
  const [sessionCount, setSessionCount] = useState(0);
  // Tryb aktywacji POTA -- licznik QSO zalogowanych W TEJ SESJI pod aktualnym
  // pota_ref (osobny od sessionCount, ktory liczy WSZYSTKIE QSO niezaleznie
  // od parku). Nazwa parku z lookupu -- czysto informacyjna, nie zapisywana
  // do QSO (to pole formularza pota_ref juz robi -- patrz "Uwaga:
  // pota_ref/... NIE sa czyszczone" przy resecie formularza).
  const [potaActivationCount, setPotaActivationCount] = useState(0);
  const [potaParkName, setPotaParkName] = useState('');
  const [potaLookupBusy, setPotaLookupBusy] = useState(false);

  // Wolne tagi QSO -- lista znanych tagow (do autouzupelniania w polu ponizej,
  // <datalist>) zaladowana raz przy montowaniu.
  const [availableTags, setAvailableTags] = useState([]);
  useEffect(() => { axios.get(`${API}/api/qso/tags`).then(r=>setAvailableTags(r.data)).catch(()=>{}); }, []);

  // Makra klawiszy funkcyjnych (F1-F6) do notatek -- patrz useNoteMacros w
  // hooks.js. Nasluch globalny na window, ale aktywny TYLKO gdy widok
  // loggera jest otwarty (view==='logger') -- inaczej F-klawisze wstawialyby
  // tekst do notatek nawet gdy user ogladalby np. mape czy statystyki.
  const [noteMacros, setNoteMacros] = useNoteMacros();
  const insertMacro = useCallback((text) => {
    setForm(f => ({...f, notes: f.notes ? `${f.notes} ${text}` : text}));
  }, []);
  useEffect(() => {
    if (view !== 'logger') return;
    const onKeyDown = (e) => {
      const m = noteMacros.find(mm => mm.key === e.key);
      if (!m) return;
      e.preventDefault();
      insertMacro(m.text);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [view, noteMacros, insertMacro]);
  const editMacro = (idx) => {
    const current = noteMacros[idx];
    const next = window.prompt(tx('Tekst makra {0}:', [current.key]), current.text);
    if (next === null) return;
    setNoteMacros(noteMacros.map((m,i) => i===idx ? {...m, text: next} : m));
  };
  // Zegar UTC w naglowku loggera -- stala, czytelna referencja czasu bez
  // patrzenia w pasek systemowy (i bez mylenia z czasem lokalnym). Tyka co 1s.
  const [timeData, setTimeData] = useState({});
  const [dxccList, setDxccList] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  // Jezyk konta (raz na wczytanie profilu) -- patrz syncLanguageWithAccount
  const langSyncedFor = useRef(null);
  useEffect(() => {
    if (!userProfile || langSyncedFor.current === userProfile.id) return;
    langSyncedFor.current = userProfile.id;
    syncLanguageWithAccount(userProfile);
  }, [userProfile]);
  const [agentCatStatus, setCatStatus] = useState({connected:false});
  const webRig = useWebSerialRig();
  const catStatus = rigAsCatStatus(webRig) || agentCatStatus;
  // Po odswiezeniu: wznow polaczenie USB z radiem, jesli przegladarka pamieta zgode na port
  useEffect(() => { autoReconnectRig().then(() => autoReconnectRotor()); }, []);
  useRadioStatusPush(catStatus);
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [showOfflineQueueModal, setShowOfflineQueueModal] = useState(false);
  const [showSyncPrompt, setShowSyncPrompt] = useState(false);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  // Nasluch zdarzenia z index.js -- nowa wersja Service Workera już
  // aktywna w tle, ale NIE przeladowujemy automatycznie (mogloby skasowac
  // niezapisany formularz QSO) -- pokazujemy baner, user odswieza kiedy
  // jest gotowy.
  // Pamiec banera w obrebie sesji karty (sessionStorage):
  //  - update_dismissed = wersja, dla ktorej kliknieto "Pozniej" (bez ponownego
  //    nagabywania co 2 minuty -- wczesniej baner wracal przy kazdym sprawdzeniu),
  //  - update_refreshed = wersja, dla ktorej kliknieto "Odswiez teraz"; jesli po
  //    odswiezeniu serwer nadal ma inna wersje niz wczytany kod (np. trwa
  //    wdrozenie), nie pokazujemy banera znowu -- inaczej petla "odswiez -> baner".
  const updateMemo = (key, v) => { try { if (v === undefined) return sessionStorage.getItem(key); sessionStorage.setItem(key, v); } catch { return null; } return null; };
  const latestServerVersion = useRef(null);
  const offerUpdate = useCallback((serverVersion) => {
    const tag = serverVersion || 'sw';
    if (updateMemo('update_dismissed') === tag || updateMemo('update_refreshed') === tag) return;
    setSwUpdateAvailable(true);
  }, []);
  useEffect(() => {
    const onSwUpdate = () => offerUpdate(latestServerVersion.current);
    window.addEventListener('sw-update-available', onSwUpdate);
    return () => window.removeEventListener('sw-update-available', onSwUpdate);
  }, []);

  // ── DRUGI, NIEZALEZNY sposob wykrycia nowej wersji ───────────────────────
  // Mechanizm wyzej opiera się w CALOSCI na cyklu zycia Service Workera
  // (controllerchange). Gdy SW z jakiegokolwiek powodu się zatnie -- nie
  // zainstaluje się, utknie w stanie "waiting", albo przegladarka nie odpyta
  // pliku -- user NIE DOWIE SIĘ, ze jest nowa wersja, i zostanie na starym
  // kodzie. Objaw: "dopiero wyczyszczenie danych strony/cookies pomaga".
  //
  // Dlatego pytamy backend WPROST: jaka wersja jest wdrozona? Backend nie ma
  // nic wspolnego z cache przeglądarki, wiec ta sciezka działa nawet gdy cala
  // warstwa SW/cache jest zepsuta. Porownujemy z wersja WKOMPILOWANA w ten
  // bundle (APP_VERSION) -- jeśli się roznia, user ma stary kod.
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        // cache:'no-store' -- ta odpowiedz NIGDY nie może przyjsc z cache,
        // bo caly sens polega na uzyskaniu stanu z serwera.
        const r = await fetch(`${API}/api/version`, { cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        // Tylko gdy serwer ma NOWSZA wersje niz wczytany kod -- starszy backend
        // (np. w trakcie wdrozenia) nie jest powodem do odswiezania strony.
        if (!stop && d.version && isNewerVersion(d.version, APP_VERSION)) {
          latestServerVersion.current = d.version;
          offerUpdate(d.version);
        }
      } catch { /* offline albo backend chwilowo niedostepny -- pomijamy */ }
    };
    check();                                  // od razu po wejsciu
    const t = setInterval(check, 2 * 60 * 1000);  // i co 2 minuty (bylo 10 -- user zglosil zbyt wolne wykrywanie)
    // Sprawdź tez, gdy user wraca do karty -- typowy moment po wdrozeniu
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, []);

  // Twarde przeladowanie -- uzywane przez baner aktualizacji.
  // Zwykly location.reload() NIE wystarcza, gdy zacial się Service Worker:
  // nadal serwowalby stary index.html z Cache Storage. Dlatego najpierw
  // wyrejestrowujemy WSZYSTKIE service workery i czyscimy Cache Storage,
  // a dopiero potem przeladowujemy -- to gwarantuje pobranie swiezego kodu
  // z serwera. To programowy odpowiednik "wyczysc dane strony", które
  // użytkownik musial dotad robic ręcznie.
  const hardReload = useCallback(() => {
    updateMemo('update_refreshed', latestServerVersion.current || 'sw');
    forceRefreshApp();
  }, []);
  const dismissUpdate = useCallback(() => {
    updateMemo('update_dismissed', latestServerVersion.current || 'sw');
    setSwUpdateAvailable(false);
  }, []);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Jeśli user akurat przegladal zakladke z grupy "Na zywo" i traci siec,
  // przycisk do powrotu zniknie z nawigacji (patrz NAV_GROUPS) -- bez tego
  // zabezpieczenia user zostalby uwiezony na pustym/bledowym ekranie bez
  // żadnego sposobu na powrot. Przekieruj automatycznie do Loggera.
  const LIVE_ONLY_VIEWS = ['goals','cluster','watchlist','pota','pskreporter','weather','propagation','activation-planner','aprs','satellites','websdr','svxlink','news','contests','dxpeditions','mostwanted','wwff','dxneeded','beacons','chat','market'];
  useEffect(() => {
    if (!isOnline && LIVE_ONLY_VIEWS.includes(view)) setView('logger');
  }, [isOnline, view]);

  // Konto nasluchowca: bez wlasnego wyboru ekranu startowego zaczynamy w nasluchach
  useEffect(() => {
    if (!userProfile?.is_swl || swlStartDone.current) return;
    swlStartDone.current = true;
    let chosen = null;
    try { chosen = localStorage.getItem('start_view'); } catch { /* brak storage */ }
    if (!chosen) setView(v => (v === 'logger' ? 'swl' : v));
  }, [userProfile?.is_swl]);

  // Tryb uproszczony: tylko podstawowe widoki
  const simple = !!userProfile?.simple_mode;
  useEffect(() => {
    if (simple && !SIMPLE_VIEWS.includes(view)) setView(userProfile?.is_swl ? 'swl' : 'logger');
  }, [simple, view, userProfile?.is_swl]);

  // Przejscie do widoku z komponentow bez dostepu do setView (np. panel WSJT-X
  // w widzecie radia -> Diagnostyka agenta).
  useEffect(() => {
    const go = (e) => { if (typeof e.detail === 'string') setView(e.detail); };
    window.addEventListener('hamlogger:navigate', go);
    return () => window.removeEventListener('hamlogger:navigate', go);
  }, []);

  // Obsluga skrotow PWA (long-press na ikonie apki -> "Nowe QSO"/"Logbook").
  // Skroty przekazuja parametr w URL; czytamy go RAZ przy montowaniu i
  // czyscimy z paska adresu (replaceState), zeby odswiezenie strony nie
  // powtarzalo akcji.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const wantView = params.get('view');
    if (action === 'new-qso' || wantView === 'logbook') {
      setView('logger');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (['skeds', 'upload', 'chat', 'market', 'exam', 'alert-rules', 'contests', 'goals', 'portable',
                'weather', 'cluster', 'pskreporter', 'dashboard'].includes(wantView)) {
      setView(wantView);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openNavGroup, setOpenNavGroup] = useState(null);
  const navRef = useRef(null);

  // Zamknij rozwiniete menu nawigacji po kliknieciu gdziekolwiek poza nim
  useEffect(() => {
    if (openNavGroup===null) return;
    const onClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenNavGroup(null);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [openNavGroup]);
  // Zapamietywane w localStorage -- kto regularnie loguje POTA/SOTA/zawody nie
  // musi rozwijac tej sekcji na nowo po kazdym odswiezeniu strony.
  const [showContestFields, setShowContestFieldsState] = useState(() => localStorage.getItem('show_contest_fields') === 'true');
  const setShowContestFields = (updater) => setShowContestFieldsState(prev => {
    const next = typeof updater === 'function' ? updater(prev) : updater;
    localStorage.setItem('show_contest_fields', String(next));
    return next;
  });
  const [showCabrilloModal, setShowCabrilloModal] = useState(false);
  const [showExternalUploadModal, setShowExternalUploadModal] = useState(false);
  const [showLotwModal, setShowLotwModal] = useState(false);
  const [showCwDecoder, setShowCwDecoder] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountTab, setAccountTab] = useState(null);
  useEffect(() => {
    const open = (e) => { setAccountTab(typeof e.detail === 'string' ? e.detail : null); setShowAccountModal(true); };
    window.addEventListener('hamlogger:account', open);
    return () => window.removeEventListener('hamlogger:account', open);
  }, []);
  const [eqslDefaultApplied, setEqslDefaultApplied] = useState(false);
  const [theme, setTheme] = useTheme();
  const [navLayout, setNavLayout] = useNavLayout();
  const [contestMode, setContestMode] = useState(false);
  // Globalny stan odtwarzanej grupy SVXLink -- ZYJE poza komponentem
  // SvxlinkView, zeby element <audio> nie znikal (i nie przerywal
  // odtwarzania) przy przelaczaniu zakladek. Sam element <audio> renderuje
  // się w SvxlinkPlayerWidget (globalny, floating, jak dialer radiowy).
  const [svxlinkActiveGroup, setSvxlinkActiveGroup] = useState(() => {
    try { return JSON.parse(localStorage.getItem('svxlink_active_group')) || null; }
    catch { return null; }
  });

  const [form, setForm] = useState({
    callsign:'',frequency:'',mode:'SSB',rst_sent:'59',rst_recv:'59',
    operator_name:'',qth:'',country:'',grid_locator:'',
    power:'',notes:'',date_time:nowUTC(),dxcc:'',iota:'',
    qsl_sent:false,qsl_recv:false,lat:null,lon:null,
    us_state:'',cq_zone:'',pota_ref:'',sota_ref:'',
    contest_name:'',contest_exchange_sent:'',contest_exchange_recv:'',
    auto_send_eqsl:false, manual_email:'',
    // Znak, POD KTÓRYM pracujesz (okolicznosciowy). Pusty = znak główny konta.
    // Lepki miedzy QSO -- podczas aktywacji pracujesz pod jednym znakiem.
    station_callsign:'',
    tags:'',
    // Sprzet z profilu (StationProfileBar) -- lepki miedzy QSO.
    my_rig:'', my_antenna:'',
  });

  // KLUCZOWE: notify MUSI byc stabilne miedzy renderami (useCallback z pusta
  // lista zaleznosci). Wczesniej bylo zwykla funkcja tworzona OD NOWA przy
  // kazdym renderze -- a jest przekazywane jako prop do komponentow, które maja
  // je w zaleznosciach useCallback/useEffect. Efekt: nowe notify -> nowe load ->
  // useEffect odpala się -> setState -> render -> nowe notify -> ... NIESKONCZONA
  // PETLA (objaw: zakladka "QSL papierowe" migala co ~3s, bo w kolko się
  // przeladowywala). setNotification jest stabilne (setter useState), wiec pusta
  // lista zaleznosci jest poprawna.
  const notify = useCallback((msg, type='success', durationMs=3000) => {
    setNotification({msg,type});
    setTimeout(()=>setNotification(null),durationMs);
  }, []);

  // ── Paleta polecen (Ctrl/Cmd+K) ────────────────────────────────────────────
  // Globalny skrot. Handlery są useCallback-stabilne -- funkcje przekazywane
  // jako propy i uzywane w zaleznosciach hookow musza miec stala referencje,
  // inaczej wpadamy w petle renderowania (zdarzylo się już z `notify`).
  const [showPalette, setShowPalette] = useState(false);
  const closePalette = useCallback(() => setShowPalette(false), []);
  // Jednorazowe powitanie po pierwszym logowaniu -- apka ma kilkadziesiat
  // widokow, latwo przeoczyc np. ze watchlista umie wysylac push, albo ze
  // eQSL/ClubLog/QRZ moga wysylac sie same. Zamiast pelnego "tour" z
  // podswietlaniem konkretnych elementow (kruche przy zmieniajacym sie
  // layoucie/rozdzielczosci), jeden ekran-podsumowanie najwazniejszych rzeczy.
  const [showWelcome, setShowWelcome] = useState(
    () => localStorage.getItem('onboarding_seen') !== '1'
  );
  const dismissWelcome = useCallback(() => {
    localStorage.setItem('onboarding_seen', '1');
    setShowWelcome(false);
  }, []);
  // Samouczek dla poczatkujacych: przy pierwszym logowaniu na KONCIE (flaga na
  // serwerze) albo na zyczenie z ustawien konta (zdarzenie hamlogger:tour).
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    const open = () => setShowTour(true);
    window.addEventListener('hamlogger:tour', open);
    return () => window.removeEventListener('hamlogger:tour', open);
  }, []);
  // Paleta polecen (Ctrl+K) byla WYLACZNIE klawiszowa -- zero widocznego
  // przycisku w interfejsie oznaczalo, ze user bez wczesniejszej wiedzy nie
  // mial jak sie o niej dowiedziec. Teraz jest tez klikalny przycisk w
  // naglowku, plus jednorazowa, dyskretna podpowiedz (pulsujaca kropka),
  // ktora znika na dobre po pierwszym uzyciu (klik ALBO skrot).
  const [paletteHintSeen, setPaletteHintSeen] = useState(
    () => localStorage.getItem('palette_hint_seen') === '1'
  );
  const openPalette = useCallback(() => {
    setShowPalette(v => !v);
    if (!paletteHintSeen) { localStorage.setItem('palette_hint_seen', '1'); setPaletteHintSeen(true); }
  }, [paletteHintSeen]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();          // nie oddawaj skrotu przegladarce
        openPalette();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPalette]);

  // Szybkie zalogowanie QSO prosto z odtwarzacza SVXLink -- przelacza na
  // Logger i dopisuje w notatkach przez ktora grupe uslyszano stacje
  // (sam mode/RST user wpisuje recznie, bo nie znamy go z samej grupy --
  // SVXLink most moze niesc DMR, D-STAR, C4FM czy zwykle FM, zaleznie od
  // konfiguracji administratora).
  // WAZNE: ta deklaracja musi byc PO "const notify" powyzej -- useCallback
  // ocenia tablice zaleznosci [notify] NATYCHMIAST/synchronicznie podczas
  // renderowania (w przeciwienstwie do treci samego callbacku, ktora
  // wykonuje sie pozniej, po kliknieciu) -- gdyby notify nie bylo jeszcze
  // zainicjalizowane w tym miejscu, JS rzucilby "Cannot access before
  // initialization" (co faktycznie sie zdarzylo, zanim to przeniesiono).
  const handleSvxlinkQuickLog = useCallback((group) => {
    setView('logger');
    setForm(f => ({...f, notes: f.notes ? f.notes : tx('Usłyszano przez SVXLink: {0}', [group.name])}));
    notify(tx('📝 Otwarto formularz QSO -- uzupełnij znak i pozostałe dane'));
  }, [notify]);

  // Powiadomienie po automatycznym nadaniu uprawnien administratora przy
  // logowaniu (ekran logowania nie ma jeszcze dostepu do tego systemu
  // powiadomien, wiec pokazujemy to tutaj, już po przejsciu do glownej apki)
  useEffect(() => {
    if (justBecameAdmin && callsign) {
      notify(tx('🛠️ Zostałeś automatycznie ustawiony jako pierwszy administrator systemu! Zakładka "Admin" jest już dostępna w menu.'));
      setJustBecameAdmin(false);
    }
  }, [justBecameAdmin, callsign]);

  // Sortowanie dziennika działa tylko w jego widoku -- ta sama lista zasila
  // "Ostatnie QSO" w loggerze i na pulpicie, które maja byc od najnowszych.
  const logbookSortKey = view==='logbook' && !(logSort.key==='date_time'&&logSort.order==='desc') ? `${logSort.key}|${logSort.order}` : '';
  const logbookSort = React.useMemo(() => logbookSortKey ? {key:logbookSortKey.split('|')[0], order:logbookSortKey.split('|')[1]} : null, [logbookSortKey]);
  const fetchQsos = useCallback(async () => {
    try {
      const sortParams = logbookSort ? {sort:logbookSort.key, order:logbookSort.order} : {};
      const r = await axios.get(`${API}/api/qso`, {params:{page,per_page:50,search:searchQuery,band:filterBand,mode:filterMode,tag:filterTag,...sortParams}});
      setQsos(asArray(r.data.qsos)); setTotalPages(r.data.pages || 1);
    } catch(e) { if (e.response?.status!==401) notify(tx('Błąd pobierania logbook'),'error'); }
  }, [page, searchQuery, filterBand, filterMode, filterTag, logbookSort]);

  // Wolane po KAZDYM zapisie/usunieciu QSO. Dwie poprawki:
  // 1. allSettled zamiast Promise.all -- wczesniej błąd JEDNEGO z zapytan
  //    (np. timeout statystyk przy duzym logu) wyrzucal wynik WSZYSTKICH,
  //    lacznie z /api/auth/me -> profil się nie ustawial, znikal panel admina.
  // 2. Analiza czasowa (time-analysis) NIE jest już pobierana przy kazdym
  //    QSO -- to byla najciezsza operacja (caly log), a jej wynik jest
  //    potrzebny tylko w widoku "Analiza" (patrz efekt ponizej).
  const fetchStats = async () => {
    const [sR, dxR, profR] = await Promise.allSettled([
      axios.get(`${API}/api/stats`),
      axios.get(`${API}/api/stats/dxcc`),
      axios.get(`${API}/api/auth/me`),
    ]);
    if (sR.status === 'fulfilled') setStats(sR.value.data);
    if (dxR.status === 'fulfilled') setDxccList(asArray(dxR.value.data.worked_countries));
    if (profR.status === 'fulfilled') setUserProfile(profR.value.data);
  };

  useEffect(() => {
    if (!callsign || view !== 'analysis') return;
    axios.get(`${API}/api/stats/time-analysis`).then(r => setTimeData(r.data)).catch(() => {});
  }, [callsign, view]);

  // Dziennik -- przy zmianie strony/filtrow/wyszukiwania. Krotki debounce:
  // wpisywanie w wyszukiwarke nie wysyla już zapytania po KAZDYM znaku.
  useEffect(() => {
    if (!callsign) return;
    const t = setTimeout(fetchQsos, 250);
    return () => clearTimeout(t);
  }, [callsign, fetchQsos]);

  // Jednorazowo po zalogowaniu. Wczesniej to bylo w TYM SAMYM efekcie co
  // dziennik (zalezne od fetchQsos), wiec KAZDY znak wpisany w wyszukiwarke
  // dziennika wywolywal tez statystyki i sprawdzanie sesji na serwerach
  // QRZ.com (verify=1 laczy się z QRZ).
  useEffect(()=>{ if(callsign){ fetchStats();
    axios.get(`${API}/api/qrz/status?verify=1`).then(r=>{
      setQrzStatus(r.data);
      // Sesja QRZ wygasla (byla, ale już nie zyje) -- daj znac, ze trzeba się
      // zalogowac ponownie, zeby lookup działał. Bez tego user widzialby tylko
      // "brak danych" i nie wiedzial dlaczego.
      if (r.data.session_expired) notify(tx('Sesja QRZ wygasla -- zaloguj się ponownie, aby wyszukiwac dane operatorow'),'info');
    }).catch(()=>{}); } }, [callsign]);

  // Live-refresh Logbooka -- QSO przychodzace w tle (agent WSJT-X, inna
  // karta/urzadzenie) pojawiaja się same, bez ręcznego odswiezania strony.
  // Ten sam mechanizm (Socket.IO "pokoje") co wspolny dziennik w Community.js,
  // ale prywatny pokoj per-user (patrz join_user_logbook w backendzie).
  // Wolamy fetchQsos/fetchStats zamiast ręcznie doklejac QSO do stanu --
  // gwarantuje to poszanowanie aktywnych filtrow/strony, zamiast pokazac
  // nowe QSO tam gdzie nie pasuje do biezacego widoku.
  // Ref na NAJNOWSZE fetchQsos/fetchStats -- dzieki temu połączenie Socket.IO
  // zależy tylko od zalogowania, a nie od fetchQsos (które zmienia się przy
  // KAZDEJ zmianie strony/filtra/wyszukiwania). Wczesniej kazdy wpisany znak
  // w wyszukiwarce zrywal i zestawial od nowa połączenie z serwerem.
  const liveRefreshRef = useRef(null);
  liveRefreshRef.current = () => { fetchQsos(); fetchStats(); };
  useEffect(() => {
    if (!callsign) return;
    const socket = io(API || window.location.origin, {transports:['websocket','polling']});
    // Dolacz do pokoju przy KAZDYM (ponownym) polaczeniu, z AKTUALNYM tokenem.
    // Wczesniej join szedl raz, z tokenem z chwili montowania -- po zerwaniu
    // połączenia (siec, uspienie laptopa) socket.io laczyl się ponownie, ale
    // już BEZ pokoju, a stary token i tak po 1h byl niewazny -- live-refresh
    // dziennika po cichu przestawal działać.
    const join = () => socket.emit('join_user_logbook', {token: localStorage.getItem('access_token')});
    socket.on('connect', join);
    // Backend emituje 'new_qso' TAKŻE dla QSO zapisanego ręcznie w TEJ karcie
    // (submitQso samo już odswieza dziennik i pokazuje "zapisano") -- wiec:
    // powiadomienie tylko dla QSO z innych źródeł (agent WSJT-X itd.), a
    // odswiezenie zbierane w jedno (1s), zeby seria QSO z agenta nie
    // wywolywala serii pelnych odswiezen dziennika i statystyk.
    // To samo QSO z agenta przychodzi DWA razy (zapis + uzupelnienie danych
    // z QRZ w tle) -- powiadamiamy tylko raz na id.
    let refreshTimer = null;
    const notifiedIds = new Set();
    socket.on('new_qso', (qso) => {
      if (qso.source && qso.source !== 'manual' && !notifiedIds.has(qso.id)) {
        notifiedIds.add(qso.id);
        notify(tx('📻 Nowe QSO: {0} ({1} {2})', [qso.callsign, qso.band||'?', qso.mode||'']));
      }
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => liveRefreshRef.current?.(), 1000);
    });
    return () => {
      clearTimeout(refreshTimer);
      socket.emit('leave_user_logbook', {token: localStorage.getItem('access_token')});
      socket.disconnect();
    };
  }, [callsign, notify]);

  // Zastosuj domyślny stan checkboxa "Wyślij eQSL" z ustawien konta -- TYLKO
  // RAZ po pierwszym zaladowaniu profilu, zeby nie nadpisywac ręcznego
  // wyboru użytkownika przy kazdym kolejnym odswiezeniu profilu (np. po
  // zapisaniu kazdego QSO fetchStats() odswieza userProfile na nowo).
  useEffect(() => {
    if (userProfile && !eqslDefaultApplied) {
      setForm(f => ({...f, auto_send_eqsl: !!userProfile.eqsl_auto_send_default}));
      setEqslDefaultApplied(true);
    }
  }, [userProfile, eqslDefaultApplied]);

  // Zegar "na zywo" dla godziny nowego QSO -- dopoki user jej nie cofnie ręcznie,
  // pole ma zawsze pokazywac obecny czas komputera (nawet jeśli formularz stoi
  // otwarty kilka minut, np. bo laczysc się trwala). 15s wystarcza przy precyzji
  // minutowej pola datetime-local. Wyłączone podczas edycji istniejacego QSO.
  useEffect(() => {
    if (editingQso || !dateTimeAuto || qsoStart) return;
    const tick = () => setForm(f => ({...f, date_time: nowUTC()}));
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [editingQso, dateTimeAuto, qsoStart]);

  // Czyscimy zaznaczenie masowe przy zmianie strony/filtrow -- inaczej
  // zaznaczenie "przeciekaloby" miedzy widokami bez wizualnego potwierdzenia
  useEffect(() => { setSelectedQsoIds(new Set()); }, [page, filterBand, filterMode, searchQuery]);

  // Sprawdzanie duplikatow na zywo (ostrzegawcze, nie blokujace) -- czy już
  // mamy QSO z tym znakiem na tym pasmie/modzie tego samego dnia. Debounce
  // 600ms, zeby nie odpytywac przy kazdym wpisanym znaku.
  useEffect(() => {
    if (editingQso) { setDuplicateWarning(null); return; } // przy edycji znalazloby samo siebie
    if (!form.callsign || form.callsign.length < 3 || !form.date_time) { setDuplicateWarning(null); return; }
    const t = setTimeout(async () => {
      try {
        const dateOnly = form.date_time.split('T')[0];
        const r = await axios.get(`${API}/api/qso/check-duplicate`, {
          params: { callsign: form.callsign, frequency: form.frequency, mode: form.mode, date: dateOnly }
        });
        setDuplicateWarning(r.data.duplicates?.length > 0 ? r.data.duplicates : null);
      } catch { /* ciche niepowodzenie -- to tylko podgląd, nie krytyczne */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form.callsign, form.frequency, form.mode, form.date_time, editingQso]);

  // B4 (worked before): pobierz historie znaku z calego logu. Debounce 600ms.
  // Osobno od duplicateWarning (ten patrzy tylko na dziś) -- tu chodzi o "czy
  // kiedykolwiek go pracowalem i na czym".
  useEffect(() => {
    const call = (form.callsign || '').trim().toUpperCase();
    if (editingQso || call.length < 3) { setWorkedBefore(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/qso/worked-before/${call}`);
        setWorkedBefore(r.data.worked ? r.data : null);
      } catch { /* ciche niepowodzenie -- to tylko podpowiedz */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form.callsign, editingQso]);

  // Namiar/odległość i wskaznik "nowy kraj/pasmo/mode" są wypelniane przez
  // lookupCallsign (po znalezieniu danych w QRZ) -- ale jeśli user zmieni
  // znak BEZ ponownego wyszukania (albo wroci do edycji innego QSO), te
  // dane staja się nieaktualne dla aktualnie wpisanego znaku. Czyscimy je
  // wtedy, zeby nie pokazywac informacji o kims innym.
  useEffect(() => {
    setBearingInfo(null);
    setNewAchievement(null);
  }, [form.callsign]);

  // Kolejka offline: sprawdź ile QSO czeka na wysłanie -- ale NIE wysylaj
  // automatycznie. Zamiast tego pokaż baner z prosba o potwierdzenie. To
  // ważne dla osób pracujacych w terenie (np. POTA/SOTA bez zasiegu) --
  // moga chciec najpierw przejrzec/poprawic dane offline, albo po prostu
  // nie chca zuzywac transferu na slabym/kosztownym sygnale bez pytania.
  const checkOfflineQueue = useCallback(async () => {
    let queued;
    try { queued = await getQueuedQsos(); } catch { return; }
    const count = queued?.length || 0;
    setOfflineQueueCount(count);
    if (count > 0) setShowSyncPrompt(true);
  }, []);

  // Faktyczna wysyłka -- wywolywana TYLKO po potwierdzeniu przez usera
  // (przycisk w bannerze), nigdy automatycznie.
  const sendOfflineQueue = useCallback(async () => {
    setShowSyncPrompt(false);
    let queued;
    try { queued = await getQueuedQsos(); } catch { return; }
    if (!queued || queued.length === 0) { setOfflineQueueCount(0); return; }
    let sent = 0;
    let lastError = null;
    let failedDueToOffline = false;
    for (const item of queued) {
      const { localId, queuedAt, ...qsoPayload } = item;
      try {
        await axios.post(`${API}/api/qso`, qsoPayload);
        await removeQueuedQso(localId);
        sent++;
      } catch (err) {
        // WAŻNE: rozrozniamy DWA rozne przypadki, które poprzednio byly
        // milczone identycznie -- (1) faktyczny brak sieci (err.response
        // nie istnieje -- request nie dotarl do serwera wcale) -- to
        // oczekiwane, zostawiamy w kolejce bez alarmowania; (2) serwer
        // ODPOWIEDZIAL, ale odmownie (np. 400 -- niezgodnosc formatu
        // danych zapisanych offline z aktualna walidacja backendu, po
        // ktorejs z wielu zmian backendu) -- TO byl bug: QSO zostawalo w
        // kolejce NA ZAWSZE, bez ŻADNEGO komunikatu dla użytkownika o
        // przyczynie ("nie idzie wysłać", bez wskazania dlaczego).
        if (err.response) {
          lastError = err.response?.data?.error || tx('Błąd {0}', [err.response.status]);
          try { await updateQueuedQsoError(localId, lastError); } catch {}
        } else {
          failedDueToOffline = true;
        }
      }
    }
    if (sent > 0) { notify(tx('📡 Wyslano {0} QSO z kolejki offline', [sent])); fetchQsos(); fetchStats(); }
    if (lastError) {
      notify(tx('⚠️ Część QSO z kolejki offline NIE wysłała się (serwer odrzucił): {0}. Kliknij ikonę 📴 w nagłówku, żeby zobaczyć szczegóły i usunąć/poprawić.', [lastError]), 'error', 10000);
    }
    try {
      const remaining = (await getQueuedQsos()).length;
      setOfflineQueueCount(remaining);
      if (remaining > 0 && failedDueToOffline) setShowSyncPrompt(true); // wciaz offline -- pytaj dalej przy kolejnej okazji
    } catch {}
  }, [fetchQsos]);

  useEffect(() => {
    if (!callsign) return;
    checkOfflineQueue();
    window.addEventListener('online', checkOfflineQueue);
    window.addEventListener('hamlogger:offline-queued', checkOfflineQueue);
    return () => {
      window.removeEventListener('online', checkOfflineQueue);
      window.removeEventListener('hamlogger:offline-queued', checkOfflineQueue);
    };
  }, [callsign, checkOfflineQueue]);

  // "Co teraz robic?" (pulpit) -> spot do formularza loggera
  useEffect(() => {
    const onSpot = (e) => {
      const spot = e.detail || {};
      if (!spot.dx) return;
      setForm(f=>({...f,callsign:spot.dx,frequency:spot.freq||f.frequency,
        mode:(spot.mode||f.mode).toUpperCase(),rst_sent:getRst((spot.mode||f.mode).toUpperCase()),
        rst_recv:getRst((spot.mode||f.mode).toUpperCase()),date_time:nowUTC()}));
      setView('logger');
      notify(tx('Zaladowano spot: {0} @ {1} MHz', [spot.dx, spot.freq]));
    };
    window.addEventListener('hamlogger:load-spot', onSpot);
    return () => window.removeEventListener('hamlogger:load-spot', onSpot);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Klikniecie powiadomienia push przy otwartej karcie -- service worker
  // przysyla adres (/?view=...), przechodzimy do wskazanego widoku.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return undefined;
    const onMsg = (e) => {
      if (e.data?.type !== 'open-url') return;
      try {
        const v = new URL(e.data.url, window.location.origin).searchParams.get('view');
        if (v && /^[a-z0-9-]+$/.test(v)) setView(v);
      } catch { /* zly adres */ }
    };
    sw.addEventListener('message', onMsg);
    return () => sw.removeEventListener('message', onMsg);
  }, []);

  // Widoczny w naglowku wskaznik offline/online -- zeby user zawsze wiedzial
  // w jakim trybie aktualnie działa aplikacja (waznejszsze niz zwykla apka
  // webowa, bo HamLogger ma działać w terenie bez zasiegu).
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  // CAT control: odpytuj backend o ostatnia pozycje radia (push'owana przez
  // lokalny agent cat_agent/rigctl_agent.py) -- tylko gdy zakladka loggera jest aktywna
  useEffect(() => {
    if (!callsign || view !== 'logger') return;
    const poll = () => axios.get(`${API}/api/cat/status`).then(r=>setCatStatus(r.data)).catch(()=>{});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [callsign, view]);

  // Automatycznie przelicz lokator na lat/lon, gdy brakuje wspolrzednych
  // (działa zarowno przy recznym wpisywaniu, jak i przy edycji istniejacego QSO,
  // które ma zapisany lokator ale nie ma lat/lon w bazie)
  useEffect(() => {
    if (form.grid_locator && form.grid_locator.length >= 4 && form.lat == null) {
      const pos = gridToLatLon(form.grid_locator);
      if (pos) setForm(f => ({ ...f, lat: pos.lat, lon: pos.lon }));
    }
  }, [form.grid_locator, form.lat]);

  // "Śledź radio": pole częstotliwości (i emisji) w loggerze podąża za radiem --
  // przy każdej ZMIANIE odczytu z radia (pokrętło, pasmo, tryb), a nie co odpytanie,
  // więc ręcznie wpisana wartość zostaje, dopóki radio się nie przestroi.
  // Emisja tylko gdy radio zmieni tryb (FT4 wybrane ręcznie nie przeskoczy na FT8).
  // Nie przy edycji zapisanego QSO.
  const [followRadio, setFollowRadio] = useState(() => { try { return localStorage.getItem('follow_radio') !== '0'; } catch { return true; } });
  const toggleFollowRadio = () => setFollowRadio(v => { try { localStorage.setItem('follow_radio', v ? '0' : '1'); } catch { /* brak storage */ } return !v; });
  const lastRigRead = useRef({ freq: null, mode: null });
  useEffect(() => {
    const freq = catStatus.connected && typeof catStatus.frequency === 'number' ? catStatus.frequency.toFixed(6) : null;
    const mode = catStatus.connected ? catStatus.mode || null : null;
    const prev = lastRigRead.current;
    lastRigRead.current = { freq, mode };
    if (!followRadio || editingQso || !freq) return;
    const freqChanged = freq !== prev.freq, modeChanged = mode && mode !== prev.mode;
    if (!freqChanged && !modeChanged) return;
    setForm(f => ({ ...f,
      ...(freqChanged ? { frequency: freq } : {}),
      ...(modeChanged ? { mode, rst_sent: getRst(mode), rst_recv: getRst(mode) } : {}),
    }));
  }, [catStatus.connected, catStatus.frequency, catStatus.mode, followRadio, editingQso]);

  // Moc ustawiona w radiu -> pole "Moc (W)" (gdy radio ja podaje i sledzenie jest wlaczone)
  const lastRigPower = useRef(null);
  useEffect(() => {
    const p = catStatus.connected && catStatus.power != null ? catStatus.power : null;
    const prev = lastRigPower.current;
    lastRigPower.current = p;
    if (!followRadio || editingQso || p == null || p === prev) return;
    setForm(f => ({ ...f, power: String(p) }));
  }, [catStatus.connected, catStatus.power, followRadio, editingQso]);

  // Strojenie z pola czestotliwosci: strzalki / kolko myszy (Shift = x10), PageUp/PageDown = pasmo.
  // Z podlaczonym radiem przestraja radio (bez komunikatow, split zostaje), bez radia zmienia samo pole.
  const formRef = useRef(form);
  formRef.current = form;
  const freqInputRef = useRef(null);
  const tuneTimer = useRef(null);
  const tuneTo = (mhz, mode) => {
    setForm(f => ({ ...f, frequency: mhz.toFixed(6) }));
    if (!catStatus.connected) return;
    clearTimeout(tuneTimer.current);
    const web = catStatus.source === 'webserial';
    tuneTimer.current = setTimeout(() => {
      if (web) rigQsy(mhz, mode, { keepSplit: !mode }).catch(() => {});
      else axios.post(`${API}/api/cat/command`, { frequency: mhz, mode }).catch(() => {});
    }, web ? 60 : 400);
  };
  const tuneStep = (dir, big) => {
    const f = formRef.current;
    const base = parseFreq(f.frequency) || catStatus.frequency;
    if (!base) return;
    tuneTo(stepFreq(base, f.mode, dir, big));
  };
  const tuneBand = (dir) => {
    const f = formRef.current;
    const base = parseFreq(f.frequency) || catStatus.frequency || 14.2;
    const nb = neighbourBand(base, f.mode, dir);
    tuneTo(nb.freq, f.mode);
  };
  const onFreqKeyDown = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); tuneStep(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey); }
    else if (e.key === 'PageUp' || e.key === 'PageDown') { e.preventDefault(); tuneBand(e.key === 'PageUp' ? 1 : -1); }
  };
  useFreqWheel(freqInputRef, tuneStep);

  // Stacja ze spotu klastra na czestotliwosci radia -- podpowiedz znaku (klik albo strzalka w dol w pustym polu)
  const spotAtRadio = useSpotAtRadio(catStatus.frequency, catStatus.mode, catStatus.connected && view === 'logger' && followRadio && !editingQso);
  const spotHint = spotAtRadio && spotAtRadio.dx && spotAtRadio.dx !== (form.callsign || '').trim() ? spotAtRadio : null;
  const applySpotHint = () => {
    if (!spotHint) return;
    setForm(f => ({ ...f, callsign: spotHint.dx }));
    lookupCallsign(spotHint.dx);
  };

  // Ostrzezenie o pasmie / uprawnieniach dla czestotliwosci RADIA (widoczne w naglowku przy kreceniu galka)
  const rigWarnings = catStatus.connected && catStatus.frequency
    ? (!isFreqInHamBand(String(catStatus.frequency))
      ? [tx('Radio poza pasmami amatorskimi')]
      : bandPlanWarnings(catStatus.frequency, catStatus.mode, null, userProfile).map(w => w.text))
    : [];

  // Czas QSO: poczatek w chwili wpisania znaku (pole daty przestaje wtedy "tykac"),
  // koniec (time_off) w chwili zapisu. Tylko przy automatycznym czasie i nowym QSO.
  useEffect(() => {
    if (editingQso || !dateTimeAuto) { if (qsoStart) setQsoStart(null); return; }
    const has = (form.callsign || '').trim().length >= 3;
    if (has && !qsoStart) { const now = nowUTC(); setQsoStart(now); setForm(f => ({ ...f, date_time: now })); }
    else if (!has && qsoStart) setQsoStart(null);
  }, [form.callsign, editingQso, dateTimeAuto]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dane QSO do zapisu: time_off + przy splicie FREQ = nadawanie, FREQ_RX = odbior (jak w ADIF)
  const buildQsoPayload = () => {
    const p = { ...form };
    if (editingQso) return p;
    if (dateTimeAuto && qsoStart) {
      // znak wpisany dawno temu (ponad 2 h) to nie poczatek tej lacznosci
      if (Date.now() - new Date(qsoStart + 'Z').getTime() > 2 * 3600 * 1000) p.date_time = nowUTC();
      else p.time_off = new Date().toISOString().slice(0, 19);
    }
    const rx = parseFreq(form.frequency);
    if (catStatus.splitTx && rx && Math.abs(catStatus.splitTx - rx) < 0.1 && Math.abs(catStatus.splitTx - rx) > 0.00001) {
      p.frequency = catStatus.splitTx.toFixed(6);
      p.freq_rx = rx;
    }
    return p;
  };

  const fillFromRadio = () => {
    if (!catStatus.connected) return;
    setForm(f=>({...f,
      frequency: catStatus.frequency?.toFixed(6) || f.frequency,
      mode: catStatus.mode || f.mode,
      rst_sent: getRst(catStatus.mode||f.mode), rst_recv: getRst(catStatus.mode||f.mode),
    }));
    notify(tx('Wypelniono z radia: {0} MHz / {1}', [catStatus.frequency?.toFixed(3), catStatus.mode]));
  };

  // Auto-lookup QRZ podczas pisania -- po 2s bez zmian wywoluje ten sam
  // lookupCallsign co przycisk 🔍 QRZ (nie duplikujemy logiki: grid, namiar,
  // odległość, WAS/WAZ licza się tak samo). Działa tylko gdy user zalogowany
  // do QRZ i znak ma sens (>=3 znaki). Bez klikania.
  const autoLookupTimerRef = useRef(null);
  // Ref do pola znaku -- po zapisie QSO kursor wraca tu automatycznie, zeby
  // od razu wpisac kolejny znak (rytm logowania, jak w trybie zawodow).
  const mainCallsignRef = useRef(null);
  const autoLookupTimerRef2 = useRef(null);
  const autoLookupCallsign = (cs) => {
    if (autoLookupTimerRef.current) clearTimeout(autoLookupTimerRef.current);
    const call = (cs||'').trim();
    // Uzupelnienie kraju/strefy CQ z lokalnej bazy cty.dat -- działa ZAWSZE,
    // niezaleznie od tego czy user ma zalogowana sesje QRZ (w przeciwienstwie
    // do lookupCallsign ponizej, który wymaga QRZ). To jedyne źródło strefy
    // CQ/ITU/kraju dla kogos bez (platnego) dostepu do QRZ XML API.
    if (autoLookupTimerRef2.current) clearTimeout(autoLookupTimerRef2.current);
    if (call.length >= 3) {
      autoLookupTimerRef2.current = setTimeout(() => autoFillFromCty(call), 600);
    }
    if (!(qrzStatus.logged_in || qrzStatus.hamqth) || call.length < 3) return;
    autoLookupTimerRef.current = setTimeout(() => {
      // Wywolaj tylko jeśli pole nadal ma ten sam znak (user nie zdazyl zmienic)
      lookupCallsign(call);
    }, 2000);
  };

  // Lekki, zawsze-dostepny lookup kraju/strefy CQ z lokalnej bazy cty.dat
  // (patrz backend/ctydat.py) -- uzupelnia TYLKO puste pola, zeby nie
  // nadpisac bogatszych danych z QRZ, jeśli ten już zdazyl odpowiedziec.
  const autoFillFromCty = async (cs) => {
    try {
      const r = await axios.get(`${API}/api/dxcc/lookup/${cs}`);
      if (r.data.found) {
        setForm(f => (f.callsign === cs ? {
          ...f,
          country: f.country || r.data.name,
          cq_zone: f.cq_zone || String(r.data.cq_zone),
        } : f));
      }
    } catch { /* cicho -- to tylko wygoda, brak wpisu w cty.dat nie jest bledem */ }
  };

  const lookupCallsign = async (cs) => {
    if (!cs||cs.length<3||!(qrzStatus.logged_in||qrzStatus.hamqth)) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/qrz/lookup/${cs}`);
      if (r.data.success) {
        const d = r.data.data;
        let lat = d.lat ? parseFloat(d.lat) : null;
        let lon = d.lon ? parseFloat(d.lon) : null;
        const qthValue = [d.addr2,d.state].filter(Boolean).join(', ');
        // Priorytet: 1) grid z QRZ, 2) wyliczony ze wspolrzednych, 3) zwyczajnie skopiowany z QTH
        const grid = d.grid || (lat!=null && lon!=null ? latLonToGrid(lat, lon, 4) : '') || qthValue;
        // Jeśli mamy grid (np. wykryty w tekscie adresu -- geo_source
        // "addr_locator"), ale backend NIE dal lat/lon (bo to nie ten sam
        // mechanizm co geokodowanie), wylicz wspolrzedne z samego grida --
        // potrzebne do mapki/odległości, która inaczej zostalaby pusta.
        if (lat==null && lon==null && grid) {
          const pos = gridToLatLon(grid);
          if (pos) { lat = pos.lat; lon = pos.lon; }
        }
        // WAS/WAZ: QRZ podaje 'state' (tylko USA) i 'cqzone' bezposrednio w danych
        const usState = (d.country==='United States' && d.state) ? d.state.toUpperCase().slice(0,2) : '';
        setForm(f=>({...f,
          operator_name:[d.fname,d.name].filter(Boolean).join(' ')||f.operator_name,
          country:d.country||f.country, qth:qthValue||f.qth,
          grid_locator: grid || f.grid_locator, dxcc:d.dxcc||f.dxcc,
          lat: lat!=null?lat:f.lat, lon: lon!=null?lon:f.lon,
          us_state: usState||f.us_state, cq_zone: d.cqzone||f.cq_zone,
        }));

        // Ostrzeżenie o Silent Key -- HEURYSTYKA (patrz komentarz w
        // backend/app.py przy lookup_qrz), nie pewnik. Nie blokuje logowania,
        // tylko zwraca uwage, zeby user sam ocenil czy to nie pomylka/duplikat
        // konta rodziny zmarlego kolegi.
        if (d.is_silent_key) {
          notify(tx('⚠️ {0} -- profil QRZ sugeruje, że to może być Silent Key (zmarły krótkofalowiec). Sprawdź, zanim zalogujesz.', [d.call]), 'warning', 8000);
        }

        // Namiar i odległość -- tylko gdy mamy WŁASNA lokalizację domowa
        // (profil) ORAZ wspolrzedne drugiej stacji (z QRZ/grida/geokodowania
        // powyzej). Wyliczane lokalnie w JS, bez dodatkowego zapytania do
        // backendu -- to czysta matematyka na już posiadanych danych.
        if (lat!=null && lon!=null && userProfile?.home_lat && userProfile?.home_lon) {
          const distanceKm = haversineKm(userProfile.home_lat, userProfile.home_lon, lat, lon);
          const brg = bearingDeg(userProfile.home_lat, userProfile.home_lon, lat, lon);
          setBearingInfo({distanceKm, bearingDeg: brg, compass: bearingToCompass(brg)});
        } else {
          setBearingInfo(null);
        }

        // Wskaznik "nowy kraj/pasmo/mode" -- klasyka znana z N1MM/Log4OM.
        // "Nowe pasmo"/"nowy mode" są liczone WZGLEDEM TEGO KRAJU (czyli
        // np. "pierwsze QSO z Japonia na 80m"), nie globalnie. Band nie
        // jest osobnym polem formularza -- wyliczamy go z frequency tak
        // jak zrobilby to backend przy zapisie.
        if (d.dxcc) {
          const freqNum = parseFreq(form.frequency);
          const currentBand = !isNaN(freqNum) ? freqToBandJs(freqNum) : '';
          axios.get(`${API}/api/qso/check-new`, {params: {dxcc: d.dxcc, band: currentBand, mode: form.mode}})
            .then(r => setNewAchievement(r.data))
            .catch(() => setNewAchievement(null));
        } else {
          setNewAchievement(null);
        }
        if (d.geo_source === 'addr_locator') {
          // Operator wpisal swoj prawdziwy lokator w pole adresu/QTH na QRZ
          // (zamiast w dedykowane pole Grid Square) -- wykrylismy to i
          // uzylismy go bezposrednio, NIE geokodujac tekstu jak adresu
          // (co dalo by bledna lokalizację).
          notify(tx('QRZ: {0} — wykryto lokator wpisany w polu adresu ({1}), uzyto bezposrednio', [d.call, grid]));
        } else if (d.geo_source === 'geocoded') {
          // Backend zgeokodowal adres tekstowy (Nominatim/OSM), bo QRZ nie dal
          // ani grida ani lat/lon (typowo brak platnej subskrypcji XML Logbook Data)
          notify(tx('QRZ: {0} — {1} (lokalizacja orientacyjna, zgeokodowana z adresu -- QRZ nie dal wspolrzednych)', [d.call, d.country]), 'warning');
        } else if (!d.grid && lat==null) {
          // Nawet geokodowanie zawiodlo (np. zbyt ogolny/nietypowy adres) --
          // prawdziwy ostatni fallback: kopia tekstu QTH do pola lokator
          notify(tx('QRZ: {0} — dane podstawowe OK, ale brak lokatora/koordynatow nawet po geokodowaniu -- wpisano QTH jako tymczasowy lokator, popraw recznie jesli znasz prawdziwy', [d.call]), 'warning');
        } else {
          notify(`QRZ: ${d.call} — ${d.country}`);
        }
      } else {
        if (r.data.session_expired) {
          setQrzStatus({logged_in:false});
          notify(tx('Sesja QRZ wygasla -- zaloguj się ponownie (przycisk 🔑 QRZ)'), 'warning');
        } else {
          notify(r.data.error||tx('Brak danych QRZ'),'warning');
        }
      }
    } catch { notify(tx('Błąd QRZ'),'error'); }
    setLoading(false);
  };

  // Krotki beep po zapisie QSO -- w pile-upie slyszysz potwierdzenie bez
  // patrzenia na ekran. Web Audio (jak dekoder CW). Cichy, ~120ms. Bezpieczny:
  // błąd audio (np. brak zgody przeglądarki) nie przerywa niczego.
  const qsoBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.frequency.value = 880; osc.type = 'sine';
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.12);
      osc.onended = () => ctx.close();
    } catch { /* audio niedostepne -- trudno, to tylko potwierdzenie */ }
  };

  // Reset licznika/nazwy parku gdy referencja POTA się zmienia (ręcznie
  // wpisana nowa albo wyczyszczona) -- inaczej licznik z POPRZEDNIEGO parku
  // myloby się z nowa aktywacja.
  useEffect(() => { setPotaActivationCount(0); setPotaParkName(''); }, [form.pota_ref]);

  const lookupPotaPark = async () => {
    if (!form.pota_ref.trim()) return;
    setPotaLookupBusy(true);
    try {
      const r = await axios.get(`${API}/api/pota/park/${encodeURIComponent(form.pota_ref.trim())}`);
      setPotaParkName(r.data.name || '');
    } catch (err) {
      notify(err.response?.data?.error || tx('Nie znaleziono parku o tej referencji'), 'error');
    }
    setPotaLookupBusy(false);
  };

  const submitQso = async (e) => {
    e.preventDefault();
    if (!form.callsign||!form.frequency||!form.mode){ notify(tx('Wymagane: znak, częstotliwość, emisja'),'error'); return; }
    setLoading(true);
    try {
      if (editingQso) {
        await axios.put(`${API}/api/qso/${editingQso.id}`, form);
        notify(tx('QSO zaktualizowane')); setEditingQso(null);
      } else {
        await axios.post(`${API}/api/qso`, buildQsoPayload());
        setSessionCount(n=>n+1);
        if (form.pota_ref) setPotaActivationCount(n=>n+1);
        notify(t('notify.qsoSaved')); qsoBeep();
      }
      setDateTimeAuto(true);
      setForm(f=>({...f,callsign:'',operator_name:'',country:'',qth:'',grid_locator:'',
        notes:'',tags:'',rst_sent:getRst(f.mode),rst_recv:getRst(f.mode),
        date_time:nowUTC(),dxcc:'',iota:'',lat:null,lon:null,
        us_state:'',cq_zone:'',contest_exchange_recv:'',
        auto_send_eqsl: !!userProfile?.eqsl_auto_send_default}));
        // Uwaga: pota_ref/sota_ref/contest_name/contest_exchange_sent NIE są czyszczone --
        // typowo aktywujesz ten sam park/szczyt albo jeste w tych samych zawodach
        // przez cala sesje, wiec wygodniej jest zachowac te wartosci miedzy QSO.
        // auto_send_eqsl WRACA do domyslnej wartosci z konta przy kazdym nowym QSO
        // (w przeciwienstwie do pol zawodow) -- to bardziej decyzja per-kontakt
        // (np. "ten konkretny znak nie ma maila w QRZ") niz cecha calej sesji.
      setDuplicateWarning(null);
      setWorkedBefore(null);
      // Po zapisie nowego QSO wroc kursorem do pola znaku (rytm logowania).
      // Nie robimy tego przy edycji -- tam user zwykle konczy, nie loguje serii.
      if (!editingQso) setTimeout(() => mainCallsignRef.current?.focus(), 0);
      fetchQsos(); fetchStats();
    } catch(err) {
      // Brak połączenia (a nie błąd walidacji serwera) -> zapisz offline w IndexedDB
      // i wyślij automatycznie po powrocie sieci. Dotyczy tylko NOWYCH QSO --
      // edycja offline jest poza zakresem (zbyt duze ryzyko konfliktu wersji).
      const isNetworkError = !err.response;
      if (isNetworkError && !editingQso) {
        try {
          await queueOfflineQso(buildQsoPayload());
          setOfflineQueueCount(c=>c+1);
          notify(tx('📴 Brak połączenia -- QSO zapisane offline, wysle się automatycznie'), 'warning');
          setDateTimeAuto(true);
          setForm(f=>({...f,callsign:'',operator_name:'',country:'',qth:'',grid_locator:'',
            notes:'',rst_sent:getRst(f.mode),rst_recv:getRst(f.mode),
            date_time:nowUTC(),dxcc:'',iota:'',lat:null,lon:null}));
          setDuplicateWarning(null);
          setTimeout(() => mainCallsignRef.current?.focus(), 0);
        } catch {
          notify(tx('Błąd zapisu offline (IndexedDB niedostepne w tej przeglądarce)'),'error');
        }
      } else {
        notify(err.response?.data?.error||tx('Błąd zapisu'),'error');
      }
    }
    setLoading(false);
  };

  const deleteQso = async (id) => {
    if (!window.confirm(tx('Usunac to QSO?'))) return;
    await axios.delete(`${API}/api/qso/${id}`);
    notify(tx('QSO usunięte')); fetchQsos(); fetchStats();
  };

  const sendEqslManual = async (qso) => {
    try {
      const r = await axios.post(`${API}/api/eqsl/send/${qso.id}`);
      notify(r.data.message || tx('eQSL wysłane'));
    } catch(err) { notify(err.response?.data?.error||tx('Błąd wysyłki eQSL'),'error'); }
  };

  // ── Edycja masowa (wybór wielu QSO + zmiana pola/usuniecie na raz) ──────────
  const toggleSelectQso = (id) => {
    setSelectedQsoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllQsos = () => {
    setSelectedQsoIds(prev => prev.size === qsos.length ? new Set() : new Set(qsos.map(q=>q.id)));
  };
  const clearSelection = () => setSelectedQsoIds(new Set());

  const applyBulkUpdate = async (updates) => {
    if (selectedQsoIds.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await axios.put(`${API}/api/qso/bulk-update`, {ids:[...selectedQsoIds], updates});
      notify(tx('Zaktualizowano {0} QSO', [r.data.updated]));
      clearSelection(); fetchQsos(); fetchStats();
    } catch(err) { notify(err.response?.data?.error||tx('Błąd edycji masowej'),'error'); }
    setBulkBusy(false);
  };

  const applyBulkDelete = async () => {
    if (selectedQsoIds.size === 0) return;
    if (!window.confirm(tx('Usunac {0} zaznaczonych QSO? Tej operacji nie da sie odwrocic.', [selectedQsoIds.size]))) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/api/qso/bulk-delete`, {ids:[...selectedQsoIds]});
      notify(tx('Usunieto {0} QSO', [r.data.deleted]));
      clearSelection(); fetchQsos(); fetchStats();
    } catch(err) { notify(err.response?.data?.error||tx('Błąd usuwania masowego'),'error'); }
    setBulkBusy(false);
  };

  // window.open() nie wysyla naglowka Authorization (JWT), wiec do pobierania
  // chronionych endpointow (eksporty) uzywamy axios + blob + tymczasowy link <a>.
  const loadRoutePropagation = async () => {
    // Cel: wspolrzedne z formularza (z QRZ/geokodowania) albo lokator.
    const params = {};
    if (form.lat != null && form.lon != null) { params.lat = form.lat; params.lon = form.lon; }
    else if (form.grid_locator) { params.grid = form.grid_locator; }
    else { notify(tx('Brak wspolrzednych/lokatora stacji do oceny trasy'),'error'); return; }
    try {
      const r = await axios.get(`${API}/api/propagation/route`, {params});
      setRouteProp(r.data);
    } catch(err) { notify(err.response?.data?.error||tx('Błąd oceny propagacji'),'error'); }
  };

  const downloadWithAuth = async (url, fallbackFilename) => {
    try {
      const r = await axios.get(url, {responseType:'blob'});
      const disposition = r.headers['content-disposition'];
      let filename = fallbackFilename;
      const match = disposition && disposition.match(/filename=([^;]+)/);
      if (match) filename = match[1].trim();
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch { notify(tx('Błąd pobierania pliku'),'error'); }
  };

  const handleAdifImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/import/adif`, formData, {headers:{'Content-Type':'multipart/form-data'}});
      // Rozdzielamy duplikaty od błędów -- user od razu wie, czy plik byl zly,
      // czy po prostu importuje cos, co już ma w logu.
      const parts = [`Zaimportowano ${r.data.imported} QSO`];
      if (r.data.duplicates) parts.push(tx('{0} duplikatów pominięto (już masz je w logu)', [r.data.duplicates]));
      if (r.data.invalid) parts.push(tx('{0} błędnych wpisów odrzucono', [r.data.invalid]));
      notify(parts.join(' · '), r.data.invalid ? 'error' : 'success', 6000);
      fetchQsos(); fetchStats();
      // Rekordy bez kraju (typowe dla eksportow WSJT-X) są uzupelniane danymi
      // z QRZ W TLE po stronie serwera -- nie od razu. Info + odswiezenie po
      // chwili, zeby user zobaczyl uzupełnione dane bez ręcznego F5.
      if (r.data.qrz_enrichment_queued > 0) {
        notify(tx('Uzupełniam dane (kraj, DXCC...) dla {0} QSO z QRZ.com w tle...', [r.data.qrz_enrichment_queued]), 'info', 6000);
        setTimeout(() => { fetchQsos(); fetchStats(); }, 15000);
      }
    } catch { notify(tx('Błąd importu ADIF'),'error'); }
    setLoading(false);
    e.target.value = '';
  };

  const handleCabrilloImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/import/cabrillo`, formData, {headers:{'Content-Type':'multipart/form-data'}});
      const parts = [`Zaimportowano ${r.data.imported} QSO`];
      if (r.data.contest_name) parts.push(`zawody: ${r.data.contest_name}`);
      if (r.data.duplicates) parts.push(tx('{0} duplikatów pominięto', [r.data.duplicates]));
      if (r.data.invalid) parts.push(tx('{0} błędnych wpisów odrzucono', [r.data.invalid]));
      notify(parts.join(' · '), r.data.invalid ? 'error' : 'success', 6000);
      fetchQsos(); fetchStats();
    } catch { notify(tx('Błąd importu Cabrillo'),'error'); }
    setLoading(false);
    e.target.value = '';
  };

  // Reczne uzupelnienie danych z QRZ.com (ten sam mechanizm co przy imporcie
  // ADIF) -- ids=null uzupelnia WSZYSTKIE własne QSO bez kraju, ids=[...]
  // tylko wybrane (przycisk w bulk-toolbarze po zaznaczeniu).
  const enrichMissingQrzData = async (ids) => {
    try {
      const r = await axios.post(`${API}/api/qso/enrich-qrz`, ids ? {ids} : {});
      if (r.data.queued === 0) {
        notify(ids ? tx('Zaznaczone QSO nie potrzebują uzupełnienia') : tx('Wszystkie QSO mają już uzupełniony kraj'), 'info');
      } else {
        notify(tx('Uzupełniam dane dla {0} QSO z QRZ.com w tle...', [r.data.queued]), 'info', 6000);
        if (ids) clearSelection();
        setTimeout(() => { fetchQsos(); fetchStats(); }, 15000);
      }
    } catch (err) { notify(err.response?.data?.error || tx('Błąd uzupelniania danych z QRZ'), 'error'); }
  };

  const startEdit = (qso) => {
    setEditingQso(qso);
    setForm({callsign:qso.callsign,frequency:qso.frequency,mode:qso.mode,
      rst_sent:qso.rst_sent||'59',rst_recv:qso.rst_recv||'59',
      operator_name:qso.operator_name||'',qth:qso.qth||'',country:qso.country||'',
      grid_locator:qso.grid_locator||'',power:qso.power||'',notes:qso.notes||'',
      date_time:qso.date_time?qso.date_time.slice(0,16):nowUTC(),
      dxcc:qso.dxcc||'',iota:qso.iota||'',qsl_sent:qso.qsl_sent,qsl_recv:qso.qsl_recv,
      lat:qso.lat,lon:qso.lon,
      us_state:qso.us_state||'',cq_zone:qso.cq_zone||'',
      pota_ref:qso.pota_ref||'',sota_ref:qso.sota_ref||'',
      contest_name:qso.contest_name||'',contest_exchange_sent:qso.contest_exchange_sent||'',
      contest_exchange_recv:qso.contest_exchange_recv||'',auto_send_eqsl:false,
      manual_email:qso.manual_email||'',
      my_rig:qso.my_rig||'', my_antenna:qso.my_antenna||'',
      tags:Array.isArray(qso.tags)?qso.tags.join(', '):(qso.tags||'')});
    if (qso.us_state||qso.cq_zone||qso.pota_ref||qso.sota_ref||qso.contest_name) {
      setShowContestFields(true);
    }
    setView('logger');
  };

  const formatDT = (iso) => { try { return format(parseISO(iso),'dd.MM.yyyy HH:mm'); } catch { return iso; } };

  const logout = () => {
    // Czyscimy cache API w Service Workerze -- może zawierac odpowiedzi z
    // danymi QSO; na wspolnym urzadzeniu nie powinny zostac po wylogowaniu.
    try { navigator.serviceWorker?.controller?.postMessage({type:'CLEAR_API_CACHE'}); } catch {}
    localStorage.clear();
    setCallsign(null);
  };

  // Wysyla komende QSY do agenta CAT (Hamlib/rigctld) -- przyjmuje ja przy
  // kolejnym cyklu pollingu i faktycznie przestawia radio. Działa tylko gdy
  // user ma uruchomionego lokalnego agenta CAT -- jeśli nie, komenda po
  // prostu wygasa po 60s bez efektu (nic się nie psuje, tylko nic się nie dzieje).
  // Spot na klaster DX -- idzie do PUBLICZNEJ sieci, wiec zawsze z
  // potwierdzeniem pokazujacym dokladna tresc. Wysylany pod znakiem usera
  // (albo okolicznosciowym, pod którym aktualnie pracuje -- station_callsign).
  const sendClusterSpot = async () => {
    const dx = (form.callsign||'').trim().toUpperCase();
    const freqMhz = parseFreq(form.frequency);
    if (!dx || isNaN(freqMhz)) { notify(tx('Wpisz znak i częstotliwość'),'error'); return; }
    const comment = window.prompt(tx('Komentarz do spotu (opcjonalnie, max 30 znaków, bez polskich liter):'), form.mode||'');
    if (comment === null) return;
    const spotter = form.station_callsign || callsign;
    const khz = (freqMhz*1000).toFixed(1);
    if (!window.confirm(tx('Wysłać do publicznej sieci klastrów DX?\n\nDX de {0}:  {1}  {2}  {3}', [spotter, khz, dx, comment.slice(0,30)]))) return;
    try {
      const r = await axios.post(`${API}/api/cluster/spot`, {
        dx, frequency: freqMhz, comment, station_callsign: form.station_callsign||undefined,
      });
      notify(tx('📢 Spot wysłany: {0} {1} kHz (jako {2})', [dx, khz, r.data.spotter]));
    } catch (err) {
      notify(err.response?.data?.error || tx('Węzeł klastra odrzucił spot: {0}', [err.response?.data?.reply || '']), 'error');
    }
  };

  // Push "przypomnienie o zawodach" otwiera /?view=logger&contest=NAZWA --
  // wlaczamy tryb zawodow z wpisana nazwa (raz, przy montowaniu).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contest = params.get('contest');
    if (!contest) return;
    setView('logger');
    setContestMode(true);
    setForm(f => ({ ...f, contest_name: contest.slice(0, 100) }));
    window.history.replaceState({}, '', window.location.pathname);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // opts.comment -- komentarz spotu ("UP 5", "QSX 14.205"): radio dostaje split (odbior na DX-ie,
  // nadawanie obok); opts.repeater -- przesuniecie i ton przemiennika. Tylko radio przez USB.
  const sendQsyCommand = async (freq, mode, opts) => {
    if (!freq) return;
    try {
      if (catStatus.source === 'webserial') {
        const splitTx = opts?.comment ? parseSplit(opts.comment, freq, mode) : null;
        await rigQsy(freq, mode, { splitTx, repeater: opts?.repeater });
        if (splitTx) notify(tx('📻 Radio (USB): {0} MHz{1}, SPLIT — nadawanie {2} MHz', [freq, mode?` (${mode})`:'', splitTx.toFixed(4)]));
        else if (opts?.repeater?.offsetMHz) notify(tx('📻 Radio (USB): przemiennik {0} MHz, przesunięcie {1} kHz{2}', [freq, Math.round(opts.repeater.offsetMHz*1000), opts.repeater.ctcss?`, ton ${opts.repeater.ctcss} Hz`:'']));
        else notify(tx('📻 Radio (USB) nastawione: {0} MHz{1}', [freq, mode?` (${mode})`:'']));
        return;
      }
      await axios.post(`${API}/api/cat/command`, {frequency: freq, mode});
      notify(tx('📻 Wyslano QSY: {0} MHz{1} -- jeśli masz uruchomiony agent CAT, radio przestawi się automatycznie', [freq, mode?` (${mode})`:'']));
    } catch { notify(tx('Blad wysylki komendy QSY'),'error'); }
  };

  // Powrot z sesji "Zaloguj sie jako" (impersonacja admina) do wlasnego konta --
  // przywraca tokeny zachowane przed przelaczeniem, patrz impersonateUser w AdminPanel.
  const returnToOwnAccount = () => {
    const adminToken = localStorage.getItem('admin_access_token');
    const adminRefresh = localStorage.getItem('admin_refresh_token');
    const adminCallsign = localStorage.getItem('admin_callsign');
    if (!adminToken) return;
    localStorage.setItem('access_token', adminToken);
    localStorage.setItem('refresh_token', adminRefresh);
    localStorage.setItem('callsign', adminCallsign);
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_callsign');
    window.location.reload();
  };

  // Proste routowanie po sciezce URL (bez react-router) -- obsluga linkow
  // z emaili (reset hasla / weryfikacja email) dziala niezaleznie od tego,
  // czy ktos jest zalogowany.
  const path = window.location.pathname;
  const resetMatch = path.match(/^\/reset-password\/(.+)$/);
  const verifyMatch = path.match(/^\/verify-email\/(.+)$/);
  if (resetMatch) {
    return <ResetPasswordScreen token={resetMatch[1]} onDone={()=>{ window.location.href='/'; }} />;
  }
  if (verifyMatch) {
    return <VerifyEmailScreen token={verifyMatch[1]} onDone={()=>{ window.location.href='/'; }} />;
  }

  if (!callsign) return <AuthScreen onLogin={(cs, becameAdmin)=>{ setCallsign(cs); if(becameAdmin) setJustBecameAdmin(true); }} />;

  // Zwracamy pozycje TYLKO gdy obie wspolrzedne są liczbami -- inaczej null,
  // zeby konsument mogl uzyc swojego domyslnego punktu (patrz Maps.js).
  const homePos = (Number.isFinite(Number(userProfile?.home_lat))
                   && Number.isFinite(Number(userProfile?.home_lon)))
    ? [Number(userProfile.home_lat), Number(userProfile.home_lon)]
    : null;

  return (
    <div className={"app"+(!isMobile&&navLayout==='sidebar-left'?" app--sidebar-left":!isMobile&&navLayout==='sidebar-right'?" app--sidebar-right":"")
      +(!isMobile&&navLayout!=='top'&&sideCollapsed?" app--sidebar-collapsed":"")+(isMobile?" app--tabs":"")}>
      {notification&&<div className={"notification notification--"+notification.type}>{notification.msg}</div>}
      {showSyncPrompt&&offlineQueueCount>0&&(
        <div className="sync-prompt">
          <span className="sync-prompt__text">
            {tx('📴 Masz')} <strong>{offlineQueueCount}</strong> {offlineQueueCount===1?'QSO':'QSO'} {tx('zapisanych offline — wysłać do bazy?')}
          </span>
          <div className="sync-prompt__actions">
            <button className="btn btn--primary btn--sm" onClick={sendOfflineQueue}>{t('eqsl.sendNow')}</button>
            <button className="btn btn--ghost btn--sm" onClick={()=>setShowSyncPrompt(false)}>{t('common.later')}</button>
          </div>
        </div>
      )}
      {swUpdateAvailable&&(
        <div className="sync-prompt" style={showSyncPrompt&&offlineQueueCount>0?{bottom:'calc(90px + env(safe-area-inset-bottom))'}:undefined}>
          <span className="sync-prompt__text">
            {t('newVersion')}
          </span>
          <div className="sync-prompt__actions">
            <button className="btn btn--primary btn--sm" onClick={hardReload}>{t('common.refreshNow')}</button>
            <button className="btn btn--ghost btn--sm" onClick={dismissUpdate}>{t('common.later')}</button>
          </div>
        </div>
      )}
      {!simple&&<RadioDialerWidget notify={notify} remote={!!userProfile?.feature_flags?.wsjtx_remote} />}
      <SvxlinkPlayerWidget activeGroup={svxlinkActiveGroup} setActiveGroup={setSvxlinkActiveGroup} notify={notify} onQuickLog={handleSvxlinkQuickLog} />
      {stationCardCall&&(
        <Suspense fallback={null}>
          <StationCardModal callsign={stationCardCall} onClose={()=>setStationCardCall(null)}
            onUseSpot={(sp)=>{
              setForm(f=>({...f,callsign:stationCardCall,frequency:sp.frequency||f.frequency,mode:(sp.mode||f.mode).toUpperCase(),
                pota_ref:sp.pota_ref||f.pota_ref}));
              setStationCardCall(null); setView('logger');
            }} />
        </Suspense>
      )}
      {showQrzModal&&<QrzModal onClose={()=>{setShowQrzModal(false);axios.get(`${API}/api/qrz/status`).then(r=>setQrzStatus(r.data)).catch(()=>{});}} notify={notify} />}
      {showCabrilloModal&&<CabrilloExportModal onClose={()=>setShowCabrilloModal(false)} downloadWithAuth={downloadWithAuth} />}
      {showExternalUploadModal&&<ExternalUploadModal onClose={()=>setShowExternalUploadModal(false)} notify={notify} userProfile={userProfile} setUserProfile={setUserProfile} />}
      {showLotwModal&&<LotwModal onClose={()=>setShowLotwModal(false)} notify={notify} />}
      {routeProp&&(
        <div className="modal-overlay" onClick={()=>setRouteProp(null)}>
          <div className="modal" style={{maxWidth:480}} onClick={e=>e.stopPropagation()}>
            <div className="modal__header">
              <h3><Icon name="sun" size={18} /> {tx('Propagacja trasy')}</h3>
              <button className="modal__close" onClick={()=>setRouteProp(null)}>✕</button>
            </div>
            <div style={{display:'flex',gap:14,flexWrap:'wrap',fontSize:12,color:'var(--text-dim)',marginBottom:14}}>
              <span>📏 {routeProp.distance_km} {tx('km')}</span>
              <span>🧭 {routeProp.bearing_deg}°</span>
              <span>↔️ ~{routeProp.hops_estimate} {tx('przeskok(ów)')}</span>
              <span>☀️ SFI {routeProp.sfi}</span>
              <span>🧲 K {routeProp.k_index}</span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {routeProp.bands.map(b=>{
                const color = b.score>=65?'var(--accent)':b.score>=40?'#ffbb00':'var(--text-dim)';
                return (
                  <div key={b.band} style={{display:'flex',alignItems:'center',gap:10}}>
                    <span style={{fontFamily:'var(--mono)',width:44,fontWeight:b.band===routeProp.best_band?700:400,
                      color:b.band===routeProp.best_band?'var(--accent)':'var(--text)'}}>{b.band}</span>
                    <div style={{flex:1,height:8,background:'var(--bg3)',borderRadius:4,overflow:'hidden'}}>
                      <div style={{width:`${b.score}%`,height:'100%',background:color,transition:'width .3s'}} />
                    </div>
                    <span style={{fontSize:11,color,width:130,textAlign:'right'}}>{b.label}</span>
                  </div>
                );
              })}
            </div>
            <p style={{fontSize:10,color:'var(--text-dim)',marginTop:14,lineHeight:1.5,fontStyle:'italic'}}>
              {routeProp.disclaimer}
            </p>
          </div>
        </div>
      )}
      {showCwDecoder&&<CWDecoder onClose={()=>setShowCwDecoder(false)} notify={notify}
        onResult={(cs)=>setForm(f=>({...f,callsign:cs.toUpperCase()}))} />}
      <NoticeCenter />
      {qslCardQso&&<QslCardModal qso={qslCardQso} notify={notify} onClose={()=>setQslCardQso(null)} onEditTemplate={()=>setView('eqsl')} />}
      {showAccountModal&&<AccountSettingsModal onClose={()=>{setShowAccountModal(false);setAccountTab(null);}} notify={notify} initialTab={accountTab}
        callsign={callsign} userProfile={userProfile} setUserProfile={setUserProfile} onLogout={logout}
        theme={theme} setTheme={setTheme} navLayout={navLayout} setNavLayout={setNavLayout}
        startView={startView} setStartView={setStartView}
        onQrzChanged={()=>axios.get(`${API}/api/qrz/status`).then(r=>setQrzStatus(r.data)).catch(()=>{})} />}
      {/* Paleta polecen -- Ctrl/Cmd+K. Nawigacja przekazywana z tego samego
          zrodla co menu, wiec nie moze sie z nim rozjechac. */}
      <CommandPalette open={showPalette} onClose={closePalette}
        navGroups={NAV_GROUPS(userProfile, isOnline, t)}
        onNavigate={setView} currentView={view}
        onStationCard={(c)=>setStationCardCall(c)}
        onLogSearch={(c)=>{ setView('logbook'); setSearchQuery(c); setPage(1); }} />

      {showOfflineQueueModal&&<OfflineQueueModal onClose={()=>setShowOfflineQueueModal(false)} notify={notify}
        onQueueChanged={checkOfflineQueue} theme={theme} />}
      {showWelcome&&!showTour&&userProfile&&userProfile.onboarding_done!==false&&<WelcomeModal onClose={dismissWelcome} />}
      {(showTour||(userProfile&&userProfile.onboarding_done===false&&!userProfile.impersonated_by_callsign))&&(
        <Suspense fallback={null}>
          <OnboardingTour isSwl={!!userProfile?.is_swl} isMobile={isMobile} setView={setView}
            simple={simple} onSimple={(on)=>{
              setUserProfile(p=>p&&({...p, simple_mode:on}));
              axios.put(`${API}/api/auth/update`, {simple_mode:on}).catch(()=>{});
            }}
            onFinish={()=>{
              setShowTour(false);
              dismissWelcome();   // samouczek zastepuje jednorazowe powitanie
              if (userProfile?.onboarding_done===false) {
                setUserProfile(p=>p&&({...p, onboarding_done:true}));
                axios.put(`${API}/api/auth/update`, {onboarding_done:true}).catch(()=>{});
              }
            }} />
        </Suspense>
      )}

      {userProfile?.impersonated_by_callsign&&(
        <div className="impersonation-banner">
          {tx('🎭 Działasz jako')} <strong>{callsign}</strong> {tx('(zalogowany jako administrator:')} {userProfile.impersonated_by_callsign})
          <button className="btn btn--sm" onClick={returnToOwnAccount} style={{marginLeft:12}}>{t('common.backToAccount')}</button>
        </div>
      )}
      <header className="header">
        <button type="button" className="header__logo" onClick={()=>setView('dashboard')} title={tx('Pulpit')}>
          <span className="header__logo-icon"><Icon name="tower" size={19} /></span>
          <span className="header__title">{tx('HamLogger')}</span>
        </button>

        {!isMobile&&navLayout==='top'&&(
          <nav className="header__nav" ref={navRef}>
            {NAV_GROUPS(userProfile, isOnline, t).map((g,gi)=>(
              g.type==='single'?(
                <button key={g.id} className={"nav-btn"+(view===g.id?" nav-btn--active":"")} onClick={()=>setView(g.id)} title={stripLabelIcon(g.label)}><NavLabel label={g.label} icon={g.id} /></button>
              ):(
                <div key={g.key} className="nav-group">
                  <button
                    className={"nav-btn nav-btn--group"+(g.items.some(([v])=>v===view)?" nav-btn--active":"")}
                    onClick={()=>setOpenNavGroup(o=>o===gi?null:gi)} title={stripLabelIcon(g.label)}>
                    <NavLabel label={g.label} icon={g.key} /> <span className="nav-btn__caret"><Icon name="chevronDown" size={13} /></span>
                  </button>
                  {openNavGroup===gi&&(
                    <div className="nav-dropdown">
                      {g.items.map(([v,label])=>(
                        <button key={v} className={"nav-dropdown__item"+(view===v?" nav-dropdown__item--active":"")}
                          onClick={()=>{setView(v);setOpenNavGroup(null);}}>
                          <Icon name={v} size={16} /> {stripLabelIcon(label)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ))}
          </nav>
        )}

        <button type="button" data-tour="search" className={"header__search"+(!isMobile&&navLayout==='top'?" header__search--compact":"")}
          onClick={openPalette} title={tx('Szukaj widoku, znaku lub QSO (Ctrl+K)')} aria-label={tx('Szukaj')}>
          <Icon name="search" size={16} />
          <span className="header__search-text">{tx('Szukaj widoku, znaku, QSO…')}</span>
          <kbd className="header__kbd">{tx('Ctrl K')}</kbd>
          {!paletteHintSeen&&<span className="header__hint-dot" />}
        </button>

        <div className="header__status">
          {!isOnline&&(
            <span className="status-chip status-chip--danger" title={tx('Brak połączenia z internetem — aplikacja działa offline')}>
              <Icon name="wifiOff" size={14} /> {tx('Offline')}
            </span>
          )}
          {offlineQueueCount>0&&(
            <button type="button" className="status-chip status-chip--warning" onClick={()=>setShowOfflineQueueModal(true)}
              title={tx('QSO czekające na wysłanie (offline) — kliknij, aby zobaczyć')}>
              <Icon name="inbox" size={14} /> {offlineQueueCount}
            </button>
          )}
          {!simple&&<AuroraAlertBadge />}
          {sessionCount>0&&<span className="status-chip status-chip--secondary" title={tx('QSO zalogowane w tej sesji')}>+{sessionCount} QSO</span>}
          {catStatus.connected&&(
            <span className={"status-chip status-chip--secondary "+(catStatus.ptt?"status-chip--danger":rigWarnings.length?"status-chip--warning":"status-chip--success")}
              title={rigWarnings.length?rigWarnings.join('\n'):tx('Radio (CAT): {0} MHz {1}', [catStatus.frequency?.toFixed?.(3), catStatus.mode||''])}>
              <Icon name={rigWarnings.length?'alert':'radio'} size={14} /> {catStatus.ptt?'TX ':''}{catStatus.frequency?.toFixed?.(3)} {catStatus.mode}{catStatus.splitTx?' SPLIT':''}
            </span>
          )}
          <UtcClockChip />
          <select className="status-chip status-chip--select status-chip--secondary" value={lang} onChange={e=>changeLanguage(setLang, e.target.value, true)}
            title={t('lang.label')} aria-label={t('lang.label')}>
            {AVAILABLE_LANGUAGES.map(l=>(
              <option key={l.code} value={l.code}>{l.code.toUpperCase()}</option>
            ))}
          </select>
          {!qrzStatus.logged_in&&!qrzStatus.hamqth&&(
            <button type="button" className="status-chip status-chip--secondary" onClick={()=>setShowQrzModal(true)} title={tx('Połącz QRZ.com (wyszukiwanie znaków)')}>
              <Icon name="key" size={14} /> QRZ
            </button>
          )}
          <NotificationBell onNavigate={setView} notify={notify} />
          <button type="button" className="header__account" data-tour="account" onClick={()=>setShowAccountModal(true)} title={tx('Ustawienia konta')}>
            <span className="header__avatar"><Icon name="user" size={15} /></span>
            <span className="header__callsign">{callsign}</span>
            {(qrzStatus.logged_in||qrzStatus.hamqth)&&(
              <span className="header__qrz-ok" title={qrzStatus.logged_in?tx('QRZ.com połączone'):tx('HamQTH połączone')}><Icon name="check" size={12} strokeWidth={3} /></span>
            )}
          </button>
        </div>
      </header>

      {!isMobile&&navLayout!=='top'&&(
        <SidebarNav navGroups={NAV_GROUPS(userProfile, isOnline, t)} view={view} setView={setView}
          position={navLayout==='sidebar-right'?'right':'left'} pinned={pinnedViews} togglePin={togglePinnedView}
          collapsed={sideCollapsed} setCollapsed={setSideCollapsed} />
      )}

      {isMobile&&(
        <BottomTabs view={view} isOnline={isOnline} moreOpen={mobileNavOpen} isSwl={!!userProfile?.is_swl} simple={simple}
          setView={(v)=>{ setView(v); setMobileNavOpen(false); }}
          onMore={()=>setMobileNavOpen(o=>!o)} />
      )}

      {isMobile&&mobileNavOpen&&(
        <>
          <div className="mobile-nav-backdrop" onClick={()=>setMobileNavOpen(false)} />
          <nav className="mobile-nav-drawer">
            {NAV_GROUPS(userProfile, isOnline, t).map((g,gi)=>(
              g.type==='single'?(
                <button key={g.id} className={"mobile-nav-btn"+(view===g.id?" mobile-nav-btn--active":"")}
                  onClick={()=>{setView(g.id);setMobileNavOpen(false);}}>
                  <Icon name={g.id} size={18} /> {stripLabelIcon(g.label)}
                </button>
              ):(
                <div key={g.key} className="mobile-nav-section">
                  <div className="mobile-nav-section__title">{stripLabelIcon(g.label)}</div>
                  {g.items.map(([v,label])=>(
                    <button key={v} className={"mobile-nav-btn"+(view===v?" mobile-nav-btn--active":"")}
                      onClick={()=>{setView(v);setMobileNavOpen(false);}}>
                      <Icon name={v} size={18} /> {stripLabelIcon(label)}
                    </button>
                  ))}
                </div>
              )
            ))}
          </nav>
        </>
      )}

      {!simple&&(
        <>
          <PropagationBar />
          <div style={{display:'flex',justifyContent:'flex-end',padding:'6px 16px 0'}}>
            <PropagationSuggestionWidget userProfile={userProfile} />
          </div>
        </>
      )}

      <main className="main">
        <AnnouncementsBanner />

        {/* Suspense: wymagane przy leniwym ladowaniu -- pokazuje wskaznik, gdy
            widok dociaga sie po raz pierwszy. Bez tego React rzuca bledem.
            Fallback jest CELOWO dyskretny (sam tekst, bez skakania layoutu) --
            dociagniecie chunka to zwykle ulamek sekundy. */}
        <ErrorBoundary key={view} onReset={()=>setView('logger')}>
        <Suspense fallback={
          <div style={{padding:'40px 24px',textAlign:'center',color:'var(--text-dim)',fontSize:13}}>
            {tx('Ładowanie widoku…')}
          </div>
        }>

        {/* ── LOGGER ── */}
        {view==='logger'&&contestMode&&(
          <>
            <div style={{display:'flex',justifyContent:'flex-end',padding:'12px 24px 0'}}>
              <button className="btn btn--ghost btn--sm" onClick={()=>setContestMode(false)}>{t('contest.backToLogger')}</button>
            </div>
            <ContestModeView form={form} setForm={setForm} submitQso={submitQso}
              lookupCallsign={lookupCallsign} loading={loading} qrzStatus={qrzStatus}
              notify={notify} duplicateWarning={duplicateWarning}
              mycall={form.station_callsign||callsign} />
          </>
        )}
        {view==='dashboard'&&<DashboardView userProfile={userProfile} qsos={qsos} stats={stats} notify={notify} onNavigate={setView} />}
        {view==='goals'&&<GoalsView />}
        {view==='portable'&&<PortableView notify={notify} />}

        {view==='logger'&&!contestMode&&(
          <div className="logger-layout">
          <section className="section logger-main">
            <div className="section__header">
              <h2><Icon name={editingQso?'edit':'mic'} size={20} /> {editingQso?tx('Edytuj QSO — {0}', [editingQso.callsign]):tx('Nowe QSO')}</h2>
              <div style={{display:'flex',gap:8}}>
                {!editingQso&&!simple&&<button className="btn btn--ghost btn--sm" onClick={()=>setContestMode(true)}><Icon name="flag" size={15} /> {tx('Tryb zawodów')}</button>}
                {editingQso&&<button className="btn btn--ghost btn--sm" onClick={()=>setEditingQso(null)}><Icon name="x" size={15} /> {tx('Anuluj edycję')}</button>}
              </div>
            </div>
            <form className="qso-form" onSubmit={submitQso}
              onKeyDown={(e)=>{
                // Ctrl/Cmd+Enter -- zapisz z dowolnego pola (nie trzeba wracac
                // do ostatniego). Esc -- wyczysc biezacy kontakt (znak/dane),
                // zachowujac lepkie pola (pasmo/emisja/park). Praktyczne w serii.
                if ((e.ctrlKey||e.metaKey) && e.key==='Enter') { e.preventDefault(); submitQso(e); }
                else if (e.key==='Escape') {
                  e.preventDefault();
                  setForm(f=>({...f,callsign:'',operator_name:'',country:'',qth:'',grid_locator:'',
                    notes:'',rst_sent:getRst(f.mode),rst_recv:getRst(f.mode),date_time:nowUTC(),
                    dxcc:'',iota:'',lat:null,lon:null,us_state:'',cq_zone:''}));
                  setDuplicateWarning(null); setWorkedBefore(null); setBearingInfo(null); setNewAchievement(null);
                  mainCallsignRef.current?.focus();
                }
              }}>
              {/* Znak, POD KTORYM pracujesz. Zawsze widoczny (inaczej user nie
                  odkrylby, ze moze dodac znak okolicznosciowy). Lepki miedzy
                  QSO -- podczas aktywacji pracujesz pod jednym znakiem. */}
              {/* "Tego dnia" -- pokazuje sie TYLKO gdy sa lacznosci z tego dnia
                  w poprzednich latach (komponent sam zwraca null, gdy nie ma). */}
              {!simple&&<OnThisDay theme={theme} />}

              {!simple&&<StationCallsignBar
                userProfile={userProfile}
                value={form.station_callsign||''}
                onChange={(v)=>setForm(f=>({...f,station_callsign:v}))} />}
              {!simple&&<StationProfileBar form={form} setForm={setForm} editing={!!editingQso}
                onManage={()=>setView('station')} />}

              <div className="form-row form-row--highlight">
                <div className="form-group form-group--lg" data-tour="callsign">
                  <label>{t('logger.callsignReq')}</label>
                  <div className="callsign-input">
                    <input ref={mainCallsignRef} className="input input--callsign" value={form.callsign}
                      onKeyDown={e=>{ if (e.key==='ArrowDown' && spotHint && !form.callsign) { e.preventDefault(); applySpotHint(); } }}
                      onChange={e=>{const v=e.target.value.toUpperCase(); setForm(f=>({...f,callsign:v})); autoLookupCallsign(v);}}
                      onBlur={e=>{if(autoLookupTimerRef.current)clearTimeout(autoLookupTimerRef.current); lookupCallsign(e.target.value);}} placeholder="SP1ABC" required />
                    {!simple&&<VoiceCallsignButton notify={notify}
                      onResult={(cs)=>{ setForm(f=>({...f,callsign:cs})); lookupCallsign(cs); }} />}
                    <button type="button" className="btn btn--qrz"
                      onClick={()=>lookupCallsign(form.callsign)}
                      disabled={loading||!(qrzStatus.logged_in||qrzStatus.hamqth)}
                      title={qrzStatus.logged_in?tx('Szukaj w QRZ.com (a gdy brak -- w HamQTH)'):tx('Szukaj w HamQTH')}>
                      {loading?'…':<><Icon name="search" size={15} /> {qrzStatus.logged_in?'QRZ':tx('HamQTH')}</>}
                    </button>
                  </div>
                </div>
                <div className="form-group" data-tour="datetime">
                  <label>{t('logger.dateTimeUtc')} {!editingQso&&(dateTimeAuto
                    ? <span style={{color:'var(--text-dim)',fontWeight:400,fontSize:11}}
                        title={qsoStart?tx('Początek QSO zapisany przy wpisaniu znaku; koniec zapisze się przy zapisie'):undefined}>{qsoStart?tx('⏱ początek QSO'):tx('🕐 na zywo')}</span>
                    : <button type="button" className="btn btn--ghost btn--sm" style={{padding:'0 6px'}}
                        onClick={()=>{setDateTimeAuto(true); setForm(f=>({...f,date_time:nowUTC()}));}}>{tx('↺ Teraz')}</button>)}
                  </label>
                  <input className="input" type="datetime-local" value={form.date_time}
                    onChange={e=>{setDateTimeAuto(false); setForm(f=>({...f,date_time:e.target.value}));}} />
                </div>
              </div>

              {spotHint&&!editingQso&&(
                <button type="button" className="spot-hint" onClick={applySpotHint}
                  title={tx('Spot z klastra na częstotliwości radia — kliknij albo naciśnij strzałkę w dół w pustym polu znaku')}>
                  <Icon name="network" size={14} /> {tx('Na tej częstotliwości:')} <strong>{spotHint.dx}</strong>
                  <span className="spot-hint__meta">{(Number(spotHint.freq)*1000).toFixed(1)} kHz{spotHint.entity?` · ${spotHint.entity}`:''}{spotHint.comment?` · ${spotHint.comment}`:''}</span>
                </button>
              )}
              {catStatus.splitTx&&!editingQso&&(
                <div className="split-note">
                  <span>{tx('SPLIT: nadajesz na {0} MHz — w logu: nadawanie {0}, odbiór {1}', [catStatus.splitTx.toFixed(4), parseFreq(form.frequency)?.toFixed?.(4)||'?'])}</span>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={()=>rigSplitOff()}>{tx('Wyłącz split')}</button>
                </div>
              )}
              {duplicateWarning&&(
                <div className="dup-warning">
                  {tx('⚠️ Masz już QSO z')} <strong>{form.callsign}</strong> {tx('dzisiaj na tym paśmie/modzie:')}
                  {' '}
                  {duplicateWarning.map((d,i)=>(
                    <span key={d.id}>
                      {i>0&&', '}
                      {d.date_time?.slice(11,16)} UTC ({d.band}/{d.mode})
                    </span>
                  ))}
                  {(' ' + tx('— sprawdź czy to nie przypadkowy duplikat.') )}
                </div>
              )}

              {/* Znaczniki wartosci QSO -- jeden rzad pod znakiem zamiast kilku
                  osobnych banerow: wczesniej pracowane, nowy na pasmie/emisji,
                  nowy kraj, namiar. */}
              {(()=>{
                const curBand = form.frequency && !isNaN(parseFloat(form.frequency)) ? freqToBandJs(parseFloat(form.frequency)) : '';
                const pairNew = workedBefore && !duplicateWarning && curBand && form.mode && !workedBefore.band_mode_pairs.includes(`${curBand}|${form.mode}`);
                const hasAny = (workedBefore && !duplicateWarning) || bearingInfo || newAchievement?.is_new_country || newAchievement?.is_new_band || newAchievement?.is_new_mode;
                if (!hasAny) return null;
                return (
                  <div className="qso-chips">
                    {newAchievement?.is_new_country&&<span className="chip chip--success"><Icon name="star" size={13} /> {tx('Nowy kraj')}</span>}
                    {newAchievement?.is_new_band&&<span className="chip chip--success">{tx('Nowe pasmo dla kraju')}</span>}
                    {newAchievement?.is_new_mode&&<span className="chip chip--success">{tx('Nowa emisja dla kraju')}</span>}
                    {pairNew&&<span className="chip chip--success">{tx('Nowy na')} {curBand} {form.mode}</span>}
                    {workedBefore&&!duplicateWarning&&(
                      <span className="chip" title={`${workedBefore.bands.join(', ')} · ${workedBefore.modes.join(', ')}`}>
                        {tx('Pracowany')} {workedBefore.count}× · {workedBefore.bands.join(' ')}
                      </span>
                    )}
                    {bearingInfo&&(
                      <span className="chip" title={tx('Namiar i odległość od Twojej lokalizacji do {0}', [form.callsign])}>
                        <Icon name="compass" size={13} /> {Math.round(bearingInfo.bearingDeg)}° {bearingInfo.compass} · {Math.round(bearingInfo.distanceKm)} {tx('km')}
                      </span>
                    )}
                    {bearingInfo&&(form.lat||form.grid_locator)&&(
                      <button type="button" className="chip chip--action" onClick={loadRoutePropagation}
                        title={tx('Oceń, które pasmo może być otwarte na tej trasie')}>
                        <Icon name="sun" size={13} /> {tx('Propagacja trasy')}
                      </button>
                    )}
                  </div>
                );
              })()}

              <div className="form-row form-row--freq">
                <div className="form-group" data-tour="mode">
                  <label>{t('logger.modeReq')}</label>
                  <div className="callsign-input">
                    <select className="input" value={form.mode}
                      onChange={e=>setForm(f=>({...f,mode:e.target.value,rst_sent:getRst(e.target.value),rst_recv:getRst(e.target.value)}))}>
                      {MODES.map(m=><option key={m}>{m}</option>)}
                    </select>
                    {form.mode==='CW'&&(
                      <button type="button" className="btn btn--qrz" onClick={()=>setShowCwDecoder(true)}
                        title={tx('Dekoder CW (beta) -- nasluchuje mikrofonu')}><Icon name="radio" size={16} /></button>
                    )}
                  </div>
                </div>
                <div className="form-group form-group--freq" data-tour="freq">
                  <label>{t('logger.freqReq')} {(() => {
                    const b = form.frequency && !isNaN(parseFloat(form.frequency)) ? freqToBandJs(parseFloat(form.frequency)) : '';
                    return b ? <span style={{color:'var(--accent)',fontWeight:700,fontFamily:'var(--mono)',marginLeft:6}}>· {b}</span> : null;
                  })()}</label>
                  <div className="callsign-input">
                    <input ref={freqInputRef} className="input input--freq" type="text" inputMode="decimal" placeholder="14.074"
                      onKeyDown={onFreqKeyDown}
                      style={!isFreqInHamBand(form.frequency)?{borderColor:'var(--danger)',boxShadow:'0 0 0 1px var(--danger)'}:undefined}
                      title={!isFreqInHamBand(form.frequency)?tx('Czestotliwosc poza pasmami amatorskimi -- sprawdz'):tx('Strzałki ↑↓ lub kółko myszy: strojenie (Shift = ×10), PageUp/PageDown: pasmo')}
                      value={form.frequency} onChange={e=>setForm(f=>({...f,frequency:e.target.value}))} required />
                    {catStatus.connected&&(
                      <button type="button" className={"btn btn--qrz follow-radio"+(followRadio?" follow-radio--on":"")}
                        onClick={followRadio?toggleFollowRadio:()=>{ toggleFollowRadio(); fillFromRadio(); }}
                        aria-pressed={followRadio}
                        title={followRadio
                          ? tx('Śledzę radio: {0} MHz / {1} -- kliknij, żeby wyłączyć', [catStatus.frequency?.toFixed(3), catStatus.mode])
                          : tx('Kliknij, żeby pole podążało za radiem ({0} MHz / {1})', [catStatus.frequency?.toFixed(3), catStatus.mode])}>
                        <Icon name="radio" size={16} /> {followRadio?tx('Śledzę radio'):tx('Z radia')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="form-group" data-tour="rst"><label>{t('logger.rstSent')}</label>
                  {catStatus.smeterPeak!=null&&!DIGITAL_SNR_MODES.includes(form.mode)?(
                    <div className="callsign-input">
                      <input className="input" value={form.rst_sent} onChange={e=>setForm(f=>({...f,rst_sent:e.target.value}))} />
                      <button type="button" className="btn btn--qrz smeter-btn"
                        onClick={()=>setForm(f=>({...f,rst_sent:rstFromSmeter(catStatus.smeterPeak,f.mode)}))}
                        title={tx('Wpisz raport z S-metra radia (szczyt z ostatnich 5 s): {0}', [rstFromSmeter(catStatus.smeterPeak,form.mode)])}>
                        {sUnitsLabel(catStatus.smeterPeak)}
                      </button>
                    </div>
                  ):(
                    <input className="input" value={form.rst_sent} onChange={e=>setForm(f=>({...f,rst_sent:e.target.value}))} />
                  )}</div>
                <div className="form-group"><label>{t('logger.rstRecv')}</label>
                  <input className="input" value={form.rst_recv} onChange={e=>setForm(f=>({...f,rst_recv:e.target.value}))} /></div>
                <div className="form-group"><label>{t('logger.power')}</label>
                  <input className="input" type="number" value={form.power} onChange={e=>setForm(f=>({...f,power:e.target.value}))} placeholder="100" /></div>
              </div>

              {!simple&&form.mode==='CW'&&(catStatus.connected||agentCatStatus.connected)&&(
                <KeyerPanel compact mode={form.mode} notify={notify}
                  ctx={{ mycall: callsign, call: form.callsign, nr: '', rst: form.rst_sent }} />
              )}
              {(()=>{
                const warns = bandPlanWarnings(form.frequency, form.mode, form.power, userProfile);
                return warns.length>0 && (
                  <div className="bandplan-warning">
                    {warns.map((w,i)=><div key={i}>⚠️ {w.text}</div>)}
                  </div>
                );
              })()}
              {/* Szybki wybor czestotliwosci -- zwiniety (duzy panel), stan zapamietany */}
              {!simple&&<button type="button" className={"more-toggle more-toggle--slim"+(showFreqPicker?" more-toggle--open":"")}
                aria-expanded={showFreqPicker} onClick={()=>setShowFreqPicker(!showFreqPicker)}>
                <Icon name={showFreqPicker?'chevronDown':'chevronRight'} size={16} />
                <span>{tx('Szybki wybór częstotliwości')}</span>
                <span className="more-toggle__hint">{tx('popularne częstotliwości, przemienniki, ulubione')}</span>
              </button>}
              {!simple&&showFreqPicker&&(
                <QuickFreqPanel currentMode={form.mode} currentFreq={form.frequency} notify={notify}
                  onSelect={(freq,band,mode,opts)=>{
                    setForm(f=>({...f,frequency:freq,mode:mode||f.mode,rst_sent:getRst(mode||f.mode),rst_recv:getRst(mode||f.mode)}));
                    // Radio podlaczone (USB albo agent) -- kafelek od razu przestraja radio
                    // (takze wlasne ulubione i przemienniki -- z przesunieciem i tonem); komunikat daje sendQsyCommand
                    if (catStatus.connected) sendQsyCommand(freq, mode, opts);
                    else notify(tx('{0} MHz · {1} · {2}', [freq.toFixed(3), band, mode]));
                  }} />
              )}

              {form.pota_ref&&(
                <div className="activation-bar">
                  <span><Icon name="tree" size={15} /> <strong>{t('logger.activating')}</strong> {form.pota_ref}{potaParkName&&` — ${potaParkName}`}</span>
                  <span className="chip">{potaActivationCount} QSO</span>
                  <button type="button" className="btn btn--ghost btn--sm"
                    onClick={()=>setForm(f=>({...f,pota_ref:''}))}>
                    {t('logger.endActivation')}
                  </button>
                </div>
              )}

              {(()=>{
                const filled = [form.operator_name, form.qth, form.grid_locator, form.notes&&'notatki', form.tags&&'tagi',
                  form.pota_ref&&`POTA ${form.pota_ref}`, form.sota_ref&&`SOTA ${form.sota_ref}`, form.contest_name].filter(Boolean);
                const open = showMoreFields || !!editingQso;
                return (
                  <button type="button" className={"more-toggle"+(open?" more-toggle--open":"")} aria-expanded={open}
                    onClick={()=>setShowMoreFields(!showMoreFields)} disabled={!!editingQso}>
                    <Icon name={open?'chevronDown':'chevronRight'} size={16} />
                    <span>{tx('Więcej szczegółów')}</span>
                    <span className="more-toggle__hint">{filled.length ? filled.join(' · ') : tx('imię, QTH, lokator, QSL, POTA/SOTA, zawody, notatki')}</span>
                  </button>
                );
              })()}

              {(showMoreFields||!!editingQso)&&(
              <div className="more-fields">
              <div className="form-row">
                <div className="form-group form-group--lg"><label>{tx('Imie i Nazwisko')}</label>
                  <input className="input" value={form.operator_name} onChange={e=>setForm(f=>({...f,operator_name:e.target.value}))} placeholder={tx('Jan Kowalski')} /></div>
                <div className="form-group"><label>{t('logger.qth')}</label>
                  <input className="input" value={form.qth} onChange={e=>setForm(f=>({...f,qth:e.target.value}))} placeholder={tx('Warszawa')} /></div>
                <div className="form-group"><label>{tx('Kraj')}</label>
                  <input className="input" value={form.country} onChange={e=>setForm(f=>({...f,country:e.target.value}))} placeholder={tx('Poland')} /></div>
              </div>

              <div className="form-row">
                <div className="form-group"><label>{tx('Lokator')}</label>
                  <input className="input" value={form.grid_locator}
                    onChange={e=>setForm(f=>({...f,grid_locator:e.target.value.toUpperCase()}))}
                    style={!isValidGrid(form.grid_locator)?{borderColor:'var(--danger)',boxShadow:'0 0 0 1px var(--danger)'}:undefined}
                    title={!isValidGrid(form.grid_locator)?tx('Nieprawidlowy format lokatora (np. KO02, JO91ab)'):undefined}
                    placeholder="KO02" /></div>
                <div className="form-group"><label>DXCC</label>
                  <input className="input" value={form.dxcc} onChange={e=>setForm(f=>({...f,dxcc:e.target.value}))} placeholder="269" /></div>
                <div className="form-group"><label>IOTA</label>
                  <input className="input" value={form.iota} onChange={e=>setForm(f=>({...f,iota:e.target.value}))} placeholder="EU-132" /></div>
                <div className="form-group form-group--checks">
                  <label>QSL</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" checked={form.qsl_sent} onChange={e=>setForm(f=>({...f,qsl_sent:e.target.checked}))}/>{tx('Wysłana')}</label>
                    <label className="checkbox-label"><input type="checkbox" checked={form.qsl_recv} onChange={e=>setForm(f=>({...f,qsl_recv:e.target.checked}))}/>{tx('Otrzymana')}</label>
                    <label className="checkbox-label" title={tx('Po zapisaniu QSO wyśle automatycznie graficzną kartę eQSL mailem na adres znaleziony w QRZ (jeśli druga stacja go udostępniła publicznie)')}>
                      <input type="checkbox" checked={form.auto_send_eqsl} onChange={e=>setForm(f=>({...f,auto_send_eqsl:e.target.checked}))}/>{tx('📧 Wyślij eQSL')}
                    </label>
                  </div>
                </div>
                {/* Tylko w edycji -- na to, by recznie podac adres email tej
                    konkretnej stacji, gdy QRZ nie ma dla niej publicznego
                    adresu (autowysylka/przycisk w Logbooku wtedy by nie
                    zadzialal). Ma priorytet nad wyszukiwaniem w QRZ. */}
                {editingQso&&(
                  <div className="form-group form-group--lg">
                    <label>{tx('Adres email (opcjonalnie -- jeśli QRZ nie ma publicznego)')}</label>
                    <input className="input" type="email" value={form.manual_email}
                      onChange={e=>setForm(f=>({...f,manual_email:e.target.value}))}
                      placeholder={tx('np. sp5xyz@example.com')} />
                  </div>
                )}
              </div>

              <QsoLocatorMap
                homePos={homePos}
                dxPos={(form.lat!=null&&form.lon!=null)?{lat:form.lat,lon:form.lon}:null}
                dxCallsign={form.callsign||'DX'}
                homeGrid={userProfile?latLonToGrid(userProfile.home_lat,userProfile.home_lon,4):null}
                dxGrid={form.grid_locator||((form.lat!=null&&form.lon!=null)?latLonToGrid(form.lat,form.lon,4):null)}
                theme={theme}
              />

              <button type="button" className="btn btn--ghost btn--sm"
                style={{marginTop:4}}
                onClick={()=>setShowContestFields(s=>!s)}>
                <Icon name={showContestFields?'chevronDown':'chevronRight'} size={14} /> {tx('Zawody i dyplomy (WAS, WAZ, POTA, SOTA)')}
              </button>

              {showContestFields&&(
                <div className="form-row">
                  <div className="form-group"><label>{tx('Stan USA (WAS)')}</label>
                    <input className="input" value={form.us_state} onChange={e=>setForm(f=>({...f,us_state:e.target.value.toUpperCase().slice(0,2)}))} placeholder="CA" /></div>
                  <div className="form-group"><label>{tx('Zona CQ (WAZ)')}</label>
                    <input className="input" value={form.cq_zone} onChange={e=>setForm(f=>({...f,cq_zone:e.target.value}))} placeholder="14" /></div>
                  <div className="form-group"><label>{tx('POTA ref')}</label>
                    <div style={{display:'flex',gap:4}}>
                      <input className="input" value={form.pota_ref} onChange={e=>setForm(f=>({...f,pota_ref:e.target.value.toUpperCase()}))} placeholder="K-1234" />
                      <button type="button" className="btn btn--secondary btn--sm" onClick={lookupPotaPark} disabled={potaLookupBusy||!form.pota_ref.trim()}
                        title={tx('Sprawdz nazwe parku (waliduje referencje)')}>
                        <Icon name={potaLookupBusy?'clock':'search'} size={16} />
                      </button>
                    </div></div>
                  <div className="form-group"><label>{tx('SOTA ref')}</label>
                    <input className="input" value={form.sota_ref} onChange={e=>setForm(f=>({...f,sota_ref:e.target.value.toUpperCase()}))} placeholder="W4C/CM-001" /></div>
                </div>
              )}
              {showContestFields&&(
                <div className="form-row">
                  <div className="form-group"><label>{tx('Nazwa zawodow')}</label>
                    <input className="input" value={form.contest_name} onChange={e=>setForm(f=>({...f,contest_name:e.target.value}))} placeholder="CQ WW DX" /></div>
                  <div className="form-group"><label>{tx('Wymiana wysłana')}</label>
                    <input className="input" value={form.contest_exchange_sent} onChange={e=>setForm(f=>({...f,contest_exchange_sent:e.target.value}))} placeholder="599 14" /></div>
                  <div className="form-group"><label>{tx('Wymiana odebrana')}</label>
                    <input className="input" value={form.contest_exchange_recv} onChange={e=>setForm(f=>({...f,contest_exchange_recv:e.target.value}))} placeholder="599 05" /></div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>{tx('Tagi')} <span style={{fontWeight:400,color:'var(--text-dim)',fontSize:11}}>{tx('(własne, po przecinku -- np. SSTV, pogawędka)')}</span></label>
                  <input className="input" list="qso-tag-suggestions" value={form.tags}
                    onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder={tx('SSTV, sieć ratunkowa')} />
                  <datalist id="qso-tag-suggestions">
                    {availableTags.map(tg=><option key={tg} value={tg} />)}
                  </datalist>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group--full">
                  <label>{tx('Notatki')}</label>
                  <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:6}}>
                    {noteMacros.map((m,i)=>(
                      <button key={m.key} type="button" className="btn btn--ghost btn--sm"
                        style={{padding:'2px 8px',fontSize:11}}
                        title={tx('{0} -- kliknij prawym/długo aby edytować', [m.key])}
                        onClick={()=>insertMacro(m.text)}
                        onContextMenu={e=>{e.preventDefault(); editMacro(i);}}>
                        {m.key}: {m.text.length>18?m.text.slice(0,18)+'…':m.text}
                      </button>
                    ))}
                  </div>
                  <textarea className="input textarea" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
                    placeholder={tx('Warunki propagacji, antena, uwagi...')} rows={2} />
                </div>
              </div>
              </div>
              )}

              <div className="form-actions">
                <button className="btn btn--primary btn--lg" type="submit" disabled={loading} data-tour="save">
                  {loading?tx('Zapisuję…'):(editingQso?<><Icon name="check" size={17} /> {tx('Zapisz zmiany')}</>:<><Icon name="plus" size={17} /> {tx('Dodaj do logu')}</>)}
                </button>
                {!editingQso&&(
                  <button type="button" className="btn btn--secondary" onClick={sendClusterSpot}
                    disabled={!form.callsign||!form.frequency}
                    title={tx('Wyślij spot tej stacji na klaster DX (pod Twoim znakiem)')}>
                    <Icon name="send" size={15} /> {tx('Spot')}
                  </button>
                )}
                <button type="button" className="btn btn--ghost"
                  onClick={()=>setForm(f=>({...f,callsign:'',operator_name:'',country:'',qth:'',
                    grid_locator:'',notes:'',date_time:nowUTC(),dxcc:'',iota:'',lat:null,lon:null,
                    us_state:'',cq_zone:'',pota_ref:'',sota_ref:'',contest_name:'',
                    contest_exchange_sent:'',contest_exchange_recv:''}))}>
                  {tx('Wyczyść')}
                </button>
                <span className="form-actions__hint">{tx('Ctrl+Enter zapisuje · Esc czyści')}</span>
              </div>
            </form>

          </section>

          <aside className="logger-aside">
            <Suspense fallback={<div className="card station-panel station-panel--empty">…</div>}>
              <StationPanel callsign={form.callsign}
                onOpenFull={()=>setStationCardCall(form.callsign.trim().toUpperCase())}
                onUseSpot={(sp)=>setForm(f=>({...f,frequency:sp.frequency||f.frequency,mode:(sp.mode||f.mode).toUpperCase(),
                  pota_ref:sp.pota_ref||f.pota_ref}))} />
            </Suspense>
            {/* Ostatnie QSO -- szybka kontrola/poprawka literowki; klik = edycja */}
            <div className="card">
              <div className="card__head">
                <span className="card__title">{tx('Ostatnie QSO')}</span>
                <button type="button" className="btn btn--ghost btn--sm" onClick={()=>setView('logbook')}>{tx('Dziennik')} <Icon name="chevronRight" size={14} /></button>
              </div>
              {qsos.length===0 ? (
                <div className="empty-mini">{tx('Tu pojawią się Twoje ostatnie łączności.')}</div>
              ) : (
                <div className="recent-list">
                  {qsos.slice(0,8).map(q=>(
                    <button key={q.id} type="button" className="recent-row" title={tx('Kliknij, aby edytować')}
                      onClick={()=>{startEdit(q); window.scrollTo({top:0,behavior:'smooth'});}}>
                      <span className="recent-row__call">{q.callsign}</span>
                      <span className="recent-row__meta">{q.band} {q.mode}</span>
                      <span className="recent-row__time">{q.date_time?.slice(11,16)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>
          </div>
        )}

        {/* ── LOGBOOK ── */}
        {view==='logbook'&&(
          <section className="section" data-tour="logbook">
            <div className="section__header">
              <h2><Icon name="book" size={20} /> {t('logbook.header')}</h2>
              <div className="logbook-controls">
                <div className="search-field">
                  <Icon name="search" size={16} />
                  <input className="input input--search" placeholder={tx('Szukaj znaku, kraju, notatki…')} value={searchQuery}
                    onChange={e=>{setSearchQuery(e.target.value);setPage(1);}} aria-label={tx('Szukaj w dzienniku')} />
                </div>
                <select className="input input--sm" value={filterBand} onChange={e=>{setFilterBand(e.target.value);setPage(1);}} aria-label={tx('Pasmo')}>
                  <option value="">{tx('Wszystkie pasma')}</option>
                  {BANDS.map(b=><option key={b}>{b}</option>)}
                </select>
                <select className="input input--sm" value={filterMode} onChange={e=>{setFilterMode(e.target.value);setPage(1);}} aria-label={tx('Emisja')}>
                  <option value="">{tx('Wszystkie emisje')}</option>
                  {MODES.map(m=><option key={m}>{m}</option>)}
                </select>
                {availableTags.length>0&&(
                  <select className="input input--sm" value={filterTag} onChange={e=>{setFilterTag(e.target.value);setPage(1);}} aria-label={tx('Tag')}>
                    <option value="">{tx('Wszystkie tagi')}</option>
                    {availableTags.map(tg=><option key={tg} value={tg}>{tg}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div className="toolbar">
              <div className="toolbar__group">
                <button className="btn btn--ghost btn--sm" onClick={()=>downloadWithAuth(`${API}/api/export/adif`,'hamlogger.adi')}><Icon name="download" size={15} /> ADIF</button>
                <button className="btn btn--ghost btn--sm" onClick={()=>setShowCabrilloModal(true)}><Icon name="download" size={15} /> {tx('Cabrillo')}</button>
              </div>
              <div className="toolbar__group">
                <label className="btn btn--ghost btn--sm" style={{cursor:'pointer',margin:0}}>
                  <Icon name="upload" size={15} /> {tx('Import ADIF')}
                  <input type="file" accept=".adi,.adif" style={{display:'none'}} onChange={handleAdifImport} />
                </label>
                <label className="btn btn--ghost btn--sm" style={{cursor:'pointer',margin:0}}
                  title={tx('Import logu zawodów z pliku Cabrillo (np. eksport z N1MM+) -- pole wymiany może wymagać ręcznej poprawki dla bardziej złożonych formatów')}>
                  <Icon name="upload" size={15} /> {tx('Import Cabrillo')}
                  <input type="file" accept=".log,.cbr,.txt" style={{display:'none'}} onChange={handleCabrilloImport} />
                </label>
              </div>
              <div className="toolbar__group">
                <button className="btn btn--ghost btn--sm" onClick={()=>setShowExternalUploadModal(true)}
                  title={tx('eQSL.cc, ClubLog, QRZ Logbook, LoTW, HRDLog')}><Icon name="send" size={15} /> {tx('Wysyłka do serwisów')}</button>
                <button className="btn btn--ghost btn--sm" onClick={()=>setShowLotwModal(true)}><Icon name="award" size={15} /> {tx('LoTW')}</button>
                <button type="button" className="btn btn--ghost btn--sm"
                  title={tx('Uzupełnij brakujące dane (kraj, DXCC...) z QRZ.com dla wszystkich QSO bez kraju')}
                  onClick={()=>enrichMissingQrzData()}><Icon name="search" size={15} /> {tx('Uzupełnij z QRZ')}</button>
              </div>
              <div className="toolbar__group toolbar__group--end">
                <details className="menu">
                  <summary className="btn btn--ghost btn--sm"><Icon name="columns" size={15} /> {tx('Kolumny')}</summary>
                  <div className="menu__panel">
                    {LOG_COLUMNS.filter(c=>c.hideable).map(c=>(
                      <label key={c.id} className="checkbox-label">
                        <input type="checkbox" checked={!hiddenCols.has(c.id)} onChange={()=>toggleCol(c.id)} /> {c.label}
                      </label>
                    ))}
                  </div>
                </details>
                <button type="button" className="btn btn--ghost btn--sm" title={tx('Przełącz gęstość tabeli')}
                  onClick={()=>setTableDensity(d=>d==='compact'?'normal':'compact')}>
                  <Icon name="bars" size={15} /> {tableDensity==='compact'?tx('Standardowa'):tx('Kompaktowa')}
                </button>
              </div>
            </div>

            {selectedQsoIds.size>0&&(
              <div className="bulk-toolbar">
                <span className="bulk-toolbar__count">{tx('✓ Zaznaczono')} {selectedQsoIds.size}</span>
                <select className="input input--sm" value={bulkBand} onChange={e=>setBulkBand(e.target.value)}>
                  <option value="">{t('bulk.changeBand')}</option>
                  {BANDS.map(b=><option key={b}>{b}</option>)}
                </select>
                <button className="btn btn--secondary btn--sm" disabled={!bulkBand||bulkBusy}
                  onClick={()=>applyBulkUpdate({band:bulkBand})}>{t('bulk.applyBand')}</button>
                <select className="input input--sm" value={bulkMode} onChange={e=>setBulkMode(e.target.value)}>
                  <option value="">{t('bulk.changeMode')}</option>
                  {MODES.map(m=><option key={m}>{m}</option>)}
                </select>
                <button className="btn btn--secondary btn--sm" disabled={!bulkMode||bulkBusy}
                  onClick={()=>applyBulkUpdate({mode:bulkMode})}>{t('bulk.applyMode')}</button>
                {/* Znak stacji -- ratunek gdy zrobiles serie QSO pod znakiem
                    okolicznosciowym i zapomniales przelaczyc selektor. Widoczne
                    tylko gdy masz zadeklarowane znaki dodatkowe. */}
                {userProfile?.alt_callsigns?.length>0&&(
                  <>
                    <select className="input input--sm" value={bulkStationCall}
                      onChange={e=>setBulkStationCall(e.target.value)} style={{fontFamily:'var(--mono)'}}>
                      <option value="">{tx('Znak stacji na...')}</option>
                      <option value="__own__">{userProfile.callsign} {tx('(własny)')}</option>
                      {userProfile.alt_callsigns.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                    <button className="btn btn--secondary btn--sm" disabled={!bulkStationCall||bulkBusy}
                      onClick={()=>applyBulkUpdate({station_callsign: bulkStationCall==='__own__'?'':bulkStationCall})}
                      title={t('bulk.applyStation')}>
                      <Icon name="radio" size={16} /> {t('bulk.applyStation')}
                    </button>
                  </>
                )}
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy||selectedQsoIds.size>60}
                  title={selectedQsoIds.size>60?tx('Maksymalnie 60 kart naraz'):tx('Karty QSL zaznaczonych QSO do druku (PDF, A4)')}
                  onClick={async()=>{ setBulkBusy(true); await downloadQslCardsPdf([...selectedQsoIds], notify); setBulkBusy(false); }}>
                  <Icon name="printer" size={16} /> {tx('Karty QSL (PDF)')}</button>
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy}
                  onClick={()=>applyBulkUpdate({qsl_sent:true})}><Icon name="mail" size={16} /> {tx('QSL wysłana')}</button>
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy}
                  onClick={()=>applyBulkUpdate({qsl_recv:true})}><Icon name="check" size={16} /> {tx('QSL otrzymana')}</button>
                {/* Dodanie do kolejki QSL papierowych -- osobny obieg niz zwykle
                    oznaczenie "wyslana" (to dopiero ZAMIAR wyslania). */}
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy}
                  title={tx('Dodaj zaznaczone do kolejki QSL papierowych')}
                  onClick={async()=>{
                    setBulkBusy(true);
                    try {
                      const r = await axios.post(`${API}/api/qsl/mark`, {ids:[...selectedQsoIds], action:'queue'});
                      notify(tx('Dodano {0} QSO do kolejki QSL', [r.data.updated]));
                      clearSelection(); fetchQsos();
                    } catch(err) { notify(err.response?.data?.error||tx('Blad'),'error'); }
                    setBulkBusy(false);
                  }}>{t('bulk.qslToSend')}</button>
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy}
                  title={tx('Wyslij eQSL dla zaznaczonych QSO')}
                  onClick={async()=>{
                    setBulkBusy(true);
                    try {
                      const r = await axios.post(`${API}/api/eqsl/send-bulk`, {ids:[...selectedQsoIds]});
                      notify(tx('Wysyłka eQSL wystartowała dla {0} QSO', [r.data.started]));
                      clearSelection();
                      setTimeout(fetchQsos, 3000);
                    } catch(err) { notify(err.response?.data?.error||tx('Blad wysylki eQSL'),'error'); }
                    setBulkBusy(false);
                  }}><Icon name="mail" size={16} /> {tx('eQSL (zaznaczone)')}</button>
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy}
                  onClick={()=>downloadWithAuth(`${API}/api/export/adif?ids=${[...selectedQsoIds].join(',')}`, `hamlogger_wybrane_${selectedQsoIds.size}.adi`)}>
                  <Icon name="download" size={16} /> {tx('ADIF (zaznaczone)')}
                </button>
                <button className="btn btn--secondary btn--sm" disabled={bulkBusy}
                  title={tx('Uzupełnij brakujące dane (kraj, DXCC...) z QRZ.com dla zaznaczonych')}
                  onClick={()=>enrichMissingQrzData([...selectedQsoIds])}>
                  <Icon name="search" size={16} /> {tx('Uzupełnij z QRZ')}
                </button>
                <button className="btn-icon btn-icon--danger" disabled={bulkBusy} onClick={applyBulkDelete} title={tx('Usuń zaznaczone')}><Icon name="trash" size={16} /></button>
                <button className="btn btn--ghost btn--sm" onClick={clearSelection}>{tx('✕ Odznacz')}</button>
              </div>
            )}

            <div className="table-wrap table-wrap--sticky">
              <table className={"log-table log-table--cards-mobile"+(tableDensity==='compact'?' log-table--compact':'')}>
                <thead><tr>
                  <th style={{width:32}}>
                    <input type="checkbox" checked={qsos.length>0&&selectedQsoIds.size===qsos.length}
                      onChange={toggleSelectAllQsos} aria-label={tx('Zaznacz wszystkie')} />
                  </th>
                  {LOG_COLUMNS.filter(c=>!hiddenCols.has(c.id)).map(c=>(
                    <th key={c.id} aria-sort={c.sort&&logSort.key===c.sort?(logSort.order==='asc'?'ascending':'descending'):undefined}>
                      {c.sort ? (
                        <button type="button" className={"th-sort"+(logSort.key===c.sort?" th-sort--on":"")}
                          onClick={()=>{ setLogSort({key:c.sort, order: logSort.key===c.sort&&logSort.order==='desc'?'asc':'desc'}); setPage(1); }}>
                          {c.label}
                          <Icon name={logSort.key===c.sort?(logSort.order==='asc'?'arrowUp':'arrowDown'):'sortable'} size={13} />
                        </button>
                      ) : c.label}
                    </th>
                  ))}
                  <th></th>
                </tr></thead>
                <tbody>
                  {qsos.length===0&&<tr><td colSpan={16} className="empty-state">{searchQuery||filterBand||filterMode||filterTag?tx('Nic nie pasuje do filtrów.'):tx('Dziennik jest pusty — pierwsze QSO dodasz w Loggerze.')}</td></tr>}
                  {qsos.map(q=>{
                    const cell = {
                      date_time: <td key="date_time" className="td--dt" data-label="Data/Czas">{formatDT(q.date_time)}</td>,
                      callsign: (
                        <td key="callsign" className="td--call" data-label={tx('Znak')}>
                          <button type="button" className="call-link" onClick={()=>setStationCardCall(q.callsign)} title={tx('Karta stacji')}>
                            <strong>{q.callsign}</strong>
                          </button>
                          {q.tags?.length>0&&(
                            <div style={{fontSize:10,color:'var(--text-dim)',marginTop:2}}>
                              {q.tags.map(tg=>`#${tg}`).join(' ')}
                            </div>
                          )}
                        </td>
                      ),
                      band: <td key="band" data-label={tx('Pasmo')}><span className={"badge band-"+(q.band||'').replace('m','').replace('cm','c')}>{q.band}</span></td>,
                      mode: <td key="mode" data-label={tx('Emisja')}><span className="badge mode-badge">{q.mode}</span></td>,
                      frequency: <td key="frequency" className="td--freq" data-label={tx('Częst.')}>{q.frequency}</td>,
                      rst: <td key="rst" className="td--rst" data-label="RST S/R">{q.rst_sent}/{q.rst_recv}</td>,
                      operator_name: <td key="operator_name" data-label={tx('Operator')}>{q.operator_name||'—'}</td>,
                      country: <td key="country" data-label={tx('Kraj')}>{q.country||'—'}</td>,
                      grid_locator: <td key="grid_locator" className="td--grid" data-label={tx('Lokator')}>{q.grid_locator||'—'}</td>,
                      source: <td key="source" data-label={tx('Źródło')}><span style={{fontSize:11,color:'var(--text-dim)',fontFamily:'var(--mono)'}}>{q.source||'manual'}</span></td>,
                      qsl: <td key="qsl" className="td--qsl" data-label="QSL" title={tx('QSL wysłana: {0} · otrzymana: {1}', [q.qsl_sent?'tak':'nie', q.qsl_recv?'tak':'nie'])}>
                        <span className={"qsl-dot"+(q.qsl_sent?" qsl-dot--on":"")}>W</span><span className={"qsl-dot"+(q.qsl_recv?" qsl-dot--on":"")}>O</span></td>,
                      eqsl: <td key="eqsl" data-label="eQSL" style={{textAlign:'center'}} title={q.eqsl_sent?tx('eQSL wysłane {0}', [formatDT(q.eqsl_sent_date)]):tx('eQSL nie wysłane')}>
                        {q.eqsl_sent?<Icon name="check" size={15} style={{color:'var(--accent)'}} />:<span style={{color:'var(--text-dim)'}}>·</span>}
                      </td>,
                      qrz: <td key="qrz" data-label="QRZ" style={{textAlign:'center'}} title={q.qrz_upload_result?`QRZ: ${q.qrz_upload_result}`:tx('Nie wysłane do QRZ Logbook')}>
                        {q.qrz_upload_result==='OK'?<Icon name="check" size={15} style={{color:'var(--accent)'}} />
                          :q.qrz_upload_result==='duplicate'?<span style={{color:'var(--warning)'}}>{tx('dup')}</span>
                          :q.qrz_upload_result?.startsWith('error')?<Icon name="x" size={15} style={{color:'var(--danger)'}} />
                          :<span style={{color:'var(--text-dim)'}}>·</span>}
                      </td>,
                    };
                    return (
                      <tr key={q.id} className={"log-row"+(selectedQsoIds.has(q.id)?" log-row--selected":"")}>
                        <td data-label=""><input type="checkbox" checked={selectedQsoIds.has(q.id)} onChange={()=>toggleSelectQso(q.id)} aria-label={tx('Zaznacz {0}', [q.callsign])} /></td>
                        {LOG_COLUMNS.filter(c=>!hiddenCols.has(c.id)).map(c=>cell[c.id])}
                        <td className="td--actions" data-label="">
                          <button className="btn-icon" onClick={()=>startEdit(q)} title={tx('Edytuj')} aria-label={tx('Edytuj')}><Icon name="edit" size={16} /></button>
                          <button className="btn-icon" onClick={()=>sendEqslManual(q)} title={tx('Wyślij eQSL ręcznie')} aria-label={tx('Wyślij eQSL')}><Icon name="mail" size={16} /></button>
                          <button className="btn-icon" onClick={()=>setQslCardQso(q)} title={tx('Karta QSL — druk / PDF')} aria-label={tx('Karta QSL — druk / PDF')}><Icon name="printer" size={16} /></button>
                          <button className="btn-icon btn-icon--danger" onClick={()=>deleteQso(q.id)} title={tx('Usuń')} aria-label={tx('Usuń')}><Icon name="trash" size={16} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages>1&&(
              <div className="pagination">
                <button className="btn btn--ghost btn--sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>{tx('← Poprz.')}</button>
                <span>{tx('Strona')} {page} / {totalPages}</span>
                <button className="btn btn--ghost btn--sm" disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}>{tx('Nast. →')}</button>
              </div>
            )}
          </section>
        )}

        {/* ── MAPA ── */}
        {view==='map'&&(
          <section className="section">
            <div className="section__header">
              <h2><Icon name="map" size={20} /> {t('qsomap.title')}</h2>
              <div style={{display:'flex',gap:12,alignItems:'center'}}>
                <span className="badge" style={{background:'#00e5a022',color:'#00e5a0',borderColor:'#00e5a044'}}>
                  {tx('Pracowane kraje:')} {dxccList.length}
                </span>
                <span className="badge" style={{background:'#00b8ff22',color:'#00b8ff',borderColor:'#00b8ff44'}}>
                  {tx('QSO z koord.:')} {qsos.filter(q=>q.lat&&q.lon).length}
                </span>
              </div>
            </div>
            <QSOMap qsos={qsos} homePos={homePos} theme={theme} />
            {dxccList.length>0&&(
              <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)'}}>
                <div style={{fontSize:11,color:'var(--text-dim)',marginBottom:8,fontFamily:'var(--mono)'}}>
                  DXCC PROGRESS: {dxccList.length} / 340
                </div>
                <div style={{background:'var(--bg4)',borderRadius:4,height:8,overflow:'hidden',marginBottom:10}}>
                  <div style={{height:'100%',width:`${(dxccList.length/340)*100}%`,background:'var(--accent)',borderRadius:4,transition:'width 1s'}} />
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                  {dxccList.sort().map(c=><span key={c} className="badge mode-badge" style={{fontSize:10}}>{c}</span>)}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── DX CLUSTER ── */}
        {view==='cluster'&&(
          <ClusterView notify={notify}
            onLoadSpot={(spot)=>{
              setForm(f=>({...f,callsign:spot.dx,frequency:spot.freq,
                mode:(spot.mode||spot.comment?.match(/\b(FT8|FT4|CW|SSB|JS8|RTTY)\b/i)?.[1]||f.mode).toUpperCase(),
                date_time:nowUTC()}));
              setView('logger');
              notify(tx('Zaladowano spot: {0} @ {1} MHz', [spot.dx, spot.freq]));
            }}
            onQsy={(spot)=>sendQsyCommand(spot.freq, spot.mode, {comment: spot.comment})}
          />
        )}

        {/* ── WATCHLIST ── */}
        {view==='watchlist'&&<WatchlistView notify={notify} userProfile={userProfile} />}

        {/* ── POTA/SOTA ── */}
        {view==='pota'&&(
          <PotaSotaView theme={theme} homePos={homePos} onLoadSpot={(spot)=>{
            setForm(f=>({...f,callsign:spot.activator,frequency:spot.frequency||f.frequency,
              mode:spot.mode||f.mode, pota_ref:spot.reference, date_time:nowUTC()}));
            setView('logger');
            notify(tx('Zaladowano aktywacje: {0} @ {1}', [spot.activator, spot.reference]));
          }} onQsy={(spot)=>sendQsyCommand(spot.frequency, spot.mode)} />
        )}

        {/* ── PSK REPORTER ── */}
        {view==='pskreporter'&&<PSKReporterView homePos={homePos} theme={theme} />}
        {view==='satellites'&&<SatelliteView notify={notify} userProfile={userProfile} theme={theme} />}
        {view==='websdr'&&<WebSDRView notify={notify} />}
        {view==='svxlink'&&<SvxlinkView notify={notify} activeGroup={svxlinkActiveGroup} setActiveGroup={setSvxlinkActiveGroup} />}

        {/* ── KLUB (multi-operator) ── */}
        {view==='klub'&&<SharedLogbookView notify={notify} callsign={callsign} />}
        {view==='qsl'&&<QslManagerView notify={notify} />}
        {view==='trash'&&<TrashView notify={notify} theme={theme} />}
        {view==='mypropagation'&&<PersonalPropagation notify={notify} theme={theme} />}

        {/* ── POGODA ── */}
        {view==='weather'&&<WeatherView notify={notify} theme={theme} />}
        {view==='propagation'&&<PropagationDashboard theme={theme} />}
        {view==='activation-planner'&&<ActivationPlannerView notify={notify} userProfile={userProfile} theme={theme} />}
        {view==='aprs'&&<AprsLookupView notify={notify} theme={theme} />}
        {view==='news'&&<NewsView notify={notify} />}
        {view==='contests'&&<ContestsView notify={notify} />}
        {view==='beacons'&&<BeaconsView userProfile={userProfile} onQsy={sendQsyCommand} />}
        {view==='alert-rules'&&<AlertRulesView notify={notify} userProfile={userProfile} />}
        {view==='chat'&&<ChatView notify={notify} userProfile={userProfile} />}
        {view==='market'&&<MarketView notify={notify} userProfile={userProfile} />}
        {view==='ranking'&&<RankingView notify={notify} userProfile={userProfile} setUserProfile={setUserProfile} />}
        {view==='exam'&&<ExamView notify={notify} />}
        {view==='dxpeditions'&&<DxpeditionsView notify={notify} />}
        {view==='mostwanted'&&<MostWantedView notify={notify} />}
        {view==='wwff'&&<WwffView notify={notify} onLoadSpot={(spot)=>{
            setForm(f=>({...f,callsign:spot.activator,frequency:spot.frequency||f.frequency,
              mode:spot.mode||f.mode, date_time:nowUTC()}));
            setView('logger');
            notify(tx('Zaladowano aktywacje WWFF: {0}{1}', [spot.activator, spot.reference?` @ ${spot.reference}`:'']));
          }} onQsy={(spot)=>sendQsyCommand(spot.frequency, spot.mode)} />}
        {view==='dxneeded'&&<DxNeededView notify={notify} onLoadSpot={(spot)=>{
            setForm(f=>({...f,callsign:spot.dx,frequency:spot.freq,
              mode:(spot.mode||f.mode).toUpperCase(), date_time:nowUTC()}));
            setView('logger');
            notify(tx('Zaladowano spot: {0} @ {1} MHz', [spot.dx, spot.freq]));
          }} onQsy={(spot)=>sendQsyCommand(spot.freq, spot.mode, {comment: spot.comment})} />}
        {view==='live-roster'&&userProfile?.feature_flags?.live_roster&&<LiveRosterView notify={notify} userProfile={userProfile} onLoadSpot={(spot)=>{
            setForm(f=>({...f,callsign:spot.callsign,mode:(spot.mode||f.mode).toUpperCase(),grid_locator:spot.grid||f.grid_locator,date_time:nowUTC()}));
            setView('logger');
            notify(tx('Zaladowano: {0}', [spot.callsign]));
          }} />}
        {view==='dxccmap'&&<DxccMapView notify={notify} />}

        {/* ── PROJEKT EQSL ── */}
        {view==='eqsl'&&<EqslDesignerView notify={notify} />}
        {/* Wysyłka do serwisow z menu (QSL -> Wysyłka) -- to samo okno co przycisk
            w Dzienniku; zamkniecie wraca do dziennika. */}
        {view==='upload'&&<ExternalUploadModal onClose={()=>setView('logbook')} notify={notify} userProfile={userProfile} setUserProfile={setUserProfile} />}

        {/* ── DYPLOMY ── */}
        {view==='diplomas'&&<DiplomasView notify={notify} onLoadSpot={(spot)=>{
            setForm(f=>({...f,callsign:spot.callsign,
              frequency:spot.frequency||f.frequency,
              mode:(spot.mode||f.mode).toUpperCase(), date_time:nowUTC()}));
            setView('logger');
            notify(tx('Zaladowano: {0}{1}', [spot.callsign, spot.frequency?` @ ${spot.frequency} MHz`:'']));
          }} />}

        {/* ── SĄSIEDZI ── */}
        {view==='neighbors'&&<NeighborsView userProfile={userProfile} setUserProfile={setUserProfile} notify={notify} theme={theme} />}
        {view==='nets'&&<NetControlView notify={notify} />}
        {view==='station'&&<StationView notify={notify} />}
        {view==='agent-diag'&&<AgentDiagnosticsView notify={notify} />}
        {view==='wsjtx-remote'&&userProfile?.feature_flags?.wsjtx_remote&&<WsjtxRemoteView notify={notify} />}
        {view==='skeds'&&<SkedsView notify={notify} onUseInLogger={(s)=>{
          setForm(f=>({...f,callsign:s.callsign,frequency:s.frequency?String(s.frequency):f.frequency,
            mode:s.mode||f.mode,rst_sent:getRst(s.mode||f.mode),rst_recv:getRst(s.mode||f.mode)}));
          lookupCallsign(s.callsign);
          setView('logger');
        }} />}
        {view==='year'&&<YearSummaryView notify={notify} />}
        {view==='swl'&&<SwlView notify={notify} />}
        {view==='dxcc-matrix'&&<DxccMatrixView notify={notify} />}
        {view==='bandmap'&&<BandMapView notify={notify} onQsy={sendQsyCommand} />}
        {view==='azimuthal'&&<AzimuthalMapView notify={notify} userProfile={userProfile} />}
        {view==='keyer'&&<KeyerView notify={notify} userProfile={userProfile} />}

        {/* ── ANALIZA ── */}
        {view==='analysis'&&<TimeAnalysisView data={timeData} theme={theme} />}

        {/* ── KALKULATOR ANTEN ── */}
        {view==='antenna'&&<AntennaCalculatorView notify={notify} userProfile={userProfile} theme={theme} />}
        {view==='cw-decoder'&&userProfile?.feature_flags?.cw_decoder&&<CwDecoderView notify={notify} />}
        {view==='cw-trainer'&&userProfile?.feature_flags?.cw_trainer&&<CwTrainerView notify={notify} />}
        {view==='path-calc'&&userProfile?.feature_flags?.path_calculator&&<PathCalculatorView notify={notify} userProfile={userProfile} />}
        {view==='qso-rate'&&userProfile?.feature_flags?.qso_rate_meter&&<QsoRateMeterView notify={notify} />}

        {/* ── ADMIN ── */}
        {view==='admin'&&userProfile?.is_moderator&&<AdminPanel notify={notify} myUserId={userProfile.id} isAdmin={!!userProfile?.is_admin} theme={theme} />}

        {/* ── STATYSTYKI ── */}
        {view==='stats'&&stats&&(
          <section className="section">
            <div className="section__header"><h2><Icon name="chart" size={20} /> {tx('Statystyki')}</h2></div>
            <div className="stats-grid">
              {[
                {v:stats.total_qsos,l:tx('Łączności ogółem')},
                {v:stats.unique_callsigns,l:tx('Unikalne znaki')},
                {v:stats.unique_countries,l:tx('Kraje / DXCC')},
                {v:Math.round((stats.unique_countries/340)*100)+'%',l:tx('Postęp DXCC')},
              ].map(s=>(
                <div key={s.l} className="stat-card stat-card--big">
                  <div className="stat-card__value">{s.v}</div>
                  <div className="stat-card__label">{s.l}</div>
                </div>
              ))}
            </div>
            <div style={{padding:'0 24px 24px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:24}}>
              {[[tx('Pasma'),stats.by_band,'#00e5a0'],[tx('Emisje'),stats.by_mode,'#00b8ff']].map(([title,data,color])=>(
                <div key={title}>
                  <h3 style={{fontFamily:'var(--cond)',fontSize:13,letterSpacing:'0.1em',color:'var(--text-dim)',marginBottom:12}}>{title}</h3>
                  <div className="bar-chart">
                    {Object.entries(data||{}).sort((a,b)=>b[1]-a[1]).map(([k,v])=>(
                      <div key={k} className="bar-item">
                        <span className="bar-label">{k}</span>
                        <div className="bar-track"><div className="bar-fill" style={{width:`${Math.min(100,(v/stats.total_qsos)*100)}%`,background:color}} /></div>
                        <span className="bar-value">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <ActivityStatsPanel />

            <LogHealthPanel notify={notify} />


            <AwardsPanel theme={theme} />

            <BadgesPanel theme={theme} />

            {/* Weryfikacja email */}
            {userProfile&&!userProfile.email_verified&&(
              <div style={{margin:'0 24px 24px',padding:'14px 16px',background:'rgba(255,187,0,0.08)',borderRadius:6,border:'1px solid rgba(255,187,0,0.3)',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                <span style={{fontSize:13,color:'#ffbb00'}}>{tx('⚠️ Adres email nie zostal potwierdzony')}</span>
                <button className="btn btn--secondary btn--sm" onClick={async()=>{
                  try { await axios.post(`${API}/api/auth/resend-verification`); notify(tx('Wyslano link weryfikacyjny')); }
                  catch { notify(tx('Blad wysylki'),'error'); }
                }}>{tx('Wyślij ponownie link')}</button>
              </div>
            )}

            {/* Publiczny profil i webhook -- przeniesione do ustawien konta
                (kliknij swoj znak w prawym gornym rogu) */}
            {userProfile&&(
              <div style={{margin:'0 24px 24px',padding:'14px 16px',background:'var(--bg3)',borderRadius:6,border:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
                <span style={{fontSize:13,color:'var(--text-dim)'}}>
                  {tx('Profil publiczny, webhooki i inne ustawienia konta -- kliknij swoj znak (')}{callsign}{tx(') w prawym gornym rogu ekranu.')}
                </span>
              </div>
            )}
          </section>
        )}

        </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
      <CookieNotice />
    </div>
  );
}

// ── Account Settings Modal (klik na znak w naglowku) ──────────────────────────
// Samodzielne, trwale usuniecie konta (RODO). Celowo "schowane" za
// rozwinieciem i wymaga hasla + wpisania znaku (+ kodu 2FA, jesli wlaczone).
// HamQTH -- darmowa baza znakow, uzywana gdy nie ma sesji QRZ (albo QRZ nie
// zna znaku). Haslo zapisywane na serwerze zaszyfrowane, po sprawdzeniu logowania.
function HamqthSettings({ notify }) {
  const [saved, setSaved] = useState(null);   // login zapisanego konta albo ''
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/qrz/status`).then(r => setSaved(r.data.hamqth_username || '')).catch(() => setSaved(''));
  }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await axios.put(`${API}/api/callbook/hamqth`, { username, password });
      setSaved(r.data.username); setPassword('');
      notify(tx('✓ Konto HamQTH zapisane — wyszukiwanie znaków działa też bez QRZ (odśwież stronę)'));
    } catch (err) { notify(err.response?.data?.error || tx('Błąd zapisu'), 'error'); }
    setBusy(false);
  };
  const remove = async () => {
    try { await axios.delete(`${API}/api/callbook/hamqth`); setSaved(''); notify(tx('Konto HamQTH usunięte')); }
    catch { notify(tx('Błąd'), 'error'); }
  };
  return (
    <section className="acc-section">
      <h4 className="acc-section__title">{tx('HamQTH — darmowa baza znaków')}</h4>
      <p className="acc-section__hint">
        {tx('Wyszukiwanie znaku w loggerze (imię, QTH, lokator, e-mail do eQSL) bez płatnej subskrypcji QRZ. Używane automatycznie, gdy nie jesteś zalogowany do QRZ albo QRZ nie zna znaku. Konto zakładasz za darmo na hamqth.com.')}
      </p>
      {saved ? (
        <div style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}>
          {tx('✓ Zapisane konto:')} <strong style={{fontFamily:'var(--mono)'}}>{saved}</strong>
          <button type="button" className="btn btn--ghost btn--sm" onClick={remove}>{tx('Usuń')}</button>
        </div>
      ) : saved === '' && (
        <form onSubmit={save} className="form-row" style={{alignItems:'flex-end'}}>
          <div className="form-group"><label>{tx('Login HamQTH')}</label>
            <input className="input" value={username} onChange={e=>setUsername(e.target.value)} required autoComplete="off" /></div>
          <div className="form-group"><label>{tx('Hasło HamQTH')}</label>
            <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="new-password" /></div>
          <button className="btn btn--primary btn--sm" type="submit" disabled={busy}>{busy?'…':tx('Zapisz')}</button>
        </form>
      )}
    </section>
  );
}

function DeleteAccountPanel({ notify, callsign }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmCs, setConfirmCs] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(tx('OSTATECZNE POTWIERDZENIE: usunąć konto i WSZYSTKIE dane (QSO, ustawienia, klucze)? Tego nie da się cofnąć.'))) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/delete-account`, { password, confirm_callsign: confirmCs, totp_code: totp || undefined });
      try { localStorage.removeItem('access_token'); localStorage.removeItem('refresh_token'); } catch { /* brak storage */ }
      alert(tx('Konto zostało usunięte. 73!'));
      window.location.href = '/';
    } catch (err) {
      if (err.response?.data?.totp_required) setNeedTotp(true);
      notify(err.response?.data?.error || tx('Nie udało się usunąć konta'), 'error');
    }
    setBusy(false);
  };
  return (
    <section className="acc-section">
      <h4 className="acc-section__title"><Icon name="trash" size={16} /> {tx('Usunięcie konta')}</h4>
      {!open ? (
        <button type="button" className="btn btn--sm acc-danger" onClick={()=>setOpen(true)}>
          <Icon name="trash" size={15} /> {tx('Usuń konto…')}
        </button>
      ) : (
        <form onSubmit={submit} style={{border:'1px solid var(--danger)',borderRadius:8,padding:14}}>
          <div style={{fontWeight:700,color:'var(--danger)',marginBottom:6}}>{tx('Usunięcie konta')}</div>
          <p style={{fontSize:12,color:'var(--text-dim)',lineHeight:1.6,margin:'0 0 10px'}}>
            {tx('Trwale usuwa konto i wszystkie dane: QSO, nasłuchy, skedy, sieci, zapisane klucze i ustawienia. Zanim to zrobisz, pobierz kopię: Ustawienia → Konto → eksport danych albo eksport ADIF z Dziennika.')}
          </p>
          <div className="form-group"><label>{tx('Hasło')}</label>
            <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="current-password" /></div>
          <div className="form-group"><label>{tx('Wpisz swój znak (')}{callsign}{tx('), aby potwierdzić')}</label>
            <input className="input" value={confirmCs} onChange={e=>setConfirmCs(e.target.value.toUpperCase())} required /></div>
          {needTotp&&(
            <div className="form-group"><label>{tx('Kod 2FA (z aplikacji lub zapasowy)')}</label>
              <input className="input" value={totp} onChange={e=>setTotp(e.target.value)} autoComplete="one-time-code" /></div>
          )}
          <div className="form-actions">
            <button className="btn btn--sm" style={{background:'var(--danger)',color:'#fff'}} type="submit"
              disabled={busy||confirmCs!==callsign}>{busy?'…':tx('Usuń konto na zawsze')}</button>
            <button className="btn btn--ghost btn--sm" type="button" onClick={()=>setOpen(false)}>{tx('Anuluj')}</button>
          </div>
        </form>
      )}
    </section>
  );
}

// Sekcja w ustawieniach konta (poza komponentem -- inaczej pola tracilyby fokus)
function Section({ title, children, hint }) {
  return (
    <section className="acc-section">
      {title && <h4 className="acc-section__title">{title}</h4>}
      {hint && <p className="acc-section__hint">{hint}</p>}
      {children}
    </section>
  );
}

function AccountSettingsModal({ onClose, notify, callsign, userProfile, setUserProfile, onLogout, theme, setTheme, navLayout, setNavLayout, startView, setStartView, onQrzChanged, initialTab }) {
  const { t, lang, setLang } = useI18n();
  const [tab, setTab] = useState(initialTab || 'konto');
  const [qrz, setQrz] = useState(null);          // status QRZ.com / HamQTH
  const [showQrzLogin, setShowQrzLogin] = useState(false);
  const loadQrz = useCallback(() => {
    axios.get(`${API}/api/qrz/status`, { params: { verify: 1 } }).then(r => setQrz(r.data)).catch(() => setQrz({ logged_in: false }));
  }, []);
  useEffect(() => { if (tab === 'integracje') loadQrz(); }, [tab, loadQrz]);
  // Na telefonie zakladki sa przewijanym paskiem -- pokaz aktywna
  useEffect(() => {
    document.querySelector('.acc-nav__btn--active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [tab]);
  const qrzLogout = async () => {
    try { await axios.delete(`${API}/api/qrz/session`); notify(tx('Rozłączono QRZ.com')); loadQrz(); onQrzChanged?.(); }
    catch { notify(tx('Nie udało się rozłączyć QRZ.com'), 'error'); }
  };
  const [email, setEmail] = useState(userProfile?.email || '');
  const [gridInput, setGridInput] = useState(userProfile?latLonToGrid(userProfile.home_lat,userProfile.home_lon,4):'');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const [apiToken, setApiToken] = useState(null);

  // Ladujemy token API dopiero po wejsciu w zakladke API -- nie odpytujemy
  // przy kazdym otwarciu modala konta (wiekszosc userow tam nie zajrzy).
  useEffect(() => {
    if (tab === 'api' && apiToken === null) {
      axios.get(`${API}/api/auth/api-token`).then(r => setApiToken(r.data.api_token || '')).catch(() => setApiToken(''));
    }
  }, [tab, apiToken]);

  const regenApiToken = async () => {
    if (apiToken && !window.confirm(tx('Wygenerowac nowy token? Stary przestanie działać natychmiast (skrypty go uzywajace trzeba będzie zaktualizowac).'))) return;
    try {
      const r = await axios.post(`${API}/api/auth/api-token`);
      setApiToken(r.data.api_token);
      notify(tx('Wygenerowano nowy token API'));
    } catch { notify(tx('Błąd generowania tokenu'), 'error'); }
  };

  const revokeApiToken = async () => {
    if (!window.confirm(tx('Odwolac token API? Wszystkie skrypty go uzywajace przestana działać.'))) return;
    try {
      await axios.delete(`${API}/api/auth/api-token`);
      setApiToken('');
      notify(tx('Token API odwolany'));
    } catch { notify(tx('Błąd'), 'error'); }
  };

  const saveEmail = async (e) => {
    e.preventDefault();
    if (email === userProfile?.email) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/auth/update`, {email});
      setUserProfile(p=>({...p, email, email_verified: r.data.email_changed?false:p.email_verified}));
      notify(r.data.email_changed?tx('Email zmieniony -- wysłano link weryfikacyjny na nowy adres'):tx('Zapisano'));
    } catch(err) { notify(err.response?.data?.error||tx('Błąd zapisu emaila'),'error'); }
    setSaving(false);
  };

  const saveLocation = async (e) => {
    e.preventDefault();
    const pos = gridToLatLon(gridInput.trim());
    if (!pos) { notify(tx('Nieprawidłowy lokator (np. KO02 lub JO82FX)'),'error'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/auth/update`, {home_lat: pos.lat, home_lon: pos.lon});
      setUserProfile(p=>({...p, home_lat: pos.lat, home_lon: pos.lon}));
      notify(tx('Lokalizacja zapisana: {0}', [gridInput.toUpperCase()]));
    } catch { notify(tx('Błąd zapisu lokalizacji'),'error'); }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { notify(tx('Nowe hasło musi miec min. 6 znaków'),'error'); return; }
    if (newPw !== newPw2) { notify(tx('Nowe hasła nie są identyczne'),'error'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/auth/change-password`, {current_password: currentPw, new_password: newPw});
      // Backend bumpuje token_version (uniewaznia INNE sesje) i zwraca SWIEZE
      // tokeny dla biezacej sesji -- musimy je zapisac, inaczej NASTĘPNE
      // zadanie poleci ze starym, już uniewaznionym tokenem i wyloguje nas.
      if (r.data.access_token) localStorage.setItem('access_token', r.data.access_token);
      if (r.data.refresh_token) localStorage.setItem('refresh_token', r.data.refresh_token);
      notify(tx('Hasło zmienione! Inne sesje zostaly wylogowane.'));
      setCurrentPw(''); setNewPw(''); setNewPw2('');
    } catch(err) { notify(err.response?.data?.error||tx('Błąd zmiany hasła'),'error'); }
    setSaving(false);
  };

  const exportAccountData = async () => {
    try {
      const r = await axios.get(`${API}/api/auth/export-data`, {responseType:'blob'});
      const disposition = r.headers['content-disposition'];
      const match = disposition && disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : 'hamlogger_dane.json';
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      notify(tx('Pobrano dane konta'));
    } catch { notify(tx('Błąd pobierania danych'),'error'); }
  };

  const toggleProfileField = async (field, value) => {
    try {
      await axios.put(`${API}/api/auth/update`, {[field]: value});
      setUserProfile(p=>({...p, [field]: value}));
    } catch { notify(tx('Błąd zapisu'),'error'); }
  };

  const saveWebhook = async (e) => {
    try {
      await axios.put(`${API}/api/auth/update`, {webhook_url: e.target.value});
      setUserProfile(p=>({...p, webhook_url: e.target.value}));
      notify(tx('Webhook zapisany'));
    } catch { notify(tx('Blad zapisu webhooka'),'error'); }
  };

  const doLogout = () => {
    if (window.confirm(tx('Wylogowac sie?'))) onLogout();
  };

  if (!userProfile) return null;

  const TABS = [
    ['konto', 'user', tx('Konto')],
    ['bezpieczenstwo', 'shield', tx('Bezpieczeństwo')],
    ['prywatnosc', 'eye', tx('Prywatność i profil')],
    ['powiadomienia', 'bell', tx('Powiadomienia')],
    ['integracje', 'key', tx('Integracje')],
    ['serwisy', 'upload', tx('Serwisy QSO')],
    ...(userProfile.is_swl ? [] : [['zaproszenia', 'users', tx('Zaproś znajomych')]]),
    ['preferencje', 'sliders', tx('Wygląd i preferencje')],
  ];
  const goTo = (v) => { onClose(); window.dispatchEvent(new CustomEvent('hamlogger:navigate', { detail: v })); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal acc-modal" onClick={e=>e.stopPropagation()}>
        <div className="modal__header">
          <h3><Icon name="settings" size={18} /> {tx('Ustawienia konta —')} {callsign}</h3>
          <button className="modal__close" onClick={onClose} aria-label={tx('Zamknij')}>✕</button>
        </div>

        <div className="acc-layout">
          <nav className="acc-nav" aria-label={tx('Sekcje ustawień')}>
            {TABS.map(([id, icon, label]) => (
              <button key={id} type="button" className={'acc-nav__btn' + (tab === id ? ' acc-nav__btn--active' : '')}
                onClick={() => setTab(id)} aria-current={tab === id ? 'page' : undefined}>
                <Icon name={icon} size={16} /> <span>{label}</span>
              </button>
            ))}
            <button type="button" className="acc-nav__btn acc-nav__logout" onClick={doLogout}>
              <Icon name="logout" size={16} /> <span>{tx('Wyloguj')}</span>
            </button>
          </nav>

          <div className="acc-pane">
            {tab==='konto'&&(
              <>
                <Section title={tx('Typ konta')}>
                  <div className="auth-type">
                    <button type="button" className={'auth-type__opt'+(!userProfile.is_swl?' auth-type__opt--active':'')}
                      onClick={()=>userProfile.is_swl&&toggleProfileField('is_swl', false)}>
                      <Icon name="mic" size={18} />
                      <span><strong>{tx('Krótkofalowiec')}</strong><small>{tx('Znak wywoławczy, logger QSO')}</small></span>
                    </button>
                    <button type="button" className={'auth-type__opt'+(userProfile.is_swl?' auth-type__opt--active':'')}
                      onClick={()=>!userProfile.is_swl&&toggleProfileField('is_swl', true)}>
                      <Icon name="headphones" size={18} />
                      <span><strong>{tx('Nasłuchowiec (SWL)')}</strong><small>{tx('Dziennik nasłuchów na pierwszym miejscu')}</small></span>
                    </button>
                  </div>
                  <p className="acc-section__hint">{tx('Typ konta zmienia tylko układ menu i ekran startowy — wszystkie dane zostają.')}</p>
                </Section>

                <Section title={tx('Adres e-mail')}>
                  <form onSubmit={saveEmail}>
                    <div className="form-group">
                      <label>{tx('E-mail')} {userProfile.email_verified
                        ? <span className="acc-badge acc-badge--ok">{tx('potwierdzony')}</span>
                        : <span className="acc-badge acc-badge--warn">{tx('niepotwierdzony')}</span>}</label>
                      <input className="input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required />
                    </div>
                    <div className="acc-actions">
                      <button className="btn btn--secondary btn--sm" type="submit" disabled={saving||email===userProfile.email}>
                        {saving?'…':tx('Zapisz e-mail')}
                      </button>
                      {!userProfile.email_verified&&(
                        <button type="button" className="btn btn--ghost btn--sm" onClick={async()=>{
                          try { await axios.post(`${API}/api/auth/resend-verification`); notify(tx('Wysłano link weryfikacyjny')); }
                          catch { notify(tx('Błąd wysyłki'),'error'); }
                        }}>{tx('Wyślij link weryfikacyjny')}</button>
                      )}
                    </div>
                  </form>
                </Section>

                <Section title={tx('Lokalizacja stacji')} hint={tx('Lokator (Maidenhead) Twojego QTH — używany na mapach, do liczenia odległości i azymutów oraz w wyszukiwarce sąsiadów.')}>
                  <form onSubmit={saveLocation}>
                    <div className="form-group">
                      <label>{tx('Lokator')}</label>
                      <input className="input" style={{fontFamily:'var(--mono)'}} value={gridInput}
                        onChange={e=>setGridInput(e.target.value.toUpperCase())} placeholder={tx('np. JO82FX')} required />
                      {userProfile.home_lat!=null&&(
                        <div className="acc-section__hint">{tx('Aktualnie:')} {userProfile.home_lat.toFixed(4)}, {userProfile.home_lon.toFixed(4)}</div>
                      )}
                    </div>
                    <button className="btn btn--secondary btn--sm" type="submit" disabled={saving}>{saving?'…':tx('Zapisz lokalizację')}</button>
                  </form>
                </Section>

                {!userProfile.is_swl&&(
                  <Section title={tx('Moje uprawnienia')} hint={tx('Zaznacz pasma, na których możesz nadawać, i maksymalną moc. Logger ostrzeże, gdy wpiszesz częstotliwość lub moc poza nimi. Nic nie zaznaczone = bez sprawdzania pasm.')}>
                    <div className="acc-chips">
                      {LICENSE_BAND_OPTIONS.map(b=>{
                        const on=(userProfile.license_bands||[]).includes(b);
                        return <button key={b} type="button" aria-pressed={on} className={'acc-chip'+(on?' acc-chip--on':'')}
                          onClick={()=>{const cur=userProfile.license_bands||[]; toggleProfileField('license_bands', on?cur.filter(x=>x!==b):[...cur,b]);}}>{b}</button>;
                      })}
                    </div>
                    <label className="acc-inline">{tx('Maks. moc (W)')}
                      <input className="input" type="number" min="1" style={{width:110}}
                        defaultValue={userProfile.license_max_power||''} placeholder={tx('bez limitu')}
                        onBlur={e=>{const v=e.target.value?parseInt(e.target.value,10):null; if(v!==(userProfile.license_max_power||null)) toggleProfileField('license_max_power', v);}} />
                    </label>
                  </Section>
                )}

                <Section title={tx('Moje dane (RODO)')} hint={tx('Kopia wszystkich Twoich danych zapisanych na serwerze (profil, QSO, watchlista) w jednym pliku JSON.')}>
                  <button className="btn btn--secondary btn--sm" onClick={exportAccountData}><Icon name="download" size={16} /> {tx('Pobierz moje dane')}</button>
                </Section>
              </>
            )}

            {tab==='bezpieczenstwo'&&(
              <>
                <Section title={tx('Zmiana hasła')}>
                  <form onSubmit={changePassword}>
                    <div className="form-group">
                      <label>{tx('Aktualne hasło')}</label>
                      <input className="input" type="password" value={currentPw} onChange={e=>setCurrentPw(e.target.value)} required autoComplete="current-password" />
                    </div>
                    <div className="form-group">
                      <label>{tx('Nowe hasło')}</label>
                      <input className="input" type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder={tx('min. 6 znaków')} required autoComplete="new-password" />
                    </div>
                    <div className="form-group">
                      <label>{tx('Powtórz nowe hasło')}</label>
                      <input className="input" type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)} required autoComplete="new-password" />
                    </div>
                    <button className="btn btn--secondary btn--sm" type="submit" disabled={saving}>{saving?'…':tx('Zmień hasło')}</button>
                  </form>
                </Section>
                <Suspense fallback={<div className="acc-section__hint">{tx('Ładowanie…')}</div>}>
                  <TwoFactorPanel notify={notify} isPrivileged={!!userProfile?.is_moderator} />
                  <SessionsPanel notify={notify} onLogout={onLogout} theme={theme} />
                  <DeleteAccountPanel notify={notify} callsign={callsign} />
                </Suspense>
              </>
            )}

            {tab==='prywatnosc'&&(
              <>
                <Section title={tx('Profil publiczny')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.is_public||false}
                      onChange={e=>toggleProfileField('is_public', e.target.checked)} />
                    {tx('Profil publiczny włączony')}
                  </label>
                  {userProfile.is_public&&(
                    <>
                      <code className="acc-url">{window.location.origin}/u/{callsign}</code>
                      <div className="acc-section__hint">{tx('Co pokazywać innym:')}</div>
                      <div className="acc-checks">
                        <label className="checkbox-label">
                          <input type="checkbox" checked={userProfile.profile_show_qso_count!==false}
                            onChange={e=>toggleProfileField('profile_show_qso_count', e.target.checked)} />
                          {tx('Liczba łączności')}
                        </label>
                        <label className="checkbox-label">
                          <input type="checkbox" checked={userProfile.profile_show_countries!==false}
                            onChange={e=>toggleProfileField('profile_show_countries', e.target.checked)} />
                          {tx('Pracowane kraje (DXCC)')}
                        </label>
                        <label className="checkbox-label">
                          <input type="checkbox" checked={userProfile.profile_show_bands!==false}
                            onChange={e=>toggleProfileField('profile_show_bands', e.target.checked)} />
                          {tx('Statystyki pasm')}
                        </label>
                        <label className="checkbox-label">
                          <input type="checkbox" checked={userProfile.profile_show_location!==false}
                            onChange={e=>toggleProfileField('profile_show_location', e.target.checked)} />
                          {tx('Przybliżona lokalizacja (lokator)')}
                        </label>
                      </div>
                    </>
                  )}
                </Section>
                <Section title={tx('Czy jestem w Twoim logu?')} hint={tx('Każdy może wpisać swój znak i sprawdzić, czy jego QSO z Tobą jest w logu (widzi tylko datę, pasmo i emisję swoich własnych QSO). Działa też dla Twoich znaków okolicznościowych.')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={!!userProfile.log_search_public}
                      onChange={e=>toggleProfileField('log_search_public', e.target.checked)} />
                    {tx('Publiczne wyszukiwanie w logu')}
                  </label>
                  {userProfile.log_search_public&&[callsign, ...(userProfile.alt_callsigns||[])].map(cs=>(
                    <code key={cs} className="acc-url">{window.location.origin}/log/{cs}</code>
                  ))}
                </Section>
                <Section title={tx('Ranking')} hint={tx('Twój znak i miesięczne wyniki (QSO, CW, parki POTA, kraje DXCC) będą widoczne dla innych zalogowanych użytkowników w zakładce Społeczność → Ranking. Domyślnie wyłączone.')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={!!userProfile.ranking_opt_in}
                      onChange={e=>toggleProfileField('ranking_opt_in', e.target.checked)} />
                    {tx('Biorę udział w rankingu HamLoggera')}
                  </label>
                </Section>
              </>
            )}

            {tab==='powiadomienia'&&(
              <>
                <Section title={tx('Ostrzeżenia')} hint={tx('Okienko w aplikacji, a gdy HamLogger jest zamknięty — powiadomienie push (jeśli włączysz je w przeglądarce w regułach alertów). Każde ostrzeżenie przychodzi raz.')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.notify_storm!==false}
                      onChange={e=>toggleProfileField('notify_storm', e.target.checked)} />
                    {tx('Burza w okolicy stacji (ostrzeżenia IMGW i prognoza na 6 h)')}
                  </label>
                  {userProfile.notify_storm!==false&&(userProfile.home_lat==null||userProfile.home_lon==null)&&(
                    <p className="acc-alert acc-alert--warn">{tx('Ustaw lokator stacji w zakładce Konto — bez niego nie wiadomo, gdzie sprawdzać burze.')}</p>
                  )}
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.notify_wind!==false}
                      onChange={e=>toggleProfileField('notify_wind', e.target.checked)} />
                    {tx('Silny wiatr i oblodzenie (prognoza na 12 h) — ryzyko dla masztu i anten')}
                  </label>
                  {userProfile.notify_wind!==false&&(
                    <div className="acc-inline">
                      <span>{tx('Ostrzegaj od porywów')}</span>
                      <select className="input input--sm" value={userProfile.wind_alert_kmh||70}
                        onChange={e=>toggleProfileField('wind_alert_kmh', Number(e.target.value))} aria-label={tx('Ostrzegaj od porywów')}>
                        {[40,50,60,70,80,90,100].map(v=><option key={v} value={v}>{v} km/h</option>)}
                      </select>
                    </div>
                  )}
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.notify_flare!==false}
                      onChange={e=>toggleProfileField('notify_flare', e.target.checked)} />
                    {tx('Rozbłysk słoneczny od klasy M5 (R2+) — może na chwilę wygasić KF')}
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={!!userProfile.notify_geomagnetic}
                      onChange={e=>toggleProfileField('notify_geomagnetic', e.target.checked)} />
                    {tx('Burza geomagnetyczna (Kp ≥ 5): słabsze KF, szansa na zorzę na 6 m / 2 m')}
                  </label>
                </Section>
                <Section title={tx('Potwierdzenia i odznaki')} hint={tx('Nowe potwierdzenia z LoTW sprawdzane są co 6 godzin (login do lotw.arrl.org podajesz w zakładce Serwisy QSO). Odznaki za cele przychodzą zawsze — zobaczysz je w dzwonku.')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.notify_qsl!==false}
                      onChange={e=>toggleProfileField('notify_qsl', e.target.checked)} />
                    {tx('Powiadom o nowych potwierdzeniach QSL (np. „5 nowych z LoTW, w tym nowy kraj”)')}
                  </label>
                </Section>
                <Section title={tx('E-mail')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.email_reports_enabled||false}
                      onChange={e=>toggleProfileField('email_reports_enabled', e.target.checked)} />
                    {tx('Miesięczne podsumowanie logu mailem')}
                  </label>
                  <p className="acc-section__hint">{tx('1. dnia miesiąca: liczba QSO, nowe kraje i użyte pasma za miniony miesiąc; 1 stycznia także podsumowanie całego roku.')}</p>
                </Section>
                <Section title={tx('Webhook (Discord / Slack)')} hint={tx('Adres webhooka, na który HamLogger wysyła alerty DX (watchlista, reguły alertów).')}>
                  <input className="input" defaultValue={userProfile.webhook_url||''}
                    placeholder="https://discord.com/api/webhooks/..." onBlur={saveWebhook}
                    style={{fontFamily:'var(--mono)',fontSize:12}} />
                  <label className="checkbox-label" style={{marginTop:10}}>
                    <input type="checkbox" checked={!!userProfile.diploma_alerts_enabled}
                      onChange={e=>toggleProfileField('diploma_alerts_enabled', e.target.checked)} />
                    {tx('Powiadamiaj o nowych aktywatorach akcji dyplomowych')}
                  </label>
                  <p className="acc-section__hint">{tx('Sprawdzane co ok. 5 minut (spoty z radiodyplom.pl): znak aktywatora, pasmo, emisja i nazwa akcji. Wymaga webhooka powyżej.')}</p>
                </Section>
                <Section title={tx('Powiadomienia w przeglądarce i reguły alertów')}>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={()=>goTo('alert-rules')}>
                    <Icon name="bell" size={15} /> {tx('Otwórz reguły alertów')}
                  </button>
                </Section>
              </>
            )}

            {tab==='integracje'&&(
              <>
                <Section title={<>
                  {tx('QRZ.com — wyszukiwanie znaków')}
                  <span className={'acc-badge ' + (qrz?.logged_in ? 'acc-badge--ok' : 'acc-badge--warn')}>
                    {!qrz ? '…' : qrz.logged_in ? tx('połączono') : qrz.session_expired ? tx('sesja wygasła') : tx('niepołączono')}
                  </span>
                </>}>
                  <div className="acc-status">
                    <button type="button" className="btn btn--primary btn--sm" onClick={()=>setShowQrzLogin(true)}>
                      <Icon name="key" size={15} /> {qrz?.logged_in ? tx('Połącz ponownie') : tx('Połącz z QRZ.com')}
                    </button>
                    {qrz?.logged_in&&(
                      <button type="button" className="btn btn--ghost btn--sm" onClick={qrzLogout}>{tx('Rozłącz')}</button>
                    )}
                  </div>
                  <p className="acc-section__hint">{tx('Uzupełnia imię, QTH, lokator i kraj przy wpisywaniu znaku w loggerze. Pełne dane (lokator, współrzędne) wymagają subskrypcji QRZ „XML Logbook Data”. Sesja QRZ wygasa po ok. 24 min bez używania — wtedy połącz ponownie.')}</p>
                </Section>
                <HamqthSettings notify={notify} />
                <Section title={tx('Token API')} hint={tx('Pozwala pisać własne skrypty i integracje, które czytają Twój log (np. statystyki na stronie klubu). Daje dostęp tylko do odczytu Twojego logu — traktuj go jak hasło.')}>
                  {apiToken?(
                    <>
                      <div className="form-group">
                        <label>{t('profile.apiToken')}</label>
                        <input className="input" readOnly value={apiToken} onClick={e=>e.target.select()} style={{fontFamily:'var(--mono)',fontSize:11}} />
                      </div>
                      <div className="acc-actions">
                        <button className="btn btn--secondary btn--sm" onClick={()=>{navigator.clipboard?.writeText(apiToken); notify(tx('Skopiowano token'));}}><Icon name="copy" size={16} /> {tx('Kopiuj')}</button>
                        <button className="btn btn--ghost btn--sm" onClick={regenApiToken}><Icon name="refresh" size={16} /> {t('profile.regenerate')}</button>
                        <button className="btn btn--ghost btn--sm" onClick={revokeApiToken}><Icon name="trash" size={16} /> {t('profile.revoke')}</button>
                      </div>
                      <div className="acc-code">
                        <div className="acc-section__hint">{tx('Przykład użycia (curl):')}</div>
                        <code>curl -H "Authorization: Bearer {apiToken}" {window.location.origin}/api/v1/stats</code>
                        <div className="acc-section__hint">{tx('Dostępne:')} /api/v1/whoami, /api/v1/stats, /api/v1/qsos ({tx('parametry')}: limit, offset, band, mode, callsign), /api/v1/qsos/&lt;id&gt;</div>
                      </div>
                    </>
                  ):(
                    <button className="btn btn--primary btn--sm" onClick={regenApiToken}><Icon name="plus" size={16} /> {tx('Wygeneruj token API')}</button>
                  )}
                </Section>
                <Section title={tx('Wysyłka QSO do serwisów')} hint={tx('Dane logowania i klucze API do QRZ Logbook, eQSL, ClubLog, LoTW, HRDLOG i Wavelog są w zakładce „Serwisy QSO”.')}>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={()=>setTab('serwisy')}>
                    <Icon name="upload" size={15} /> {tx('Przejdź do Serwisów QSO')}
                  </button>
                </Section>
              </>
            )}

            {tab==='zaproszenia'&&!userProfile.is_swl&&(
              <InvitesPanel notify={notify} onOpenRankingSettings={()=>setTab('prywatnosc')} />
            )}

            {tab==='serwisy'&&(
              <ServiceAccounts notify={notify} userProfile={userProfile} setUserProfile={setUserProfile} onOpenUpload={()=>goTo('upload')} />
            )}

            {tab==='preferencje'&&(
              <>
                <p className="acc-section__hint" style={{marginTop:0}}>{tx('Wygląd zapisuje się w tej przeglądarce — na innym urządzeniu ustawisz go osobno.')}</p>
                <Section title={tx('Język')} hint={tx('Zapisuje się na koncie (także język maili). Domyślnie: polski dla polskich znaków (SP, SQ, SN, SO, SR, 3Z, HF), angielski dla pozostałych.')}>
                  <select className="input" style={{maxWidth:220}} value={lang} onChange={e=>changeLanguage(setLang, e.target.value, true)} aria-label={tx('Język')}>
                    {AVAILABLE_LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.label || l.code.toUpperCase()}</option>)}
                  </select>
                </Section>
                <Section title={tx('Motyw')}>
                  {(()=>{
                    // Podglad kolorow: element z data-theme dostaje zmienne tego motywu
                    const card = (th) => (
                      <button key={th.id} type="button" className={"theme-card"+(theme===th.id?" theme-card--active":"")}
                        onClick={()=>setTheme(th.id)} aria-pressed={theme===th.id}>
                        <span className="theme-card__preview" data-theme={th.id}>
                          <i style={{background:'var(--preview, var(--bg))'}} /><i style={{background:'var(--bg3)'}} />
                          <i style={{background:'var(--accent)'}} /><i style={{background:'var(--accent2)'}} />
                        </span>
                        <span className="theme-card__label">{th.label}</span>
                        {th.experimental&&<span className="theme-card__beta" title={tx('Motyw eksperymentalny (animowany)')}>beta</span>}
                      </button>
                    );
                    const all = visibleThemes(userProfile);
                    const extra = all.filter(th=>th.group!=='main');
                    return (
                      <>
                        <div className="theme-grid theme-grid--main">{all.filter(th=>th.group==='main').map(card)}</div>
                        <details className="theme-more" open={extra.some(th=>th.id===theme)}>
                          <summary>{tx('Więcej motywów (')}{extra.length})</summary>
                          {THEME_GROUPS.filter(g=>g.id!=='main').map(g=>{
                            const inGroup = extra.filter(th=>th.group===g.id);
                            if (inGroup.length===0) return null;  // "Ekskluzywne" ukryte bez wczesnego dostepu
                            return (
                              <div key={g.id} style={{marginTop:10}}>
                                <div className="theme-more__group">{g.label}</div>
                                <div className="theme-grid">{inGroup.map(card)}</div>
                              </div>
                            );
                          })}
                        </details>
                      </>
                    );
                  })()}
                </Section>
                <Section title={tx('Układ menu')} hint={tx('Boczne menu można zwinąć do ikon (przycisk na dole menu), a pozycje przypiąć na górze (pinezka po najechaniu). Na telefonie zawsze są dolne zakładki.')}>
                  <div className="acc-seg" role="radiogroup" aria-label={tx('Układ menu')}>
                    {[['sidebar-left',tx('Boczne, z lewej')],['sidebar-right',tx('Boczne, z prawej')],['top',tx('Pasek u góry')]].map(([id,label])=>(
                      <button key={id} type="button" role="radio" aria-checked={navLayout===id}
                        className={'acc-seg__btn'+(navLayout===id?' acc-seg__btn--on':'')} onClick={()=>setNavLayout(id)}>{label}</button>
                    ))}
                  </div>
                </Section>
                <Section title={tx('Ekran startowy')} hint={tx('Co widzisz zaraz po zalogowaniu.')}>
                  <div className="acc-seg" role="radiogroup" aria-label={tx('Ekran startowy')}>
                    {[['logger','mic',tx('Logger')],['dashboard','home',tx('Pulpit')],['swl','headphones',tx('Nasłuchy (SWL)')]].map(([id,icon,label])=>(
                      <button key={id} type="button" role="radio" aria-checked={startView===id}
                        className={'acc-seg__btn'+(startView===id?' acc-seg__btn--on':'')} onClick={()=>setStartView(id)}>
                        <Icon name={icon} size={15} /> {label}
                      </button>
                    ))}
                  </div>
                </Section>
                <Section title={tx('Tryb uproszczony')} hint={tx('Tylko to, co niezbędne: logger, dziennik, statystyki i wysyłka QSO. Chowa pasek propagacji, radio/CAT, tryb zawodów, klaster i dziesiątki dodatkowych narzędzi. Wszystkie dane zostają — wyłączysz go tutaj w każdej chwili.')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={!!userProfile.simple_mode}
                      onChange={e=>toggleProfileField('simple_mode', e.target.checked)} />
                    {tx('Włącz tryb uproszczony (minimum opcji)')}
                  </label>
                </Section>
                <Section title={tx('Samouczek')} hint={tx('Krótki przewodnik: gdzie wpisać łączność w loggerze, gdzie jest dziennik i ustawienia.')}>
                  <button type="button" className="btn btn--secondary btn--sm"
                    onClick={()=>{ onClose(); window.dispatchEvent(new CustomEvent('hamlogger:tour')); }}>
                    <Icon name="info" size={15} /> {tx('Pokaż samouczek')}
                  </button>
                </Section>
                <Section title={tx('Logger')}>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={userProfile.eqsl_auto_send_default||false}
                      onChange={e=>toggleProfileField('eqsl_auto_send_default', e.target.checked)} />
                    {tx('Domyślnie zaznaczaj „Wyślij eQSL” przy nowym QSO')}
                  </label>
                  <p className="acc-section__hint">{tx('Dla pojedynczego QSO zawsze możesz to odznaczyć. Wygląd karty eQSL edytujesz w widoku „Projekt eQSL”.')}</p>
                </Section>
              </>
            )}
          </div>
        </div>
      </div>
      {showQrzLogin&&<QrzModal onClose={()=>{setShowQrzLogin(false); loadQrz(); onQrzChanged?.();}} notify={notify} />}
    </div>
  );
}

// ── LoTW Modal ────────────────────────────────────────────────────────────────
// LoTW (Logbook of The World) -- workflow eksport->TQSL->import potwierdzen.
// UCZCIWIE: nie mozemy wyslac bezposrednio (TQSL podpisuje certyfikatem
// operatora na JEGO komputerze), wiec to asystent tego procesu, nie
// pelna automatyzacja -- co jasno komunikujemy userowi.
function LotwModal({ onClose, notify }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/api/lotw/status`).then(r=>setStatus(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const downloadExport = async (all) => {
    try {
      const r = await axios.get(`${API}/api/lotw/export${all?'?all=1':''}`, {responseType:'blob'});
      const disposition = r.headers['content-disposition'];
      const match = disposition && disposition.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : 'hamlogger_lotw.adi';
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch { notify(tx('Błąd pobierania'),'error'); }
  };

  const markSent = async () => {
    if (!window.confirm(tx('Oznaczyc wszystkie niewyslane QSO jako wysłane do LoTW? Rob to DOPIERO po faktycznym wgraniu pliku przez TQSL.'))) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/lotw/mark-sent`, {});
      notify(tx('Oznaczono {0} QSO jako wyslane', [r.data.marked]));
      load();
    } catch { notify(tx('Błąd'),'error'); }
    setBusy(false);
  };

  const importConfirmations = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/api/lotw/import-confirmations`, fd);
      notify(r.data.message || tx('Zaimportowano potwierdzenia'));
      load();
    } catch(err) { notify(err.response?.data?.error||tx('Blad importu'),'error'); }
    setBusy(false);
    e.target.value = '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
        <div className="modal__header">
          <h3><Icon name="award" size={18} /> {tx('LoTW (Logbook of The World)')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>

        {status&&(
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <div className="status-card" style={{flex:1,minWidth:120}}>
              <div className="status-card__header">{t('eqsl.toSend')}</div>
              <div className="status-card__main">{status.pending}</div>
            </div>
            <div className="status-card" style={{flex:1,minWidth:120}}>
              <div className="status-card__header">{t('eqsl.sent')}</div>
              <div className="status-card__main">{status.sent}</div>
            </div>
            <div className="status-card" style={{flex:1,minWidth:120}}>
              <div className="status-card__header">{tx('✅ Potwierdzone')}</div>
              <div className="status-card__main" style={{color:'var(--accent)'}}>{status.confirmed}</div>
            </div>
          </div>
        )}

        <div style={{padding:12,background:'rgba(255,187,0,0.08)',border:'1px solid rgba(255,187,0,0.3)',borderRadius:8,marginBottom:16}}>
          <p style={{fontSize:12,color:'#ffbb00',margin:0,lineHeight:1.6}}>
            {tx('⚠️ LoTW wymaga podpisania QSO Twoim certyfikatem przez program')} <strong>TQSL</strong> {tx('na Twoim komputerze -- HamLogger nie może zrobić tego za Ciebie (certyfikat nigdy nie opuszcza Twojego komputera, i tak ma być). To jest asystent tego procesu.')}
          </p>
        </div>

        <ol style={{fontSize:13,lineHeight:1.7,paddingLeft:20,color:'var(--text)'}}>
          <li>
            <strong>{tx('Pobierz plik')}</strong> {tx('z QSO do wysłania:')}
            <div style={{display:'flex',gap:8,margin:'8px 0',flexWrap:'wrap'}}>
              <button className="btn btn--primary btn--sm" onClick={()=>downloadExport(false)}>
                <Icon name="download" size={16} /> {tx('Niewysłane (')}{status?status.pending:'...'})
              </button>
              <button className="btn btn--ghost btn--sm" onClick={()=>downloadExport(true)}><Icon name="download" size={16} /> {tx('Wszystkie')}</button>
            </div>
          </li>
          <li style={{marginTop:8}}>
            <strong>{tx('Wgraj przez TQSL')}</strong> {tx('-- otwórz pobrany plik')} <code style={{fontFamily:'var(--mono)',fontSize:11}}>{tx('.adi')}</code> {tx('w TQSL (Sign and upload), który podpisze go i wyśle do LoTW.')}
          </li>
          <li style={{marginTop:8}}>
            <strong>{tx('Oznacz jako wysłane')}</strong> {tx('tutaj (żeby nie eksportować ich ponownie):')}
            <div style={{margin:'8px 0'}}>
              <button className="btn btn--secondary btn--sm" onClick={markSent} disabled={busy}>{t('eqsl.markSent')}</button>
            </div>
          </li>
          <li style={{marginTop:8}}>
            <strong>{tx('Importuj potwierdzenia')}</strong> {tx('-- gdy pobierzesz z LoTW plik QSL-i (Download → QSLs, format ADIF), wgraj go tutaj, żeby oznaczyć potwierdzone QSO:')}
            <div style={{margin:'8px 0'}}>
              <label className="btn btn--secondary btn--sm" style={{cursor:'pointer',margin:0}}>
                <Icon name="upload" size={16} /> {tx('Importuj potwierdzenia LoTW')}
                <input type="file" accept=".adi,.adif" style={{display:'none'}} onChange={importConfirmations} disabled={busy} />
              </label>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
}

// ── QRZ Modal ─────────────────────────────────────────────────────────────────
function QrzModal({ onClose, notify }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const login = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await axios.post(`${API}/api/qrz/login`, {username, password});
      if (r.data.success) { notify(tx('Zalogowano do QRZ.com!')); onClose(); }
      else notify(r.data.error||tx('Blad logowania QRZ'),'error');
    } catch { notify(tx('Nie mozna polaczyc z QRZ.com'),'error'); }
    setLoading(false);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal__header">
          <h3><Icon name="key" size={18} /> {tx('Logowanie QRZ.com')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="modal__info">
          {tx('Wymagane konto QRZ.com z aktywna platna subskrypcja "XML Logbook Data" (osobny dodatek, nie samo Premium Membership) -- bez niej dane podstawowe (adres, kraj) działają, ale lokator/koordynaty (lat/lon/grid) nie będą zwracane przez QRZ.')}
        </p>
        <form onSubmit={login}>
          <div className="form-group"><label>{tx('Znak / Login QRZ')}</label>
            <input className="input" value={username} onChange={e=>setUsername(e.target.value)} placeholder="SP1ABC" required autoFocus autoComplete="off" name="qrz-username" /></div>
          <div className="form-group"><label>{tx('Hasło QRZ.com')}</label>
            <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="new-password" name="qrz-password" /></div>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={loading}>{loading?'…':tx('🔑 Zaloguj')}</button>
            <button type="button" className="btn btn--ghost" onClick={onClose}>{tx('Anuluj')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Cabrillo Export Modal ──────────────────────────────────────────────────────
function CabrilloExportModal({ onClose, downloadWithAuth }) {
  const [contestName, setContestName] = useState('CQ-WW-DX');
  const [categoryOperator, setCategoryOperator] = useState('SINGLE-OP');
  const [categoryPower, setCategoryPower] = useState('HIGH');
  const [contestOnly, setContestOnly] = useState(true);
  const [loading, setLoading] = useState(false);

  const doExport = async (e) => {
    e.preventDefault(); setLoading(true);
    const params = new URLSearchParams({
      contest: contestName, category_operator: categoryOperator,
      category_power: categoryPower, contest_only: contestOnly,
    });
    await downloadWithAuth(`${API}/api/export/cabrillo?${params}`, `${contestName}.cbr`);
    setLoading(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal__header">
          <h3><Icon name="download" size={18} /> {tx('Eksport Cabrillo')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="modal__info">{tx('Standardowy format logow zawodowych')}</p>
        <form onSubmit={doExport}>
          <div className="form-group"><label>{tx('Nazwa zawodow')}</label>
            <input className="input" value={contestName} onChange={e=>setContestName(e.target.value.toUpperCase())} placeholder="CQ-WW-DX" required autoFocus /></div>
          <div className="form-group"><label>{tx('Kategoria operatora')}</label>
            <select className="input" value={categoryOperator} onChange={e=>setCategoryOperator(e.target.value)}>
              <option value="SINGLE-OP">SINGLE-OP</option>
              <option value="MULTI-OP">MULTI-OP</option>
              <option value="CHECKLOG">CHECKLOG</option>
            </select></div>
          <div className="form-group"><label>{tx('Kategoria mocy')}</label>
            <select className="input" value={categoryPower} onChange={e=>setCategoryPower(e.target.value)}>
              <option value="HIGH">HIGH</option>
              <option value="LOW">LOW</option>
              <option value="QRP">QRP</option>
            </select></div>
          <label className="checkbox-label" style={{marginBottom:14}}>
            <input type="checkbox" checked={contestOnly} onChange={e=>setContestOnly(e.target.checked)} />
            {tx('Tylko QSO oznaczone nazwa tych zawodow (pole "Nazwa zawodow" w formularzu loggera)')}
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={loading}>{loading?'…':tx('⬇ Eksportuj')}</button>
            <button type="button" className="btn btn--ghost" onClick={onClose}>{tx('Anuluj')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── External Upload Modal (eQSL / ClubLog) ────────────────────────────────────
// Auto-wysylka do serwisu: wlaczenie, interwal, stan ostatniego przebiegu i
// ostatni blad. Interwal zapisuje sie od razu; wlaczenie -- od razu tylko gdy
// dane logowania sa juz zapisane na serwerze (inaczej przy pierwszej recznej
// wysylce z opcja "zapamietaj", jak dotad).
// Serwisy w widoku wysylki (kolejnosc jak w ustawieniach) + pole wlaczajace auto-wysylke
const UPLOAD_SERVICES = SERVICE_LIST.map(s => ({...s, autoField: s.id === 'qrz' ? 'qrz_logbook_auto_upload' : `${s.id}_auto_upload`}));

const UPLOAD_INTERVAL_OPTIONS = [[1440,tx('raz dziennie')],[60,tx('co godzinę')],[30,tx('co 30 min')],[15,tx('co 15 min')],[5,tx('co 5 min')]];

function AutoUploadControl({ svc, enabledField, checked, setChecked, autoSettings, saveAutoSetting, notify, style }) {
  const st = autoSettings?.[svc] || {};
  const [interval, setIntervalMin] = useState(st.interval_min || 1440);
  useEffect(() => { if (st.interval_min) setIntervalMin(st.interval_min); }, [st.interval_min]);
  const saved = !!st.has_saved_creds;
  return (
    <div style={{marginBottom:10, ...style}}>
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <label className="checkbox-label" style={{fontSize:13,margin:0}}>
          <input type="checkbox" checked={checked} onChange={async e=>{
            const v=e.target.checked; setChecked(v);
            if (saved) {
              if (await saveAutoSetting(enabledField, v)) notify(v?tx('🔁 Automatyczna wysyłka włączona'):tx('Automatyczna wysyłka wyłączona'));
              else setChecked(!v);
            }
          }} />
          {tx('🔁 Wysyłaj nowe QSO automatycznie')}
        </label>
        <select className="input" style={{width:'auto',padding:'3px 8px',fontSize:13}} value={interval} disabled={!checked}
          onChange={async e=>{ const v=Number(e.target.value); setIntervalMin(v); await saveAutoSetting(`${svc}_upload_interval_min`, v); }}>
          {UPLOAD_INTERVAL_OPTIONS.map(([v,l])=><option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {checked&&(
        <p style={{fontSize:11,color:'var(--text-dim)',margin:'4px 0 0',lineHeight:1.5}}>
          {tx('Wysyłane są tylko nowe QSO — jeśli nic nie przybyło, nie ma żadnego zapytania do serwisu.')}
          {' '}{tx('Gdy wysyłka się nie uda, dostaniesz powiadomienie (push i e-mail), a kolejne próby będą nie częściej niż co godzinę.')}
          {st.last_auto_upload_at&&<> {tx('Ostatnia automatyczna próba:')} {st.last_auto_upload_at.slice(0,16).replace('T',' ')} UTC.</>}
        </p>
      )}
      {st.last_error&&(
        <p style={{fontSize:12,color:'var(--danger)',margin:'6px 0 0',lineHeight:1.5}}>
          {tx('⚠️ Ostatnia automatyczna wysyłka nie powiodła się:')} {st.last_error}
        </p>
      )}
    </div>
  );
}

function ExternalUploadModal({ onClose, notify, userProfile, setUserProfile }) {
  const [auto, setAuto] = useState(null);
  const [autoOn, setAutoOn] = useState({});
  const [force, setForce] = useState({});
  const [busy, setBusy] = useState(null);          // id serwisu w trakcie wysylki / 'all'
  const [qrzProgress, setQrzProgress] = useState(null);

  // Stan cyklicznego auto-uploadu i tego, czy dane logowania sa zapisane
  // (same dane wpisuje sie w Ustawienia konta -> Serwisy QSO).
  const load = useCallback(() => {
    axios.get(`${API}/api/upload/auto-settings`).then(r => {
      setAuto(r.data);
      setAutoOn(Object.fromEntries(UPLOAD_SERVICES.map(s => [s.id, !!r.data[s.id]?.auto_upload])));
    }).catch(() => setAuto({}));
  }, []);
  useEffect(() => {
    load();
    axios.get(`${API}/api/upload/qrz-logbook/status`).then(r => { if (r.data?.status === 'running') setQrzProgress(r.data); }).catch(()=>{});
    axios.get(`${API}/api/auth/me`).then(r => setUserProfile?.(r.data)).catch(()=>{});
  }, [load, setUserProfile]);

  const saveAutoSetting = async (field, value) => {
    try {
      await axios.put(`${API}/api/upload/auto-settings`, {[field]: value});
      return true;
    } catch(err) { notify(err.response?.data?.error||tx('Błąd zapisu ustawienia auto-uploadu'),'error'); return false; }
  };

  // Odpytywanie postepu eksportu do QRZ Logbook (dziala w tle na backendzie).
  // Zaleznoscia jest sam STATUS, nie caly obiekt -- inaczej interwal
  // restartowalby sie przy kazdej odpowiedzi.
  const qrzStatusValue = qrzProgress?.status;
  useEffect(() => {
    if (qrzStatusValue !== 'running') return;
    const t = setInterval(() => {
      axios.get(`${API}/api/upload/qrz-logbook/status`).then(r => setQrzProgress(r.data)).catch(()=>{});
    }, 1500);
    return () => clearInterval(t);
  }, [qrzStatusValue]);

  const openSettings = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('hamlogger:account', { detail: 'serwisy' }));
  };

  const ready = (id) => serviceConfigured(id, auto, userProfile);

  // Jedna wysylka; zwraca true przy sukcesie (uzywane tez przez "Wyslij wszedzie").
  const sendOne = async (id, quiet) => {
    const fr = !!force[id];
    try {
      if (id === 'qrz') {
        await axios.post(`${API}/api/upload/qrz-logbook`, {force_resend: fr});
        setQrzProgress({status:'running', total:0, done:0, inserted:0, duplicates:0, errors:0, skipped:0});
        if (!quiet) notify(fr ? tx('Eksport (wszystkie QSO) wystartował w tle') : tx('Eksport nowych QSO wystartował w tle'));
        return true;
      }
      const url = {eqsl:'/api/upload/eqsl', clublog:'/api/upload/clublog', lotw:'/api/upload/lotw',
                   hrdlog:'/api/upload/hrdlog', wavelog:'/api/upload/wavelog'}[id];
      const r = await axios.post(`${API}${url}`, {force_resend: fr});
      const name = UPLOAD_SERVICES.find(s => s.id === id).name;
      if (r.data.ok === false) {
        notify(tx('{0}: {1}', [name, r.data.response_snippet || r.data.error || tx('serwis odpowiedział błędem')]), 'warning');
        return false;
      }
      notify(tx('Wysłano do {0}: {1} QSO{2}', [name, r.data.uploaded||0, r.data.duplicates?tx(' (duplikaty: {0})', [r.data.duplicates]):'']));
      if (r.data.warning) notify(r.data.warning, 'warning');
      if (r.data.more) notify(tx('{0}: wysłano pierwszą partię — resztę wyślij ponownie (albo poczekaj na automatyczną wysyłkę).', [name]), 'info');
      return true;
    } catch (err) {
      notify(err.response?.data?.error || err.response?.data?.response_snippet || tx('Błąd wysyłki'), 'error');
      return false;
    }
  };

  const send = async (id) => { setBusy(id); await sendOne(id); setBusy(null); load(); };
  const sendAll = async () => {
    setBusy('all');
    for (const s of UPLOAD_SERVICES) {
      if (ready(s.id) && !(s.id === 'qrz' && qrzStatusValue === 'running')) await sendOne(s.id, false);
    }
    setBusy(null); load();
  };
  const readyCount = UPLOAD_SERVICES.filter(s => ready(s.id)).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal upl-modal" onClick={e=>e.stopPropagation()}>
        <div className="modal__header">
          <h3><Icon name="send" size={18} /> {tx('Wysyłka do serwisów')}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>
        <p className="upl-intro">
          {tx('Wysyłane są tylko nowe QSO. Dane logowania i klucze API ustawiasz w')}{' '}
          <button type="button" className="link-btn" onClick={openSettings}>{tx('Ustawienia konta → Serwisy QSO')}</button>.
        </p>

        <div className="upl-list">
          {UPLOAD_SERVICES.map(s => {
            const ok = ready(s.id);
            const a = auto?.[s.id] || {};
            const running = s.id === 'qrz' && qrzStatusValue === 'running';
            return (
              <div key={s.id} className={'upl-item' + (ok ? '' : ' upl-item--off')}>
                <div className="upl-item__head">
                  <span className="upl-item__name">{s.name}</span>
                  {auto === null ? <span className="acc-badge">…</span> : ok
                    ? (autoOn[s.id]
                        ? <span className="acc-badge acc-badge--ok">{tx('automatycznie')}</span>
                        : <span className="acc-badge acc-badge--info">{tx('ręcznie')}</span>)
                    : <span className="acc-badge acc-badge--muted">{tx('brak danych')}</span>}
                  <span className="svc-item__spacer" />
                  {ok ? (
                    <button type="button" className="btn btn--primary btn--sm" onClick={()=>send(s.id)} disabled={!!busy || running}>
                      <Icon name="upload" size={15} /> {busy===s.id||running ? tx('W toku…') : tx('Wyślij')}
                    </button>
                  ) : (
                    <button type="button" className="btn btn--ghost btn--sm" onClick={openSettings}>
                      <Icon name="key" size={15} /> {tx('Ustaw dane')}
                    </button>
                  )}
                </div>
                {ok && (
                  <div className="upl-item__body">
                    <AutoUploadControl svc={s.id} enabledField={s.autoField} checked={!!autoOn[s.id]}
                      setChecked={v=>setAutoOn(o=>({...o,[s.id]:v}))}
                      autoSettings={auto} saveAutoSetting={saveAutoSetting} notify={notify} style={{marginBottom:4}} />
                    <label className="checkbox-label upl-force">
                      <input type="checkbox" checked={!!force[s.id]} onChange={e=>setForce(f=>({...f,[s.id]:e.target.checked}))} disabled={running} />
                      {tx('Przy następnej wysyłce wyślij ponownie wszystkie QSO (np. po wyczyszczeniu logu w serwisie)')}
                    </label>
                    {(a.last_ok_at || a.last_upload_at) && (
                      <div className="upl-meta">{tx('Ostatnia udana wysyłka:')} {String(a.last_ok_at || a.last_upload_at).slice(0,16).replace('T',' ')} UTC</div>
                    )}
                    {s.id==='lotw' && a.has_login && (
                      <div className="upl-lotw">
                        <button type="button" className="btn btn--ghost btn--sm" disabled={!!busy} onClick={async()=>{
                          setBusy('lotw-dl');
                          try {
                            const r = await axios.post(`${API}/api/lotw/download-confirmations`);
                            notify(r.data.confirmed ? tx('Nowe potwierdzenia z LoTW: {0}{1}', [r.data.confirmed, r.data.new_entities?.length ? tx(', w tym nowe kraje: {0}', [r.data.new_entities.join(', ')]) : ''])
                              : tx('Brak nowych potwierdzeń w LoTW.'));
                            window.dispatchEvent(new CustomEvent('hamlogger:notifications-refresh'));
                          } catch (err) { notify(err.response?.data?.error || tx('Nie udało się pobrać potwierdzeń z LoTW.'), 'error'); }
                          setBusy(null); load();
                        }}>
                          <Icon name="download" size={15} /> {busy==='lotw-dl' ? tx('Pobieram…') : tx('Pobierz potwierdzenia')}
                        </button>
                        <span className="upl-meta">{a.last_download_at ? tx('Ostatnio: {0} UTC · automatycznie co 6 h', [String(a.last_download_at).slice(0,16).replace('T',' ')]) : tx('Automatycznie co 6 h')}</span>
                        {a.last_download_error && <span className="svc-error">{a.last_download_error}</span>}
                      </div>
                    )}
                    {s.id==='qrz' && qrzProgress && (
                      <div className="upl-progress">
                        {qrzProgress.skipped>0&&<div className="upl-meta">{tx('ℹ️ Pominięto')} {qrzProgress.skipped} {tx('już wcześniej wysłanych QSO')}</div>}
                        {qrzProgress.status==='running'&&(
                          <>
                            <div className="upl-meta">{tx('⏳ Eksport w toku:')} {qrzProgress.done} / {qrzProgress.total||'?'}</div>
                            <div className="bar-track" style={{height:8}}>
                              <div className="bar-fill" style={{width:`${qrzProgress.total?Math.round(100*qrzProgress.done/qrzProgress.total):0}%`}} />
                            </div>
                          </>
                        )}
                        {qrzProgress.status==='completed'&&(
                          <div className="upl-ok">{tx('✅ Zakończono! Wysłano:')} {qrzProgress.inserted}{tx(', duplikaty:')} {qrzProgress.duplicates}{tx(', błędy:')} {qrzProgress.errors}</div>
                        )}
                        {qrzProgress.status==='error'&&<div className="svc-error">❌ {qrzProgress.last_error}</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn--primary" onClick={sendAll} disabled={!!busy || readyCount===0}>
            <Icon name="send" size={16} /> {busy==='all' ? tx('Wysyłam…') : tx('Wyślij do wszystkich ({0})', [readyCount])}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClose}>{tx('Zamknij')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Cluster View ───────────────────────────────────────────────────────────────
function ClusterView({ onLoadSpot, onQsy, notify }) {
  const { t } = useI18n();
  // Odpowiedz przez WSJT-X -- przy spotach FT8/FT4, gdy agent widzi WSJT-X
  const [wsjtx] = useWsjtxStatus(notify, 15000);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('');
  const [clusterError, setClusterError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [ageSeconds, setAgeSeconds] = useState(null);
  const [filterBand, setFilterBand] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [newCount, setNewCount] = useState(0);  // licznik spotow doszlych na zywo
  const [loginUsed, setLoginUsed] = useState('');   // znak, pod ktorym serwer czyta klaster (ustawia admin)
  // WLASNE polaczenie WebSocket ClusterView (nie wspoldzielone z innymi
  // komponentami) -- SharedLogbookView ma swoje osobne. Wczesniej probowalem
  // przekazac tu socketRef z komponentu App, ale on tam NIE ISTNIEJE (zyje w
  // SharedLogbookView) -- stad crash "socketRef is not defined". Wlasny ref
  // jest czysty i izolowany.
  const socketRef = useRef(null);
  // Bufor przychodzacych spotow -- zamiast wykladac kazdy spot na liste
  // natychmiast (co przy ruchliwym pasmie powoduje ciagle "miganie"),
  // zbieramy je tutaj i wykladamy ZBIORCZO co 5s (patrz nizej). Ref, nie
  // state -- dopisywanie do bufora NIE ma powodowac re-renderu.
  const pendingSpotsRef = useRef([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/cluster/spots`, {params:{band:filterBand,mode:filterMode}});
      setSpots(asArray(r.data.spots)); setSource(r.data.source||''); setAgeSeconds(r.data.age_seconds);
      setLoginUsed(r.data.login||'');
      setClusterError(r.data.error||''); setStreaming(!!r.data.streaming);
    } catch (e) {
      setClusterError(tx('Błąd połączenia z backendem HamLoggera'));
    }
    setAttempted(true);
    setLoading(false);
  }, [filterBand, filterMode]);

  // Pierwsze zaladowanie (bufor może już byc pełny dzieki streamowi w tle).
  useEffect(()=>{load();},[]);

  // ── PUSH NA ZYWO (buforowany) ──
  // Backend wypycha kazdy nowy spot natychmiast, ale my NIE aktualizujemy
  // listy przy kazdym -- odkladamy je do bufora (pendingSpotsRef). Osobny
  // timer co 5s wyklada zebrane spoty zbiorczo. Efekt: lista odswieza się
  // spokojnie co 5s zamiast migac przy kazdym pojedynczym spocie.
  useEffect(() => {
    const socket = io(API || window.location.origin, {transports:['websocket','polling']});
    socketRef.current = socket;
    // Znak logowania do klastra backend bierze z konta (z tokenu), nie od klienta.
    socket.emit('join_cluster', { token: localStorage.getItem('access_token') });
    const onNewSpot = (spot) => {
      // Zabezpieczenie: bufor NIE może rosnac w nieskonczonosc. Gdy karta jest
      // w tle, przegladarka throttluje timery -- flush (co 5s) może nie biec,
      // a spoty naplywaja dalej. Bez limitu bufor puchnie przez godziny.
      // Trzymamy tylko najnowsze (i tak lista jest przycinana do 200).
      const buf = pendingSpotsRef.current;
      buf.push(spot);
      if (buf.length > 500) pendingSpotsRef.current = buf.slice(-200);
      setStreaming(true);
    };
    socket.on('new_spot', onNewSpot);
    return () => {
      socket.off('new_spot', onNewSpot);
      try { socket.emit('leave_cluster', {}); } catch {}
      socket.disconnect();
    };
  }, []);

  // Timer wykladajacy zbuforowane spoty na liste co 5s.
  useEffect(() => {
    const FLUSH_MS = 5000;
    const flush = () => {
      const pending = pendingSpotsRef.current;
      if (pending.length === 0) return;
      pendingSpotsRef.current = [];
      setSpots(prev => {
        // Deduplikacja przez Set (klucz: dx + freq + czas) zamiast .some() w
        // petli -- tamto bylo O(n*m) i przy wiekszym buforze potrafilo zamrozic
        // watek UI. Freq zaokraglamy do 3 miejsc (tolerancja jak wczesniej).
        const keyOf = s => `${s.dx}|${(Math.round(s.freq*1000)/1000).toFixed(3)}|${s.time}`;
        const seen = new Set(prev.map(keyOf));
        const fresh = [];
        for (const spot of pending) {
          const k = keyOf(spot);
          if (!seen.has(k)) { seen.add(k); fresh.push(spot); }
        }
        if (fresh.length > 0) {
          setNewCount(c => c + fresh.length);
          setAgeSeconds(0);
        }
        return [...fresh.reverse(), ...prev].slice(0, 200);
      });
    };
    const t = setInterval(flush, FLUSH_MS);
    return () => clearInterval(t);
  }, []);

  // Odswiezaj licznik "wieku" ostatniego spota co sekunde (kosmetyka).
  // Zaleznosc to jawny boolean (nie wyrazenie w tablicy -- antywzorzec, który
  // React ostrzega i który latwo przeoczyc przy refactorze). Interwal używa
  // funkcyjnej formy setState, wiec nie potrzebuje ageSeconds w zaleznosciach.
  const ageTickerOn = ageSeconds !== null;
  useEffect(() => {
    if (!ageTickerOn) return;
    const t = setInterval(() => setAgeSeconds(a => (a === null ? null : a + 1)), 1000);
    return () => clearInterval(t);
  }, [ageTickerOn]);

  const bands = [...new Set(spots.map(s=>s.band).filter(Boolean))];
  const visible = spots.filter(s=>!filterBand||s.band===filterBand).filter(s=>!filterMode||filterMode.toLowerCase()==='' || s.comment?.toLowerCase().includes(filterMode.toLowerCase()));
  const fmtAge = s => s===null?'':s<60?`${s}s temu`:`${Math.floor(s/60)}min temu`;

  return (
    <section className="section">
      <div className="section__header">
        <h2><Icon name="network" size={20} /> {t('cluster.title')}</h2>
        <div className="cluster-controls">
          <select className="input input--sm" value={filterBand} onChange={e=>setFilterBand(e.target.value)}>
            <option value="">{tx('Wszystkie pasma')}</option>
            {bands.map(b=><option key={b}>{b}</option>)}
          </select>
          <select className="input input--sm" value={filterMode} onChange={e=>setFilterMode(e.target.value)}>
            <option value="">{tx('Wszystkie emisje')}</option>
            {['FT8','FT4','CW','SSB','RTTY','JS8'].map(m=><option key={m}>{m}</option>)}
          </select>
          <button className="btn btn--secondary btn--sm" onClick={()=>load()} disabled={loading}>
            {loading?'…':tx('↻ Odśwież')}
          </button>
        </div>
      </div>
      {source&&visible.length>0&&(
        <div className="cluster-status">
          <span className="cluster-src" title={loginUsed ? tx('Serwer czyta klaster jako {0}', [loginUsed]) : undefined}>📡 {source}</span>
          <span className="cluster-age">{fmtAge(ageSeconds)}</span>
          <span className="cluster-count">{visible.length} {tx('spotow')}</span>
          {streaming
            ? <span className="cluster-auto" style={{color:'var(--accent)'}}>● LIVE{newCount>0?` (+${newCount})`:''}</span>
            : <span className="cluster-auto" style={{color:'var(--text-dim)'}}>{tx('○ łączenie...')}</span>}
        </div>
      )}
      {!loading&&!attempted&&<div className="cluster-nodata"><p>{t('cluster.connecting')}</p></div>}
      {loading&&<div className="cluster-nodata"><p>{tx('⏳ Laczenie z klastrem...')}</p></div>}
      {!loading&&attempted&&visible.length===0&&(
        <div className="cluster-nodata">
          <p>{tx('⚠️ Brak spotów.')}</p>
          {clusterError&&<p style={{fontSize:12,color:'var(--text-dim)',marginTop:6,fontFamily:'var(--mono)'}}>{clusterError}</p>}
          <p style={{fontSize:12,color:'var(--text-dim)',marginTop:6}}>
            {streaming
              ? tx('Strumień połączony — nowe spoty pojawią się automatycznie, gdy tylko ktoś zaspotuje stację.')
              : tx('Strumień się łączy — spoty pojawią się za chwilę na żywo.')}
          </p>
          {!streaming&&loginUsed==='N0CALL'&&(
            <p style={{fontSize:12,color:'var(--warning)',marginTop:6}}>
              {tx('Serwer loguje się do klastra jako N0CALL, a większość węzłów to odrzuca. Administrator ustawia znak w Panel admina → Ustawienia → „Znak logowania do klastra DX”.')}
            </p>
          )}
        </div>
      )}
      {visible.length>0&&(
        <div className="table-wrap">
          <table className="log-table cluster-table">
            <thead><tr><th>{tx('Czas')}</th><th>DX</th><th>{tx('Freq (MHz)')}</th><th>{tx('Pasmo')}</th><th>{tx('Mode')}</th><th>{tx('Komentarz')}</th><th>{tx('Spotter')}</th><th></th></tr></thead>
            <tbody>
              {visible.map((s,i)=>(
                <tr key={i} className="log-row">
                  <td style={{fontFamily:'var(--mono)',fontSize:12}}>{s.time||'—'}</td>
                  <td className="td--call"><strong>{s.dx}</strong></td>
                  <td className="td--freq">{s.freq?.toFixed(1)}</td>
                  <td><span className={"badge band-"+(s.band||'').replace('m','').replace('cm','c')}>{s.band}</span></td>
                  <td>{s.mode?<span className="badge mode-badge">{s.mode}</span>:<span style={{color:'var(--text-dim)',fontSize:11}}>?</span>}</td>
                  <td style={{fontSize:12,color:'var(--text-dim)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.comment}</td>
                  <td style={{fontFamily:'var(--mono)',fontSize:11,color:'var(--text-dim)'}}>{s.spotter}</td>
                  <td>
                    <button className="btn btn--secondary btn--sm" onClick={()=>onLoadSpot(s)}><Icon name="plus" size={16} /> {tx('Log')}</button>
                    {onQsy&&<button className="btn-icon" onClick={()=>onQsy(s)} title={tx('Nastaw radio na ta czestotliwosc (wymaga agenta CAT)')}><Icon name="radio" size={16} /></button>}
                    {['FT8','FT4'].includes((s.mode||'').toUpperCase())&&<WsjtxReplyButton callsign={s.dx} wsjtx={wsjtx} notify={notify} compact />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Time Analysis View ─────────────────────────────────────────────────────────
function TimeAnalysisView({ data, theme }) {
  if (!data?.by_hour) return <div className="empty-state">{tx('Brak danych do analizy')}</div>;
  const maxH = Math.max(...(data.by_hour||[1]));
  const maxD = Math.max(...(data.by_dow||[1]));
  const maxM = Math.max(...(data.by_month||[1]));
  const days = [tx('Pon'),tx('Wt'),tx('Śr'),tx('Czw'),tx('Pt'),tx('Sob'),tx('Ndz')];
  const months = [tx('Sty'),tx('Lut'),tx('Mar'),tx('Kwi'),tx('Maj'),tx('Cze'),tx('Lip'),tx('Się'),tx('Wrz'),tx('Paź'),tx('Lis'),tx('Gru')];
  return (
    <section className="section">
      <div className="section__header"><h2><Icon name="trending" size={20} /> {tx('Analiza czasowa')}</h2></div>
      <div style={{padding:'24px',display:'grid',gridTemplateColumns:'1fr',gap:32}}>
        {[
          {title:tx('Łączności wg godziny UTC'),data:asArray(data.by_hour),labels:Array.from({length:24},(_,i)=>i+':00'),short:Array.from({length:24},(_,i)=>i%3===0?String(i):''),max:maxH},
          {title:tx('Łączności wg dnia tygodnia'),data:asArray(data.by_dow),labels:days,max:maxD},
          {title:tx('Łączności wg miesiąca'),data:asArray(data.by_month),labels:months,max:maxM},
        ].map(chart=>(
          <div key={chart.title}>
            <h3 style={{fontSize:13,fontWeight:600,color:'var(--text-dim)',marginBottom:12}}>{chart.title}</h3>
            {/* Kolumna ma pelna wysokosc wykresu -- wczesniej miala wysokosc "auto",
                wiec slupek w procentach zapadal sie do 2 px (wykresy byly puste) */}
            <div style={{display:'flex',alignItems:'stretch',gap:3,height:140}}>
              {chart.data.map((v,i)=>(
                <div key={i} style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',alignItems:'center',gap:4,height:'100%'}}>
                  <div style={{flex:1,width:'100%',display:'flex',alignItems:'flex-end'}}>
                    <div style={{width:'100%',background:'var(--accent)',borderRadius:'3px 3px 0 0',
                      height:`${chart.max>0?(v/chart.max)*100:0}%`,minHeight:v>0?3:1,
                      transition:'height 0.5s',opacity:v>0?1:0.2}} title={`${chart.labels[i]}: ${v} QSO`} />
                  </div>
                  <span style={{fontSize:10,color:'var(--text-dim)',fontFamily:'var(--mono)',whiteSpace:'nowrap'}}>{chart.short?chart.short[i]:chart.labels[i]}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <CommunityHeatmapComparison />
    </section>
  );
}

// ── Community Heatmap Comparison ──────────────────────────────────────────────
function CommunityHeatmapComparison() {
  const [cdata, setCdata] = useState(null);
  const [band, setBand] = useState('');
  const [mode, setMode] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/stats/heatmap-comparison`, {params:{band, mode}});
      setCdata(r.data);
    } catch {}
    setLoading(false);
  }, [band, mode]);

  useEffect(()=>{ load(); }, [load]);

  if (!cdata) return null;

  const maxVal = Math.max(1, ...cdata.my_by_hour, ...cdata.community_avg_by_hour);

  return (
    <div style={{padding:'0 24px 24px',borderTop:'1px solid var(--border)',marginTop:8,paddingTop:24}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <h3 style={{fontFamily:'var(--cond)',fontSize:14,letterSpacing:'0.1em',color:'var(--text-bright)'}}>
          {tx('🌍 Ty vs Społeczność — kiedy ludzie robią QSO na 20m?')}
        </h3>
        <div style={{display:'flex',gap:8}}>
          <select className="input input--sm" value={band} onChange={e=>setBand(e.target.value)}>
            <option value="">{tx('Wszystkie pasma')}</option>
            {BANDS.map(b=><option key={b}>{b}</option>)}
          </select>
          <select className="input input--sm" value={mode} onChange={e=>setMode(e.target.value)}>
            <option value="">{tx('Wszystkie emisje')}</option>
            {MODES.map(m=><option key={m}>{m}</option>)}
          </select>
        </div>
      </div>

      <div style={{fontSize:11,color:'var(--text-dim)',marginBottom:12,fontFamily:'var(--mono)'}}>
        {tx('Próbka:')} {cdata.total_users_in_sample} {tx('użytkowników (dane zanonimizowane, zsumowane)')}
      </div>

      <div style={{display:'flex',alignItems:'flex-end',gap:3,height:140,position:'relative'}}>
        {Array.from({length:24}).map((_,i)=>(
          <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2,position:'relative',height:'100%',justifyContent:'flex-end'}}>
            <div style={{position:'relative',width:'100%',flex:1,minHeight:0,display:'flex',alignItems:'flex-end',justifyContent:'center',gap:2}}>
              <div style={{
                width:'45%',background:'var(--accent2)',borderRadius:'2px 2px 0 0',
                height:`${(cdata.community_avg_by_hour[i]/maxVal)*100}%`,
                minHeight:cdata.community_avg_by_hour[i]>0?2:0,opacity:0.55,
                transition:'height 0.5s'
              }} title={tx('{0}:00 — społeczność (średnio): {1}', [i, cdata.community_avg_by_hour[i]])} />
              <div style={{
                width:'45%',background:'var(--accent)',borderRadius:'2px 2px 0 0',
                height:`${(cdata.my_by_hour[i]/maxVal)*100}%`,
                minHeight:cdata.my_by_hour[i]>0?2:0,
                transition:'height 0.5s'
              }} title={tx('{0}:00 — Ty: {1}', [i, cdata.my_by_hour[i]])} />
            </div>
            <span style={{fontSize:10,color:'var(--text-dim)',fontFamily:'var(--mono)',whiteSpace:'nowrap',minHeight:13}}>{i%3===0?i:''}</span>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:16,marginTop:12,fontSize:12}}>
        <span style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:10,height:10,background:'var(--accent)',borderRadius:2,display:'inline-block'}}/>
          {tx('Ty')}
        </span>
        <span style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{width:10,height:10,background:'var(--accent2)',borderRadius:2,display:'inline-block',opacity:0.55}}/>
          {tx('Średni użytkownik (społeczność)')}
        </span>
      </div>
    </div>
  );
}

